import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Play, Pause, ArrowLeft, Plus, Edit, Trash2, Video, FolderOpen, Crown,
  Users, TrendingUp, Eye, EyeOff, DollarSign, Search, Upload, X, Save,
  Settings, BarChart3, Film, Layers, Loader2, Tv, Link as LinkIcon,
  Clock, Globe, Monitor, Smartphone, Tablet, Activity, PieChart,
  CreditCard, Tag, ExternalLink, Receipt, UserCircle, Mail, Calendar,
  Shield, CheckCircle, XCircle, Check, Share2, Megaphone, Image as ImageIcon,
  SearchCode, Bell, Sparkles, ToggleLeft, ToggleRight, Zap, Star,
  Instagram, Twitter, Youtube, Facebook, Link2, ChevronUp, ChevronDown, ChevronRight,
  History, Grid, Image, Heart, BadgeCheck, Target, Repeat, Timer, ArrowUpRight,
  UserCog, Copy, Key, RefreshCw, AlertCircle, Percent, Banknote, Wrench
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdManagementCombined from '../components/AdManagementCombined';
import BannerAdManagement from '../components/BannerAdManagement';
import SEOAdmin from '../components/SEOAdmin';
import CommentsReactionsAdmin from '../components/admin/CommentsReactionsAdmin';
import RevenueTab from '../components/RevenueTab';
import ReelsAnalyticsTab from '../components/ReelsAnalyticsTab';
import BlogManagement from '../components/BlogManagement';
import StaffManagement from '../components/StaffManagement';
import CreatorEarningsTab from '../components/CreatorEarningsTab';
import PromoCodesTab from '../components/PromoCodesTab';
import MaintenanceTab from '../components/MaintenanceTab';
import ErrorBoundary from '../components/ErrorBoundary';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart as RechartsPieChart, Pie, Cell, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODAdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('videos');
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userRole, setUserRole] = useState('super_admin');
  const [userPermissions, setUserPermissions] = useState({});
  const [userName, setUserName] = useState('');

  // Data
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [advancedAnalytics, setAdvancedAnalytics] = useState(null);
  const [membershipSettings, setMembershipSettings] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [creatorSubscribers, setCreatorSubscribers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Creator/Channel state
  const [creators, setCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [editingCreator, setEditingCreator] = useState(null);
  
  // Purchases state
  const [purchasesData, setPurchasesData] = useState(null);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [vodStripeBalance, setVodStripeBalance] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(null);

  // Modal states
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSeries, setEditingSeries] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dataError, setDataError] = useState(false);

  useEffect(() => {
    // Check for admin token first
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      setAuthToken(adminToken);
      setIsAuthenticated(true);
      return;
    }
    
    // Check if customer is logged in and has admin/staff privileges
    const customerToken = localStorage.getItem('livestreamToken');
    const customerData = localStorage.getItem('livestreamCustomer');
    
    if (customerToken && customerData) {
      try {
        const customer = JSON.parse(customerData);
        if (customer.is_admin === true || customer.is_staff === true) {
          setAuthToken(customerToken);
          setIsAuthenticated(true);
          return;
        }
      } catch (e) {
        console.error('Error parsing customer data:', e);
      }
    }
    
    // No valid auth, redirect to VOD page
    navigate('/vod');
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchAllData();
      // Fetch user role and permissions
      axios.get(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${authToken}` } })
        .then(res => {
          const role = res.data.role || 'super_admin';
          const perms = res.data.permissions || {};
          setUserRole(role);
          setUserPermissions(perms);
          setUserName(res.data.name || res.data.username || '');
          
          // Set default active tab to first accessible tab for staff
          if (role === 'staff') {
            const tabOrder = ['videos', 'series', 'purchases', 'analytics', 'categories', 
              'membership', 'subscribers', 'users', 'ads', 'banner-ads', 'seo', 'blog', 
              'comments', 'popup', 'creators', 'reels', 'ad-reels', 'homepage', 'revenue',
              'reels-analytics', 'creator-accounts', 'subscriptions', 'stripe-settings'];
            const levels = ['none', 'view', 'edit', 'full'];
            const firstTab = tabOrder.find(t => levels.indexOf(perms[t] || 'none') >= 1);
            if (firstTab) setActiveTab(firstTab);
          }
        })
        .catch(() => {});
    }
  }, [isAuthenticated, authToken]);

  // Permission check: super admin has access to everything
  const hasAccess = (tabId, requiredLevel = 'view') => {
    if (userRole === 'super_admin') return true;
    const level = userPermissions[tabId] || 'none';
    const levels = ['none', 'view', 'edit', 'full'];
    return levels.indexOf(level) >= levels.indexOf(requiredLevel);
  };

  const canEdit = (tabId) => hasAccess(tabId, 'edit');
  const isSuperAdmin = userRole === 'super_admin';

  const fetchAllData = async (retryCount = 0) => {
    if (!authToken) return;
    setLoading(true);
    setDataError(false);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };

      const results = await Promise.allSettled([
        axios.get(`${API}/api/vod-admin/videos`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/categories`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/series`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/analytics`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/analytics/advanced?range=30d`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/membership/settings`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/subscribers`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/vod-admin/creator-subscribers`, { headers, timeout: 15000 }),
        axios.get(`${API}/api/creators/profiles?active_only=false`, { headers, timeout: 15000 }),
      ]);

      const getValue = (idx, fallback) => results[idx].status === 'fulfilled' ? results[idx].value : { data: fallback };
      
      // Check for 401 in any response
      const unauthorized = results.find(r => r.status === 'rejected' && r.reason?.response?.status === 401);
      if (unauthorized) {
        navigate('/vod');
        return;
      }

      // Check if critical endpoints (videos, categories, series) all failed
      const criticalFailed = results[0].status === 'rejected' && results[1].status === 'rejected' && results[2].status === 'rejected';
      if (criticalFailed && retryCount < 2) {
        console.warn(`Critical API calls failed, retrying (attempt ${retryCount + 1}/2)...`);
        await new Promise(r => setTimeout(r, 1000));
        return fetchAllData(retryCount + 1);
      }
      
      if (criticalFailed) {
        setDataError(true);
      }

      setVideos(Array.isArray(getValue(0, { videos: [] }).data?.videos) ? getValue(0, { videos: [] }).data.videos : []);
      setCategories(Array.isArray(getValue(1, { categories: [] }).data?.categories) ? getValue(1, { categories: [] }).data.categories : []);
      setSeries(Array.isArray(getValue(2, { series: [] }).data?.series) ? getValue(2, { series: [] }).data.series : []);
      setAnalytics(getValue(3, {}).data || null);
      setAdvancedAnalytics(getValue(4, {}).data || null);
      setMembershipSettings(getValue(5, {}).data || null);
      setSubscribers(Array.isArray(getValue(6, { subscribers: [] }).data?.subscribers) ? getValue(6, { subscribers: [] }).data.subscribers : []);
      setCreatorSubscribers(Array.isArray(getValue(7, { subscribers: [] }).data?.subscribers) ? getValue(7, { subscribers: [] }).data.subscribers : []);
      setCreators(Array.isArray(getValue(8, { profiles: [] }).data?.profiles) ? getValue(8, { profiles: [] }).data.profiles : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (retryCount < 2) {
        console.warn(`fetchAllData error, retrying (attempt ${retryCount + 1}/2)...`);
        await new Promise(r => setTimeout(r, 1000));
        return fetchAllData(retryCount + 1);
      }
      setDataError(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = videos.filter(v => 
    v.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch VOD Purchases
  const fetchPurchases = async () => {
    setLoadingPurchases(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const [purchasesRes, balanceRes] = await Promise.all([
        axios.get(`${API}/api/vod-admin/purchases`, { headers }),
        axios.get(`${API}/api/vod-admin/stripe-balance`, { headers })
      ]);
      setPurchasesData(purchasesRes.data);
      setVodStripeBalance(balanceRes.data);
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoadingPurchases(false);
    }
  };

  // Handle refund
  const handleRefund = async (purchaseId, purchaseType) => {
    if (!window.confirm('Are you sure you want to issue a refund? This action cannot be undone.')) {
      return;
    }
    setProcessingRefund(purchaseId);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      await axios.post(`${API}/api/vod-admin/refund/${purchaseId}?purchase_type=${purchaseType}`, {}, { headers });
      alert('Refund processed successfully!');
      fetchPurchases();
    } catch (err) {
      alert(`Refund failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setProcessingRefund(null);
    }
  };

  // Load purchases when tab changes
  useEffect(() => {
    if (activeTab === 'purchases' && isAuthenticated && authToken) {
      fetchPurchases();
    }
    if (activeTab === 'users' && isAuthenticated && authToken) {
      fetchAllUsers();
    }
    if (activeTab === 'creators' && isAuthenticated && authToken) {
      fetchCreators();
    }
  }, [activeTab, isAuthenticated, authToken]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(
        `${API}/api/vod-admin/users`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setAllUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCreators = async () => {
    setLoadingCreators(true);
    try {
      const response = await axios.get(
        `${API}/api/creators/profiles?active_only=false`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setCreators(response.data.profiles || []);
    } catch (err) {
      console.error('Error fetching creators:', err);
    } finally {
      setLoadingCreators(false);
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (dataError && videos.length === 0) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center" data-testid="data-error-state">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Failed to load dashboard data</h2>
          <p className="text-gray-400 mb-6">There was a problem loading your data. This usually resolves on retry.</p>
          <Button 
            onClick={() => fetchAllData()} 
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
            data-testid="retry-load-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/vod')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
            <img 
              src="/ltv-icon.png" 
              alt="LTV Logo" 
              className="w-8 h-8 object-contain brightness-0 invert"
            />
            <span className="font-bold">VOD Admin</span>
          </div>

          <div className="flex-1" />

          <Button
            onClick={() => navigate('/vod')}
            variant="outline"
            size="sm"
            className="border-gray-700"
          >
            View VOD Site
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section - Traffic Source Style */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <Film className="h-5 w-5 text-white" />
              </div>
              VOD Dashboard
            </h2>
            <p className="text-gray-400 text-sm mt-2">Manage videos, analytics & subscriptions</p>
          </div>
        </div>

        {/* Stats Overview - Only show to users with analytics or videos access */}
        {(hasAccess('analytics') || hasAccess('videos')) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-5 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Video className="h-5 w-5 text-purple-400" />
              <span className="text-gray-300 text-xs">Total Videos</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics?.total_videos || 0}</p>
            <p className="text-xs text-purple-300 mt-1">{analytics?.published_videos || 0} published</p>
          </div>
          <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-5 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="h-5 w-5 text-blue-400" />
              <span className="text-gray-300 text-xs">Verified Views</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics?.verified_views?.toLocaleString() || 0}</p>
            <p className="text-xs text-blue-300 mt-1">{analytics?.total_views?.toLocaleString() || 0} all-time</p>
          </div>
          <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-5 border border-green-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-green-400" />
              <span className="text-gray-300 text-xs">Subscribers</span>
            </div>
            <p className="text-2xl font-bold text-white">{analytics?.active_subscriptions || 0}</p>
            <p className="text-xs text-green-300 mt-1">Premium: {analytics?.premium_subscriptions || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-5 border border-yellow-500/30">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-yellow-400" />
              <span className="text-gray-300 text-xs">Purchase Revenue</span>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/vod-admin/purchases/repair-amounts`, {}, {
                      headers: { Authorization: `Bearer ${authToken}` }
                    });
                    if (res.data.success) {
                      alert(`Fixed: ${res.data.fixed} amounts corrected, ${res.data.recovered || 0} missing purchases recovered from Stripe.\nTotal: ${res.data.total_purchases} purchases, £${res.data.new_total_revenue}`);
                      window.location.reload();
                    } else {
                      alert(res.data.detail || 'Repair failed');
                    }
                  } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
                }}
                className="ml-auto text-[10px] px-2 py-0.5 rounded bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-300 border border-yellow-500/30 transition-colors"
                data-testid="repair-amounts-btn"
              >
                Repair
              </button>
            </div>
            <p className="text-2xl font-bold text-white">£{(analytics?.purchase_revenue || 0).toFixed(2)}</p>
            <p className="text-xs text-yellow-300 mt-1">{analytics?.total_purchases || 0} purchases</p>
          </div>
        </div>
        )}

        {/* Tabs - Grid layout to show all tabs */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-2 mb-6 border-b border-gray-800 pb-4">
          {hasAccess('videos') && <TabButton active={activeTab === 'videos'} onClick={() => setActiveTab('videos')}>
            <Film className="h-4 w-4 mr-1" /> Videos
          </TabButton>}
          {hasAccess('series') && <TabButton active={activeTab === 'series'} onClick={() => setActiveTab('series')}>
            <Tv className="h-4 w-4 mr-1" /> Series
          </TabButton>}
          {hasAccess('purchases') && <TabButton active={activeTab === 'purchases'} onClick={() => setActiveTab('purchases')} gradient="purple" data-testid="purchases-tab-button">
            <CreditCard className="h-4 w-4 mr-1" /> Purchases
          </TabButton>}
          {hasAccess('analytics') && <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
            <BarChart3 className="h-4 w-4 mr-1" /> Analytics
          </TabButton>}
          {hasAccess('categories') && <TabButton active={activeTab === 'categories'} onClick={() => setActiveTab('categories')}>
            <Layers className="h-4 w-4 mr-1" /> Categories
          </TabButton>}
          {hasAccess('membership') && <TabButton active={activeTab === 'membership'} onClick={() => setActiveTab('membership')}>
            <Crown className="h-4 w-4 mr-1" /> Membership
          </TabButton>}
          {hasAccess('subscribers') && <TabButton active={activeTab === 'subscribers'} onClick={() => setActiveTab('subscribers')}>
            <Users className="h-4 w-4 mr-1" /> Subscribers
          </TabButton>}
          {hasAccess('users') && <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            <UserCircle className="h-4 w-4 mr-1" /> All Users
          </TabButton>}
          {hasAccess('ads') && <TabButton active={activeTab === 'ads'} onClick={() => setActiveTab('ads')} gradient="amber">
            <Megaphone className="h-4 w-4 mr-1" /> Ad Management
          </TabButton>}
          {hasAccess('banner-ads') && <TabButton active={activeTab === 'banner-ads'} onClick={() => setActiveTab('banner-ads')} gradient="purple">
            <ImageIcon className="h-4 w-4 mr-1" /> Banner Ads
          </TabButton>}
          {hasAccess('seo') && <TabButton active={activeTab === 'seo'} onClick={() => setActiveTab('seo')} gradient="green" data-testid="seo-tab-button">
            <SearchCode className="h-4 w-4 mr-1" /> SEO
          </TabButton>}
          {hasAccess('blog') && <TabButton active={activeTab === 'blog'} onClick={() => setActiveTab('blog')} gradient="purple" data-testid="blog-tab-button">
            <Edit className="h-4 w-4 mr-1" /> Blog
          </TabButton>}
          {hasAccess('comments') && <TabButton active={activeTab === 'comments'} onClick={() => setActiveTab('comments')} gradient="purple" data-testid="comments-tab-button">
            <Users className="h-4 w-4 mr-1" /> Comments
          </TabButton>}
          {hasAccess('popup') && <TabButton active={activeTab === 'popup'} onClick={() => setActiveTab('popup')} gradient="pink" data-testid="popup-tab-button">
            <Bell className="h-4 w-4 mr-1" /> Popup Settings
          </TabButton>}
          {hasAccess('creators') && <TabButton active={activeTab === 'creators'} onClick={() => setActiveTab('creators')} gradient="blue" data-testid="creators-tab-button">
            <Star className="h-4 w-4 mr-1" /> Creators
          </TabButton>}
          {hasAccess('reels') && <TabButton active={activeTab === 'reels'} onClick={() => setActiveTab('reels')} gradient="pink" data-testid="reels-tab-button">
            <Film className="h-4 w-4 mr-1" /> Reels
          </TabButton>}
          {hasAccess('ad-reels') && <TabButton active={activeTab === 'ad-reels'} onClick={() => setActiveTab('ad-reels')} gradient="yellow" data-testid="ad-reels-tab-button">
            <Megaphone className="h-4 w-4 mr-1" /> Ad Reels
          </TabButton>}
          {hasAccess('homepage') && <TabButton active={activeTab === 'homepage'} onClick={() => setActiveTab('homepage')} gradient="cyan" data-testid="homepage-tab-button">
            <Layers className="h-4 w-4 mr-1" /> Homepage Layout
          </TabButton>}
          {hasAccess('revenue') && <TabButton active={activeTab === 'revenue'} onClick={() => setActiveTab('revenue')} gradient="green" data-testid="revenue-tab-button">
            <DollarSign className="h-4 w-4 mr-1" /> Revenue
          </TabButton>}
          {hasAccess('reels-analytics') && <TabButton active={activeTab === 'reels-analytics'} onClick={() => setActiveTab('reels-analytics')} gradient="pink" data-testid="reels-analytics-tab-button">
            <BarChart3 className="h-4 w-4 mr-1" /> Reels Analytics
          </TabButton>}
          {hasAccess('creator-accounts') && <TabButton active={activeTab === 'creator-accounts'} onClick={() => setActiveTab('creator-accounts')} gradient="teal" data-testid="creator-accounts-tab-button">
            <UserCog className="h-4 w-4 mr-1" /> Creator Accounts
          </TabButton>}
          {hasAccess('subscriptions') && <TabButton active={activeTab === 'subscriptions'} onClick={() => setActiveTab('subscriptions')} gradient="green" data-testid="subscriptions-tab-button">
            <Crown className="h-4 w-4 mr-1" /> Subscriptions
          </TabButton>}
          {hasAccess('stripe-settings') && <TabButton active={activeTab === 'stripe-settings'} onClick={() => setActiveTab('stripe-settings')} gradient="purple" data-testid="stripe-settings-tab-button">
            <CreditCard className="h-4 w-4 mr-1" /> Stripe Settings
          </TabButton>}
          {hasAccess('revenue') && <TabButton active={activeTab === 'creator-earnings'} onClick={() => setActiveTab('creator-earnings')} gradient="amber" data-testid="creator-earnings-tab-button">
            <Banknote className="h-4 w-4 mr-1" /> Creator Earnings
          </TabButton>}
          {hasAccess('purchases') && <TabButton active={activeTab === 'promo-codes'} onClick={() => setActiveTab('promo-codes')} gradient="green" data-testid="promo-codes-tab-button">
            <Percent className="h-4 w-4 mr-1" /> Promo Codes
          </TabButton>}
          {isSuperAdmin && <TabButton active={activeTab === 'staff'} onClick={() => setActiveTab('staff')} gradient="purple" data-testid="staff-tab-button">
            <Shield className="h-4 w-4 mr-1" /> Staff
          </TabButton>}
          {isSuperAdmin && <TabButton active={activeTab === 'maintenance'} onClick={() => setActiveTab('maintenance')} gradient="amber" data-testid="maintenance-tab-button">
            <Wrench className="h-4 w-4 mr-1" /> Maintenance
          </TabButton>}
        </div>

        {/* Tab Content */}
        {activeTab === 'videos' && (
          <VideosTab 
            videos={filteredVideos}
            categories={categories}
            series={series}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onAdd={() => { setEditingVideo(null); setShowVideoModal(true); }}
            onEdit={(video) => { setEditingVideo(video); setShowVideoModal(true); }}
            onRefresh={fetchAllData}
            dataError={dataError}
          />
        )}

        {activeTab === 'series' && (
          <SeriesTab 
            series={series}
            categories={categories}
            onAdd={() => { setEditingSeries(null); setShowSeriesModal(true); }}
            onEdit={(s) => { setEditingSeries(s); setShowSeriesModal(true); }}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'purchases' && (
          <PurchasesTab 
            purchasesData={purchasesData}
            stripeBalance={vodStripeBalance}
            loading={loadingPurchases}
            processingRefund={processingRefund}
            onRefund={handleRefund}
            onRefresh={fetchPurchases}
          />
        )}

        {activeTab === 'analytics' && (
          <AdvancedAnalyticsTab 
            analytics={advancedAnalytics}
            basicAnalytics={analytics}
          />
        )}

        {activeTab === 'categories' && (
          <CategoriesTab 
            categories={categories}
            onAdd={() => { setEditingCategory(null); setShowCategoryModal(true); }}
            onEdit={(cat) => { setEditingCategory(cat); setShowCategoryModal(true); }}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'membership' && (
          <MembershipTab 
            settings={membershipSettings}
            onRefresh={fetchAllData}
          />
        )}

        {activeTab === 'subscribers' && (
          <SubscribersTab subscribers={subscribers} creatorSubscribers={creatorSubscribers} onRefresh={fetchAllData} />
        )}

        {activeTab === 'users' && (
          <UsersTab 
            users={allUsers} 
            loading={loadingUsers}
            onRefresh={fetchAllUsers}
          />
        )}
        
        {activeTab === 'ads' && (
          <AdManagementCombined authToken={authToken} />
        )}

        {activeTab === 'banner-ads' && (
          <BannerAdManagement />
        )}

        {activeTab === 'seo' && (
          <SEOAdmin platform="vod" authToken={authToken} />
        )}

        {activeTab === 'blog' && (
          <BlogManagement authToken={authToken} />
        )}

        {activeTab === 'comments' && (
          <CommentsReactionsAdmin authToken={authToken} />
        )}

        {activeTab === 'popup' && (
          <PopupSettingsTab authToken={authToken} />
        )}

        {activeTab === 'creators' && (
          <CreatorsTab 
            creators={creators}
            loading={loadingCreators}
            onAdd={() => { setEditingCreator(null); setShowCreatorModal(true); }}
            onEdit={(creator) => { setEditingCreator(creator); setShowCreatorModal(true); }}
            onRefresh={fetchCreators}
            authToken={authToken}
          />
        )}

        {activeTab === 'reels' && (
          <ReelsTab 
            creators={creators}
            authToken={authToken}
          />
        )}

        {activeTab === 'ad-reels' && (
          <AdReelsTab authToken={authToken} />
        )}

        {activeTab === 'homepage' && (
          <HomepageLayoutTab authToken={authToken} />
        )}

        {activeTab === 'revenue' && (
          <RevenueTab authToken={authToken} />
        )}

        {activeTab === 'reels-analytics' && (
          <ReelsAnalyticsTab authToken={authToken} />
        )}

        {activeTab === 'creator-accounts' && (
          <CreatorAccountsTab 
            creators={creators}
            authToken={authToken}
          />
        )}

        {activeTab === 'subscriptions' && (
          <SubscriptionsTab authToken={authToken} />
        )}

        {activeTab === 'stripe-settings' && (
          <StripeSettingsTab authToken={authToken} />
        )}

        {activeTab === 'creator-earnings' && (
          <CreatorEarningsTab authToken={authToken} />
        )}

        {activeTab === 'promo-codes' && (
          <PromoCodesTab authToken={authToken} />
        )}

        {activeTab === 'staff' && isSuperAdmin && (
          <StaffManagement authToken={authToken} />
        )}
        {activeTab === 'maintenance' && isSuperAdmin && (
          <MaintenanceTab authToken={authToken} />
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <VideoModal 
          video={editingVideo}
          categories={categories}
          series={series}
          creators={creators}
          onClose={() => setShowVideoModal(false)}
          onSave={fetchAllData}
        />
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <CategoryModal 
          category={editingCategory}
          onClose={() => setShowCategoryModal(false)}
          onSave={fetchAllData}
        />
      )}

      {/* Series Modal */}
      {showSeriesModal && (
        <SeriesModal 
          series={editingSeries}
          categories={categories}
          onClose={() => setShowSeriesModal(false)}
          onSave={fetchAllData}
        />
      )}

      {/* Creator Modal */}
      {showCreatorModal && (
        <CreatorModal 
          creator={editingCreator}
          onClose={() => setShowCreatorModal(false)}
          onSave={() => { setShowCreatorModal(false); fetchCreators(); }}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subtext }) => (
  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400">
        {icon}
      </div>
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

// Tab Button Component - Traffic Source Style
const TabButton = ({ active, onClick, children, gradient, ...props }) => {
  const gradientStyles = {
    amber: 'from-amber-600 to-orange-600',
    green: 'from-green-600 to-emerald-600',
    purple: 'from-purple-600 to-pink-600',
    blue: 'from-blue-600 to-cyan-600',
    pink: 'from-pink-600 to-rose-600',
    red: 'from-red-600 to-rose-600',
    yellow: 'from-yellow-600 to-orange-600',
    cyan: 'from-cyan-600 to-teal-600'
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center px-3 py-2 rounded-lg font-medium transition-all text-sm ${
        active 
          ? gradient && gradientStyles[gradient]
            ? `bg-gradient-to-r ${gradientStyles[gradient]} text-white border-0 shadow-lg`
            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg'
          : 'text-gray-400 hover:text-white hover:bg-gray-800 bg-gray-900 border border-gray-800'
      }`}
      {...props}
    >
      {children}
    </button>
  );
};

// Videos Tab
const VideosTab = ({ videos, categories, series, searchQuery, setSearchQuery, onAdd, onEdit, onRefresh, dataError }) => {
  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      await axios.delete(`${API}/api/vod-admin/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete video');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Uncategorized';
  };

  const getSeriesName = (seriesId) => {
    if (!seriesId) return null;
    const s = series.find(s => s.id === seriesId);
    return s?.title || null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Film className="h-5 w-5 text-purple-400" />
            Video Library
          </h3>
          <p className="text-gray-400 text-sm mt-1">Manage all your VOD content</p>
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800 border-gray-700 focus:border-purple-500"
            />
          </div>
          <Button onClick={onAdd} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500">
            <Plus className="h-4 w-4 mr-2" /> Add Video
          </Button>
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Video</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Category</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Series</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Tier</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Price</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Views</th>
                <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Status</th>
                <th className="text-right text-xs text-gray-400 font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {videos.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                      <Video className="h-8 w-8 text-gray-600" />
                    </div>
                    <p className="text-gray-400">{dataError ? 'Failed to load videos. Please try again.' : 'No videos found. Add your first video!'}</p>
                    {dataError && (
                      <Button onClick={onRefresh} variant="outline" size="sm" className="mt-4 border-gray-700" data-testid="videos-retry-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />Retry
                      </Button>
                    )}
                  </td>
                </tr>
              ) : (
                videos.map((video) => (
                  <tr key={video.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-10 rounded-lg bg-gray-800 overflow-hidden flex-shrink-0">
                          {video.thumbnail_url ? (
                            <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-4 w-4 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-white truncate max-w-xs">{video.title}</div>
                          <div className="text-xs text-gray-500 truncate max-w-xs">{video.description?.slice(0, 50)}</div>
                          {video.creator_name && (
                            <Badge className="bg-gradient-to-r from-cyan-600/20 to-teal-600/10 text-cyan-400 border-cyan-500/30 text-xs mt-1">
                              <Star className="h-3 w-3 mr-1" />
                              {video.creator_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-400">{getCategoryName(video.category_id)}</td>
                    <td className="p-4">
                      {video.series_id ? (
                        <div className="flex items-center gap-1">
                          <Badge className="bg-blue-600/20 text-blue-400 text-xs">
                            <Tv className="h-3 w-3 mr-1" />
                            S{video.season_number || 1}E{video.episode_number || 1}
                          </Badge>
                          <span className="text-xs text-gray-500 truncate max-w-[100px]">
                            {getSeriesName(video.series_id)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={video.tier === 'premium' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-700'}>
                        {video.tier === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                        {video.tier}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {(video.price || 0) <= 0 ? (
                        <span className="text-green-400 text-sm font-medium">Free</span>
                      ) : video.sale_price ? (
                        <div>
                          <span className="text-green-400">£{video.sale_price?.toFixed(2)}</span>
                          <span className="text-gray-500 line-through text-xs ml-2">£{video.price?.toFixed(2)}</span>
                        </div>
                      ) : (
                        <span className="text-amber-400 font-medium">£{video.price?.toFixed(2)}</span>
                      )}
                    </td>
                    <td className="p-4 text-gray-400">{video.view_count || 0}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {/* Visibility Status Badge */}
                        {(() => {
                          const status = video.visibility_status || (video.is_published ? 'public' : 'private');
                          switch (status) {
                            case 'public':
                              return <Badge className="bg-green-600">Public</Badge>;
                            case 'unlisted':
                              return <Badge className="bg-yellow-600">Unlisted</Badge>;
                            case 'private':
                              return <Badge className="bg-gray-600">Private</Badge>;
                            case 'premiere':
                              return <Badge className="bg-purple-600">Premiere</Badge>;
                            default:
                              return <Badge className={video.is_published ? 'bg-green-600' : 'bg-yellow-600'}>
                                {video.is_published ? 'Published' : 'Draft'}
                              </Badge>;
                          }
                        })()}
                        {video.free_preview && (
                          <Badge className="bg-blue-600">FREE</Badge>
                        )}
                        {video.visibility_status === 'premiere' && video.premiere_date && (
                          <span className="text-xs text-purple-400">
                            {new Date(video.premiere_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Copy Link Button - useful for unlisted videos */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const isUnlisted = video.visibility_status === 'unlisted';
                            const videoUrl = isUnlisted 
                              ? `${baseUrl}/vod/watch/${video.slug}?autoplay=true`
                              : `${baseUrl}/vod/watch/${video.slug}`;
                            navigator.clipboard.writeText(videoUrl);
                            alert(isUnlisted 
                              ? 'Unlisted video link copied! (with autoplay)' 
                              : 'Link copied to clipboard!');
                          }}
                          className="text-blue-400 hover:text-blue-300"
                          title={video.visibility_status === 'unlisted' ? "Copy unlisted link (with autoplay)" : "Copy video link"}
                        >
                          <LinkIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(video)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(video.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Categories Tab
const CategoriesTab = ({ categories, onAdd, onEdit, onRefresh }) => {
  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/api/vod-admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Button onClick={onAdd} className="bg-purple-600 hover:bg-purple-500">
          <Plus className="h-4 w-4 mr-2" /> Add Category
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => (
          <div key={cat.id} className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{cat.icon || '📁'}</span>
                <div>
                  <h3 className="font-bold">{cat.name}</h3>
                  <p className="text-sm text-gray-500">{cat.video_count || 0} videos</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(cat)}
                  className="text-gray-400 hover:text-white"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(cat.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-400">{cat.description || 'No description'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Series Tab
const SeriesTab = ({ series, categories, onAdd, onEdit, onRefresh }) => {
  const navigate = useNavigate();
  
  const handleDelete = async (seriesId) => {
    if (!window.confirm('Are you sure you want to delete this series? Episodes will be unlinked but not deleted.')) return;
    
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      await axios.delete(`${API}/api/vod-admin/series/${seriesId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Failed to delete series');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Uncategorized';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-400">
          Organize related videos into series with seasons and episodes
        </p>
        <Button onClick={onAdd} className="bg-purple-600 hover:bg-purple-500">
          <Plus className="h-4 w-4 mr-2" /> Add Series
        </Button>
      </div>

      {series.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800">
          <Tv className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500 mb-4">No series created yet</p>
          <Button onClick={onAdd} className="bg-purple-600 hover:bg-purple-500">
            <Plus className="h-4 w-4 mr-2" /> Create Your First Series
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {series.map((s) => (
            <div key={s.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden group">
              {/* Thumbnail */}
              <div className="aspect-video bg-gray-800 relative overflow-hidden">
                {s.thumbnail_url ? (
                  <img 
                    src={s.thumbnail_url} 
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Tv className="h-12 w-12 text-gray-600" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Stats overlay */}
                <div className="absolute bottom-2 left-2 flex items-center gap-2">
                  <Badge className="bg-black/50 text-white text-xs">
                    {s.season_count || 0} Season{(s.season_count || 0) !== 1 ? 's' : ''}
                  </Badge>
                  <Badge className="bg-black/50 text-white text-xs">
                    {s.episode_count || 0} Episode{(s.episode_count || 0) !== 1 ? 's' : ''}
                  </Badge>
                </div>

                {/* Tier badge */}
                <div className="absolute top-2 right-2">
                  <Badge className={s.tier === 'premium' 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                    : 'bg-gray-700'
                  }>
                    {s.tier === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                    {s.tier}
                  </Badge>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-lg line-clamp-1">{s.title}</h3>
                  {!s.is_published && (
                    <Badge className="bg-yellow-600 text-xs flex-shrink-0">Draft</Badge>
                  )}
                </div>
                
                <p className="text-sm text-gray-500 mb-1">
                  {getCategoryName(s.category_id)}
                </p>
                
                <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                  {s.description || 'No description'}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/vod/series/${s.id}`)}
                    className="flex-1 border-gray-700"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(s)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(s.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Advanced Analytics Tab - YouTube-style dashboard
const AdvancedAnalyticsTab = ({ analytics, basicAnalytics }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoDetails, setVideoDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [filteredAnalytics, setFilteredAnalytics] = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [analyticsSubTab, setAnalyticsSubTab] = useState('overview'); // 'overview' | 'geographic'
  const [resetting, setResetting] = useState(false);
  
  // Real-time stats
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [realtimeDashboard, setRealtimeDashboard] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const handleResetAnalytics = async () => {
    if (!window.confirm('Are you sure you want to reset ALL analytics data? This cannot be undone. All view counts, watch events, and page views will be set to zero.')) return;
    if (!window.confirm('This is your final confirmation. ALL analytics data will be permanently deleted. Continue?')) return;
    
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      const res = await axios.post(`${API}/api/vod-admin/analytics/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Analytics reset complete!\n\nVideos reset: ${res.data.videos_reset}\nWatch events cleared: ${res.data.watch_events_cleared}\nPage views cleared: ${res.data.page_views_cleared}\n\nAll analytics now start from zero.`);
      window.location.reload();
    } catch (err) {
      console.error('Error resetting analytics:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert(`Failed to reset analytics: ${msg}`);
    } finally {
      setResetting(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const handleSyncViewCounts = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      const res = await axios.post(`${API}/api/vod-admin/analytics/sync-view-counts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`View counts synced!\n\nVideos initialized: ${res.data.videos_initialized}\nVideos synced: ${res.data.videos_synced}\nTotal unique views: ${res.data.total_unique_view_records}`);
      window.location.reload();
    } catch (err) {
      console.error('Error syncing view counts:', err);
      alert('Failed to sync view counts. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const CHART_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const formatWatchTime = (hours, minutes) => {
    if (hours >= 1000) return `${(hours / 1000).toFixed(1)}K h`;
    if (hours > 0) return `${hours}h ${minutes || 0}m`;
    return `${minutes || 0}m`;
  };
  
  // Fetch real-time stats
  const fetchRealtimeStats = async () => {
    try {
      const [statsRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/api/realtime/stats`),
        axios.get(`${API}/api/realtime/dashboard`)
      ]);
      setRealtimeStats(statsRes.data);
      setRealtimeDashboard(dashboardRes.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching realtime stats:', err);
    }
  };
  
  // Auto-refresh real-time stats every 5 seconds for live updates
  useEffect(() => {
    fetchRealtimeStats();
    const interval = setInterval(fetchRealtimeStats, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch filtered analytics when time range changes
  const fetchFilteredAnalytics = async (range) => {
    setLoadingFilter(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      const res = await axios.get(`${API}/api/vod-admin/analytics/advanced?range=${range}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilteredAnalytics(res.data);
    } catch (err) {
      console.error('Error fetching filtered analytics:', err);
      setFilteredAnalytics(analytics); // Fallback to original
    } finally {
      setLoadingFilter(false);
    }
  };

  // Handle time range change
  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    fetchFilteredAnalytics(range);
  };

  // Initialize with current analytics, then refresh periodically
  useEffect(() => {
    if (analytics) {
      setFilteredAnalytics(analytics);
    }
    // Fresh fetch on mount to ensure latest data
    fetchFilteredAnalytics(timeRange);
    // Auto-refresh advanced analytics every 60 seconds
    const interval = setInterval(() => fetchFilteredAnalytics(timeRange), 60000);
    return () => clearInterval(interval);
  }, [analytics]);

  const fetchVideoDetails = async (videoId) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
      const res = await axios.get(`${API}/api/vod-admin/analytics/video/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideoDetails(res.data);
    } catch (err) {
      console.error('Error fetching video details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (!analytics || !filteredAnalytics) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const { summary, video_performance, hour_distribution, day_of_week_stats, 
          traffic_sources, device_breakdown, series_performance, completion_distribution,
          daily_views, realtime_views, impressions } = filteredAnalytics;

  // Prepare chart data based on time range
  const getDaysForRange = () => {
    switch(timeRange) {
      case '2d': return 2;
      case '4d': return 4;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 9999;
    }
  };

  const dailyChartData = daily_views?.slice(-getDaysForRange()).map(d => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    views: d.views
  })) || [];

  const hourlyData = hour_distribution?.map((count, hour) => ({
    hour: `${hour}:00`,
    views: count
  })) || [];

  const devicePieData = device_breakdown?.map((d, i) => ({
    name: d.device,
    value: d.views,
    color: CHART_COLORS[i % CHART_COLORS.length]
  })) || [];

  return (
    <div className="space-y-6">
      {/* Real-time Stats Banner */}
      <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/30 rounded-xl p-4 border border-green-500/30">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full">
              <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              <span className="text-green-400 text-lg font-bold">
                {realtimeStats?.total_watching_now || 0}
              </span>
              <span className="text-green-300 text-sm">watching now</span>
            </div>
            <div className="hidden md:flex items-center gap-4 text-sm">
              <span className="text-gray-400">
                <Play className="h-3.5 w-3.5 inline mr-1 text-green-400" />
                {realtimeStats?.playing_now || 0} playing
              </span>
              <span className="text-gray-400">
                <Pause className="h-3.5 w-3.5 inline mr-1 text-yellow-400" />
                {realtimeStats?.paused_now || 0} paused
              </span>
              {realtimeStats?.pip_viewers > 0 && (
                <span className="text-gray-400">
                  <Monitor className="h-3.5 w-3.5 inline mr-1 text-blue-400" />
                  {realtimeStats.pip_viewers} PiP
                </span>
              )}
            </div>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <Activity className="h-3 w-3 text-green-400 animate-pulse" />
            Live • Updated {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'now'}
          </div>
        </div>
        
        {/* Top Videos Being Watched Now */}
        {realtimeStats?.top_videos_now?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-green-500/20">
            <p className="text-xs text-gray-400 mb-2">Currently Being Watched:</p>
            <div className="flex flex-wrap gap-2">
              {realtimeStats.top_videos_now.slice(0, 5).map((video, i) => (
                <div key={video.video_id} className="flex items-center gap-2 bg-gray-800/50 px-2 py-1 rounded text-xs">
                  <span className="text-gray-300 truncate max-w-[150px]">{video.title}</span>
                  <span className="text-green-400 font-medium">{video.active_viewers}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Sub-tab Navigation */}
      <div className="flex gap-2 border-b border-gray-700 pb-2 overflow-x-auto">
        <button
          onClick={() => setAnalyticsSubTab('overview')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'overview'
              ? 'bg-purple-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <BarChart3 className="h-4 w-4 inline mr-2" />
          Overview
        </button>
        <button
          onClick={() => setAnalyticsSubTab('realtime')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'realtime'
              ? 'bg-red-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Zap className="h-4 w-4 inline mr-2" />
          Real-Time
        </button>
        <button
          onClick={() => setAnalyticsSubTab('content')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'content'
              ? 'bg-orange-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Film className="h-4 w-4 inline mr-2" />
          Content Performance
        </button>
        <button
          onClick={() => setAnalyticsSubTab('behavior')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'behavior'
              ? 'bg-pink-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Users className="h-4 w-4 inline mr-2" />
          User Behavior
        </button>
        <button
          onClick={() => setAnalyticsSubTab('geographic')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'geographic'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Globe className="h-4 w-4 inline mr-2" />
          Geographic & Demographics
        </button>
        <button
          onClick={() => setAnalyticsSubTab('traffic')}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            analyticsSubTab === 'traffic'
              ? 'bg-green-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Link2 className="h-4 w-4 inline mr-2" />
          Traffic Sources
        </button>
        
        {/* Sync & Reset Analytics Buttons */}
        <button
          onClick={handleSyncViewCounts}
          disabled={syncing}
          data-testid="sync-view-counts-button"
          className="ml-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/50 hover:text-emerald-300 border border-emerald-500/30"
        >
          {syncing ? 'Syncing...' : 'Sync View Counts'}
        </button>
        <button
          onClick={handleResetAnalytics}
          disabled={resetting}
          data-testid="reset-analytics-button"
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap bg-red-900/30 text-red-400 hover:bg-red-900/50 hover:text-red-300 border border-red-500/30"
        >
          {resetting ? 'Resetting...' : 'Reset All Analytics'}
        </button>
      </div>
      
      {/* Real-Time Tab */}
      {analyticsSubTab === 'realtime' && (
        <RealTimeSection data={filteredAnalytics} />
      )}
      
      {/* Content Performance Tab */}
      {analyticsSubTab === 'content' && (
        <ContentPerformanceSection data={filteredAnalytics} />
      )}
      
      {/* User Behavior Tab */}
      {analyticsSubTab === 'behavior' && (
        <UserBehaviorSection data={filteredAnalytics} />
      )}
      
      {/* Geographic & Demographics Tab */}
      {analyticsSubTab === 'geographic' && (
        <GeographicDemographicsSection data={filteredAnalytics} formatDuration={formatDuration} />
      )}
      
      {/* Traffic Sources Tab */}
      {analyticsSubTab === 'traffic' && (
        <TrafficSourcesSection data={filteredAnalytics} />
      )}
      
      {/* Overview Tab Content */}
      {analyticsSubTab === 'overview' && (
      <>
      {/* Header with Time Range Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Analytics Overview</h3>
          {loadingFilter && <Loader2 className="h-4 w-4 animate-spin text-purple-400" />}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: '2d', label: '2 Days' },
            { key: '4d', label: '4 Days' },
            { key: '7d', label: '7 Days' },
            { key: '30d', label: '30 Days' },
            { key: '90d', label: '90 Days' },
            { key: 'all', label: 'All Time' }
          ].map(range => (
            <button
              key={range.key}
              onClick={() => handleTimeRangeChange(range.key)}
              disabled={loadingFilter}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                timeRange === range.key 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              } disabled:opacity-50`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick Stats Row - Real-time Dashboard Data */}
      {realtimeDashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-xs mb-1">Verified Views (Last Hour)</p>
            <p className="text-2xl font-bold text-white">{realtimeDashboard.hourly_stats?.views_last_hour || 0}</p>
            <p className="text-xs text-cyan-400 mt-1">
              {(() => {
                const m = realtimeDashboard.hourly_stats?.watch_minutes_last_hour || 0;
                const hrs = Math.floor(m / 60);
                const mins = Math.round(m % 60);
                return `${hrs}h ${mins}m watched`;
              })()}
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-xs mb-1">Verified Views (24h)</p>
            <p className="text-2xl font-bold text-white">{realtimeDashboard.daily_stats?.views_24h || 0}</p>
            <p className="text-xs text-blue-400 mt-1">
              {(() => {
                const m = Math.round(realtimeDashboard.daily_stats?.watch_minutes_24h || 0);
                const hrs = Math.floor(m / 60);
                const mins = m % 60;
                return `${hrs}h ${mins}m total`;
              })()}
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-xs mb-1">Unique Viewers (24h)</p>
            <p className="text-2xl font-bold text-white">{realtimeDashboard.daily_stats?.unique_viewers_24h || 0}</p>
            <p className="text-xs text-purple-400 mt-1">
              {(() => {
                const m = realtimeDashboard.daily_stats?.avg_watch_time_24h || 0;
                const hrs = Math.floor(m / 60);
                const mins = Math.round(m % 60);
                return `${hrs}h ${mins}m avg`;
              })()}
            </p>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <p className="text-gray-400 text-xs mb-1">Trending Now</p>
            <p className="text-sm font-medium text-white truncate">
              {realtimeDashboard.trending_videos?.[0]?.title || 'No data'}
            </p>
            <p className="text-xs text-yellow-400 mt-1">
              {realtimeDashboard.trending_videos?.[0]?.views_24h || 0} views today
            </p>
          </div>
        </div>
      )}

      {/* Main Layout - Sidebar Stats + Large Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Key Stats */}
        <div className="lg:col-span-1 space-y-4">
          {/* Views Card */}
          <div className="bg-gradient-to-br from-teal-600/30 to-cyan-600/20 rounded-xl p-5 border border-teal-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-5 w-5 text-teal-400" />
              <span className="text-gray-300 text-sm font-medium">Verified Views</span>
            </div>
            <p className="text-3xl font-bold text-white">{summary?.verified_views?.toLocaleString() || 0}</p>
            <p className="text-xs text-teal-300 mt-2">+{realtimeDashboard?.daily_stats?.views_24h || 0} today</p>
            <p className="text-xs text-gray-400 mt-1">{summary?.total_views?.toLocaleString() || 0} all-time</p>
          </div>

          {/* Watch Time Card */}
          <div className="bg-gradient-to-br from-blue-600/30 to-indigo-600/20 rounded-xl p-5 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-blue-400" />
              <span className="text-gray-300 text-sm font-medium">Watch Time</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {formatWatchTime(summary?.total_watch_time_hours || 0, summary?.total_watch_time_minutes || 0)}
            </p>
            <p className="text-xs text-blue-300 mt-2">{Math.round((summary?.total_watch_time_seconds || 0) / 60).toLocaleString()} total minutes</p>
          </div>

          {/* Avg Watch Time Card */}
          <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-5 border border-purple-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-5 w-5 text-purple-400" />
              <span className="text-gray-300 text-sm font-medium">Avg Watch Time</span>
            </div>
            <p className="text-3xl font-bold text-white">{formatDuration(summary?.avg_watch_time_seconds || 0)}</p>
            <p className="text-xs text-purple-300 mt-2">{summary?.overall_avg_completion || 0}% avg completion</p>
          </div>
        </div>

        {/* Main Chart Area - Metric Evolution */}
        <div className="lg:col-span-3 bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-400" />
              Metric Evolution
            </h3>
            <p className="text-xs text-gray-500 mt-1">Views over the selected time period</p>
          </div>
          <div className="p-4" style={{ height: '320px' }}>
            {dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyChartData}>
                  <defs>
                    <linearGradient id="viewsGradientMain" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Area type="monotone" dataKey="views" stroke="#8b5cf6" strokeWidth={2} fill="url(#viewsGradientMain)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">No data available for this period</div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Engagement Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-cyan-400" />
            <span className="text-gray-400 text-xs">Unique Viewers</span>
          </div>
          <p className="text-xl font-bold text-white">{summary?.unique_viewers?.toLocaleString() || 0}</p>
          <p className="text-xs text-cyan-400 mt-1">
            {summary?.views_per_viewer?.toFixed(1) || 0} views/viewer
          </p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-4 w-4 text-green-400" />
            <span className="text-gray-400 text-xs">Engagement Rate</span>
          </div>
          <p className="text-xl font-bold text-white">{summary?.engagement_rate || 0}%</p>
          <p className="text-xs text-green-400 mt-1">50%+ completion</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpRight className="h-4 w-4 text-red-400" />
            <span className="text-gray-400 text-xs">Bounce Rate</span>
          </div>
          <p className="text-xl font-bold text-white" data-testid="bounce-rate">{summary?.bounce_rate || 0}%</p>
          <p className="text-xs text-red-400 mt-1">Left before 30s</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="h-4 w-4 text-purple-400" />
            <span className="text-gray-400 text-xs">Return Viewers</span>
          </div>
          <p className="text-xl font-bold text-white" data-testid="return-viewer-rate">{summary?.return_viewer_rate || 0}%</p>
          <p className="text-xs text-purple-400 mt-1">Watched 2+ videos</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Timer className="h-4 w-4 text-yellow-400" />
            <span className="text-gray-400 text-xs">Avg Session</span>
          </div>
          <p className="text-xl font-bold text-white">{formatDuration(summary?.avg_session_duration_seconds || 0)}</p>
          <p className="text-xs text-yellow-400 mt-1">Per view</p>
        </div>
        
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-4 w-4 text-orange-400" />
            <span className="text-gray-400 text-xs">Peak Hour</span>
          </div>
          <p className="text-xl font-bold text-white">{summary?.peak_hour || 0}:00</p>
          <p className="text-xs text-orange-400 mt-1">{summary?.peak_day || 'N/A'}</p>
        </div>
      </div>

      {/* Bottom Row - Three Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Content Performance - Top Videos Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Film className="h-5 w-5 text-purple-400" />
              Content Performance
            </h3>
            <p className="text-xs text-gray-500 mt-1">Top 20 Videos</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {video_performance?.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No watch data yet
              </div>
            ) : (
              <div className="divide-y divide-gray-800/50">
                {video_performance?.slice(0, 20).map((video, idx) => (
                  <div 
                    key={video.id} 
                    className="p-3 hover:bg-gray-800/30 cursor-pointer transition-colors flex items-center gap-3"
                    onClick={() => { setSelectedVideo(video); fetchVideoDetails(video.id); }}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.verified_views?.toLocaleString() || 0} verified views • {video.avg_completion_rate}% completion</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Viewing Patterns - Time Heatmap */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              Viewing Patterns
            </h3>
            <p className="text-xs text-gray-500 mt-1">Time Heatmap</p>
          </div>
          <div className="p-4" style={{ height: '350px' }}>
            {hourlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                  <XAxis type="number" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="hour" stroke="#9ca3af" fontSize={9} width={40} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                  <Bar dataKey="views" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">No data available</div>
            )}
          </div>
        </div>

        {/* Library Segmentation - Device/Completion Pie */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Monitor className="h-5 w-5 text-green-400" />
              Library Segmentation
            </h3>
            <p className="text-xs text-gray-500 mt-1">Device Types</p>
          </div>
          <div className="p-4" style={{ height: '350px' }}>
            {devicePieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={devicePieData}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={90}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {devicePieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={50}
                    formatter={(value) => <span className="text-xs text-gray-300">{value}</span>}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">No device data</div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day of Week Stats */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-400" />
              Views by Day of Week
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {day_of_week_stats?.map((day) => {
              const maxViews = Math.max(...day_of_week_stats.map(d => d.views));
              const width = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
              return (
                <div key={day.day} className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 w-24 font-medium">{day.day}</span>
                  <div className="flex-1 h-5 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full transition-all"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-300 w-12 text-right font-medium">{day.views}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-400" />
              Traffic Sources
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {traffic_sources?.length > 0 ? (
              traffic_sources.slice(0, 6).map((source, idx) => {
                const maxViews = Math.max(...traffic_sources.map(s => s.views));
                const width = maxViews > 0 ? (source.views / maxViews) * 100 : 0;
                return (
                  <div key={source.source} className="flex items-center gap-3">
                    <span className="text-sm text-gray-400 w-24 truncate">{source.source}</span>
                    <div className="flex-1 h-5 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all" 
                        style={{ width: `${width}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                      />
                    </div>
                    <span className="text-sm text-gray-300 w-12 text-right font-medium">{source.views}</span>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 text-sm py-4">No traffic data</div>
            )}
          </div>
        </div>
      </div>
      </>
      )}

      {/* Video Detail Modal */}
      {selectedVideo && (
        <VideoAnalyticsModal 
          video={selectedVideo}
          details={videoDetails}
          loading={loadingDetails}
          onClose={() => { setSelectedVideo(null); setVideoDetails(null); }}
          formatDuration={formatDuration}
        />
      )}
    </div>
  );
};

// Geographic & Demographics Section Component
const GeographicDemographicsSection = ({ data, formatDuration }) => {
  const geographic = data?.geographic || {};
  const os_breakdown = data?.os_breakdown || [];
  const device_breakdown = data?.device_breakdown || [];
  const browser_breakdown = data?.browser_breakdown || [];
  
  const countries = geographic.countries || [];
  const cities = geographic.cities || [];
  const regions = geographic.regions || [];
  
  const CHART_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];
  
  // Prepare pie chart data
  const devicePieData = device_breakdown.map((d, i) => ({
    name: d.device || 'Unknown',
    value: d.views,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));
  
  const browserPieData = browser_breakdown.map((d, i) => ({
    name: d.browser || 'Unknown',
    value: d.views,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));
  
  const osPieData = os_breakdown.map((d, i) => ({
    name: d.os || 'Unknown',
    value: d.views,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));
  
  const countryBarData = countries.slice(0, 10).map(c => ({
    name: c.country,
    views: c.views,
    percentage: c.percentage
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-sm">Countries</span>
          </div>
          <p className="text-2xl font-bold text-white">{geographic.total_countries || 0}</p>
          <p className="text-xs text-blue-300 mt-1">Unique locations</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Monitor className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-sm">Desktop</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {device_breakdown.find(d => d.device === 'desktop')?.views || 0}
          </p>
          <p className="text-xs text-purple-300 mt-1">
            {((device_breakdown.find(d => d.device === 'desktop')?.views || 0) / 
              (device_breakdown.reduce((a, b) => a + b.views, 0) || 1) * 100).toFixed(1)}% of viewers
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-sm">Mobile</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {device_breakdown.find(d => d.device === 'mobile')?.views || 0}
          </p>
          <p className="text-xs text-green-300 mt-1">
            {((device_breakdown.find(d => d.device === 'mobile')?.views || 0) / 
              (device_breakdown.reduce((a, b) => a + b.views, 0) || 1) * 100).toFixed(1)}% of viewers
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-orange-600/30 to-amber-600/20 rounded-xl p-4 border border-orange-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Tablet className="h-5 w-5 text-orange-400" />
            <span className="text-gray-300 text-sm">Tablet</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {device_breakdown.find(d => d.device === 'tablet')?.views || 0}
          </p>
          <p className="text-xs text-orange-300 mt-1">
            {((device_breakdown.find(d => d.device === 'tablet')?.views || 0) / 
              (device_breakdown.reduce((a, b) => a + b.views, 0) || 1) * 100).toFixed(1)}% of viewers
          </p>
        </div>
      </div>

      {/* Country/Region Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Country Breakdown - Bar Chart */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" />
              Top Countries
            </h3>
            <p className="text-xs text-gray-500 mt-1">Viewer distribution by country</p>
          </div>
          <div className="p-4" style={{ height: '350px' }}>
            {countryBarData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryBarData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#9ca3af" fontSize={11} width={100} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value, name, props) => [`${value} views (${props.payload.percentage}%)`, 'Views']}
                  />
                  <Bar dataKey="views" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Globe className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No geographic data available yet</p>
                  <p className="text-xs mt-1">Data will appear as viewers watch videos</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Country Table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="h-5 w-5 text-cyan-400" />
              Country Details
            </h3>
            <p className="text-xs text-gray-500 mt-1">Complete breakdown with percentages</p>
          </div>
          <div className="max-h-[350px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50 sticky top-0">
                <tr>
                  <th className="text-left text-xs text-gray-400 p-3">Country</th>
                  <th className="text-right text-xs text-gray-400 p-3">Views</th>
                  <th className="text-right text-xs text-gray-400 p-3">%</th>
                </tr>
              </thead>
              <tbody>
                {countries.length > 0 ? countries.map((country, idx) => (
                  <tr key={country.country} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getCountryFlag(country.code)}</span>
                        <span className="text-sm text-gray-300">{country.country}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-sm text-white font-medium">{country.views.toLocaleString()}</td>
                    <td className="p-3 text-right text-sm text-blue-400">{country.percentage}%</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={3} className="p-8 text-center text-gray-500">No country data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* City Data */}
      {cities.length > 0 && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="h-5 w-5 text-green-400" />
              Top Cities
            </h3>
            <p className="text-xs text-gray-500 mt-1">Major metropolitan areas</p>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {cities.slice(0, 10).map((city, idx) => (
              <div key={city.city} className="bg-gray-800/50 rounded-lg p-3">
                <p className="text-sm text-gray-300 truncate">{city.city}</p>
                <p className="text-lg font-bold text-white">{city.views}</p>
                <p className="text-xs text-gray-500">views</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Device, Browser, OS Row - Clean Design with Legends */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Device Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Monitor className="h-5 w-5 text-purple-400" />
              Device Types
            </h3>
          </div>
          <div className="p-4">
            {devicePieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <div style={{ width: '120px', height: '120px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={devicePieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={55}
                        dataKey="value"
                        stroke="none"
                      >
                        {devicePieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {devicePieData.map((item, idx) => {
                    const total = devicePieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300 capitalize">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium text-white">{item.value}</span>
                          <span className="text-xs text-gray-500 ml-2">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">No device data</div>
            )}
          </div>
        </div>

        {/* Browser Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-400" />
              Browsers
            </h3>
          </div>
          <div className="p-4">
            {browserPieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <div style={{ width: '120px', height: '120px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={browserPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={55}
                        dataKey="value"
                        stroke="none"
                      >
                        {browserPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {browserPieData.map((item, idx) => {
                    const total = browserPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium text-white">{item.value}</span>
                          <span className="text-xs text-gray-500 ml-2">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">No browser data</div>
            )}
          </div>
        </div>

        {/* OS Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Monitor className="h-5 w-5 text-green-400" />
              Operating Systems
            </h3>
          </div>
          <div className="p-4">
            {osPieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <div style={{ width: '120px', height: '120px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={osPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={55}
                        dataKey="value"
                        stroke="none"
                      >
                        {osPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {osPieData.map((item, idx) => {
                    const total = osPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-medium text-white">{item.value}</span>
                          <span className="text-xs text-gray-500 ml-2">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">No OS data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get country flag emoji
const getCountryFlag = (code) => {
  if (!code || code === 'XX') return '🌍';
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Traffic Sources Section Component
const TrafficSourcesSection = ({ data }) => {
  const traffic = data?.traffic_detailed || {};
  const trafficSources = data?.traffic_sources || [];
  const categories = traffic.categories || [];
  const socialBreakdown = traffic.social_breakdown || [];
  const searchBreakdown = traffic.search_breakdown || [];
  const referralBreakdown = traffic.referral_breakdown || [];
  const campaigns = traffic.campaigns || {};
  const summary = traffic.summary || {};
  
  const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];
  
  // Category icons and colors
  const categoryConfig = {
    direct: { icon: Globe, color: '#10b981', label: 'Direct' },
    social: { icon: Share2, color: '#3b82f6', label: 'Social Media' },
    search: { icon: Search, color: '#f59e0b', label: 'Search Engines' },
    referral: { icon: Link2, color: '#8b5cf6', label: 'Referral Sites' },
    campaign: { icon: Target, color: '#ef4444', label: 'Campaigns' },
    email: { icon: Mail, color: '#ec4899', label: 'Email' },
    paid: { icon: DollarSign, color: '#06b6d4', label: 'Paid Ads' }
  };
  
  // Social platform icons
  const socialIcons = {
    'Facebook': Facebook,
    'Instagram': Instagram,
    'Twitter': Twitter,
    'YouTube': Youtube,
    'TikTok': Play,
    'LinkedIn': Link2,
    'Reddit': Globe,
    'WhatsApp': Share2
  };
  
  // Prepare pie chart data for categories
  const categoryPieData = categories.map((cat, i) => ({
    name: categoryConfig[cat.category]?.label || cat.label,
    value: cat.views,
    color: categoryConfig[cat.category]?.color || CHART_COLORS[i % CHART_COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Direct</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.direct_traffic || 0}</p>
          <p className="text-xs text-green-300 mt-1">Type URL directly</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Social</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.social_traffic || 0}</p>
          <p className="text-xs text-blue-300 mt-1">From social media</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Search className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Search</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.search_traffic || 0}</p>
          <p className="text-xs text-yellow-300 mt-1">Organic search</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Referral</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.referral_traffic || 0}</p>
          <p className="text-xs text-purple-300 mt-1">Other websites</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-5 w-5 text-red-400" />
            <span className="text-gray-300 text-xs">Campaigns</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.campaign_traffic || 0}</p>
          <p className="text-xs text-red-300 mt-1">UTM tracked</p>
        </div>
        
        <div className="bg-gradient-to-br from-pink-600/30 to-fuchsia-600/20 rounded-xl p-4 border border-pink-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-5 w-5 text-pink-400" />
            <span className="text-gray-300 text-xs">Email</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.email_traffic || 0}</p>
          <p className="text-xs text-pink-300 mt-1">Newsletter & email</p>
        </div>
      </div>

      {/* Main Traffic Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Categories - Clean Chart with Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <PieChart className="h-5 w-5 text-green-400" />
              Traffic Distribution
            </h3>
          </div>
          <div className="p-6">
            {categoryPieData.length > 0 ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={categoryPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {categoryPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {categoryPieData.map((item, idx) => {
                    const total = categoryPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{item.value}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Link2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No traffic data available yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Traffic Categories - Horizontal Bar Chart */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              Source Breakdown
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {categories.length > 0 ? categories.map((cat, idx) => {
              const config = categoryConfig[cat.category] || {};
              const IconComponent = config.icon || Globe;
              return (
                <div key={cat.category}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4" style={{ color: config.color || '#9ca3af' }} />
                      <span className="text-sm text-gray-300">{config.label || cat.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-white">{cat.views}</span>
                      <span className="text-xs text-gray-400">({cat.percentage}%)</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all" 
                      style={{ width: `${Math.max(cat.percentage, 2)}%`, backgroundColor: config.color || CHART_COLORS[idx] }}
                    />
                  </div>
                </div>
              );
            }) : (
              <div className="text-center text-gray-500 py-8">No traffic data</div>
            )}
          </div>
        </div>
      </div>

      {/* Social Media & Search Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Social Media Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Share2 className="h-5 w-5 text-blue-400" />
              Social Media Traffic
            </h3>
          </div>
          <div className="p-4">
            {socialBreakdown.length > 0 ? (
              <div className="space-y-4">
                {socialBreakdown.map((social, idx) => {
                  const IconComponent = socialIcons[social.platform] || Share2;
                  const total = socialBreakdown.reduce((a, b) => a + b.views, 0);
                  const pct = total > 0 ? ((social.views / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={social.platform}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-4 w-4 text-blue-400" />
                          <span className="text-sm text-gray-300">{social.platform}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{social.views}</span>
                          <span className="text-xs text-gray-400">({pct}%)</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all" 
                          style={{ width: `${Math.max(parseFloat(pct), 5)}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Share2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No social media traffic yet</p>
                <p className="text-xs mt-1">Share your content on social platforms!</p>
              </div>
            )}
          </div>
        </div>

        {/* Search Engine Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Search className="h-5 w-5 text-yellow-400" />
              Search Engine Traffic
            </h3>
          </div>
          <div className="p-4">
            {searchBreakdown.length > 0 ? (
              <div className="space-y-4">
                {searchBreakdown.map((search, idx) => {
                  const total = searchBreakdown.reduce((a, b) => a + b.views, 0);
                  const pct = total > 0 ? ((search.views / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={search.engine}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4 text-yellow-400" />
                          <span className="text-sm text-gray-300">{search.engine}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{search.views}</span>
                          <span className="text-xs text-gray-400">({pct}%)</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all" 
                          style={{ width: `${Math.max(parseFloat(pct), 5)}%`, backgroundColor: '#f59e0b' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Search className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No search traffic yet</p>
                <p className="text-xs mt-1">Optimize your SEO!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Referral Sites */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Link2 className="h-5 w-5 text-purple-400" />
            Top Referral Sites
          </h3>
          <p className="text-xs text-gray-500 mt-1">Websites sending you traffic</p>
        </div>
        <div className="p-4">
          {referralBreakdown.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {referralBreakdown.map((ref, idx) => (
                <div key={ref.domain} className="bg-gray-800/50 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <ExternalLink className="h-4 w-4 text-purple-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300 truncate">{ref.domain}</span>
                  </div>
                  <span className="text-sm text-purple-400 font-medium ml-2">{ref.views}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Link2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No referral traffic yet</p>
              <p className="text-xs mt-1">Get other sites to link to your content!</p>
            </div>
          )}
        </div>
      </div>

      {/* Campaign Tracking (UTM) */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Target className="h-5 w-5 text-red-400" />
            Campaign Tracking (UTM)
          </h3>
          <p className="text-xs text-gray-500 mt-1">Marketing campaign performance</p>
        </div>
        <div className="p-4">
          {campaigns.total_campaign_views > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* By Campaign Name */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">By Campaign</h4>
                <div className="space-y-2">
                  {(campaigns.by_campaign || []).slice(0, 5).map((c, idx) => (
                    <div key={c.campaign} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-2">
                      <span className="text-sm text-gray-300 truncate">{c.campaign}</span>
                      <span className="text-sm text-red-400 font-medium">{c.views}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* By Source */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">By Source</h4>
                <div className="space-y-2">
                  {(campaigns.by_source || []).slice(0, 5).map((s, idx) => (
                    <div key={s.source} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-2">
                      <span className="text-sm text-gray-300 truncate">{s.source}</span>
                      <span className="text-sm text-blue-400 font-medium">{s.views}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* By Medium */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">By Medium</h4>
                <div className="space-y-2">
                  {(campaigns.by_medium || []).slice(0, 5).map((m, idx) => (
                    <div key={m.medium} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-2">
                      <span className="text-sm text-gray-300 truncate">{m.medium}</span>
                      <span className="text-sm text-green-400 font-medium">{m.views}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Target className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No campaign data yet</p>
              <p className="text-xs mt-1 max-w-md mx-auto">
                Add UTM parameters to your links: <br />
                <code className="text-xs bg-gray-800 px-2 py-1 rounded mt-2 inline-block">
                  ?utm_source=facebook&utm_medium=social&utm_campaign=spring_2026
                </code>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* All Referrers (Raw Data) */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Globe className="h-5 w-5 text-gray-400" />
            All Traffic Sources (Raw)
          </h3>
          <p className="text-xs text-gray-500 mt-1">Complete referrer data</p>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50 sticky top-0">
              <tr>
                <th className="text-left text-xs text-gray-400 p-3">Source</th>
                <th className="text-right text-xs text-gray-400 p-3">Views</th>
              </tr>
            </thead>
            <tbody>
              {trafficSources.length > 0 ? trafficSources.map((source, idx) => (
                <tr key={source.source} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="p-3 text-sm text-gray-300">{source.source || 'Direct'}</td>
                  <td className="p-3 text-right text-sm text-white font-medium">{source.views}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={2} className="p-8 text-center text-gray-500">No traffic data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Real-Time Section - Live viewer count, trending content, live traffic sources
const RealTimeSection = ({ data }) => {
  const realtimeData = data?.realtime || {};
  const liveViewers = realtimeData.live_viewers || {};
  const liveVideos = realtimeData.live_videos || [];
  const trendingNow = realtimeData.trending_now || [];
  const liveTraffic = realtimeData.live_traffic || {};
  const activityFeed = realtimeData.activity_feed || [];
  const hourlyTrend = realtimeData.hourly_trend || [];
  const summary = realtimeData.summary || {};
  
  const CHART_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  // Prepare traffic breakdown for donut chart
  const trafficPieData = (liveTraffic.breakdown || []).map((item, i) => ({
    name: item.source,
    value: item.count,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Live Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30 relative overflow-hidden">
          <div className="absolute top-2 right-2">
            <span className="flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-5 w-5 text-red-400" />
            <span className="text-gray-300 text-xs">LIVE NOW</span>
          </div>
          <p className="text-3xl font-bold text-white">{liveViewers.total_watching || 0}</p>
          <p className="text-xs text-red-300 mt-1">Watching now</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Play className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Playing</span>
          </div>
          <p className="text-2xl font-bold text-white">{liveViewers.playing_now || 0}</p>
          <p className="text-xs text-green-300 mt-1">Active playback</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-amber-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Pause className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Paused</span>
          </div>
          <p className="text-2xl font-bold text-white">{liveViewers.paused_now || 0}</p>
          <p className="text-xs text-yellow-300 mt-1">Video paused</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Last Hour</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.views_last_hour || 0}</p>
          <p className="text-xs text-blue-300 mt-1">New views</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-violet-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Velocity</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.velocity_per_min || 0}</p>
          <p className="text-xs text-purple-300 mt-1">Views/min</p>
        </div>
        
        <div className="bg-gradient-to-br from-cyan-600/30 to-teal-600/20 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Film className="h-5 w-5 text-cyan-400" />
            <span className="text-gray-300 text-xs">Active Videos</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.active_videos || 0}</p>
          <p className="text-xs text-cyan-300 mt-1">Being watched</p>
        </div>
      </div>

      {/* Live Videos & Trending Now */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Videos Being Watched Right Now */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h3 className="font-bold flex items-center gap-2">
                <Eye className="h-5 w-5 text-red-400" />
                Live Viewers by Video
              </h3>
              <p className="text-xs text-gray-500 mt-1">Videos being watched right now</p>
            </div>
            <span className="flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {liveVideos.length > 0 ? (
              <div className="space-y-3">
                {liveVideos.map((video, idx) => (
                  <div key={video.video_id} className="flex items-center gap-3 bg-gray-800/50 rounded-lg p-2">
                    <div className="relative">
                      <div className="w-16 h-10 rounded bg-gray-800 overflow-hidden">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="h-4 w-4 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] px-1 rounded font-bold">
                        LIVE
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">
                        {video.playing_now} playing • {video.paused_now} paused
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-bold text-red-400">{video.watching_now}</p>
                      <p className="text-xs text-gray-500">viewers</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Eye className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No live viewers right now</p>
                  <p className="text-xs mt-1">Viewers appear when watching videos</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trending Now (Last Hour) */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-400" />
              Trending Now
            </h3>
            <p className="text-xs text-gray-500 mt-1">Most views in the last hour</p>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {trendingNow.length > 0 ? (
              <div className="space-y-3">
                {trendingNow.map((video, idx) => (
                  <div key={video.video_id} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-orange-500 text-white' :
                      idx === 1 ? 'bg-orange-400 text-white' :
                      idx === 2 ? 'bg-orange-300 text-orange-900' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-3 w-3 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.watch_time_last_hour}m watched</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-orange-400">{video.views_last_hour}</p>
                      <p className="text-xs text-gray-500">views/hr</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No trending videos yet</p>
                  <p className="text-xs mt-1">Shows videos with most views in last hour</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Traffic Sources & Hourly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Traffic Sources - Donut + Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Link2 className="h-5 w-5 text-green-400" />
              Live Traffic Sources
            </h3>
            <p className="text-xs text-gray-500 mt-1">Where current visitors are coming from (last hour)</p>
          </div>
          <div className="p-6">
            {trafficPieData.length > 0 && trafficPieData.some(d => d.value > 0) ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={trafficPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {trafficPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {trafficPieData.map((item, idx) => {
                    const total = trafficPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{item.value}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Link2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No traffic data yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hourly Trend Chart */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-400" />
              Hourly Trend
            </h3>
            <p className="text-xs text-gray-500 mt-1">Views over the last 6 hours</p>
          </div>
          <div className="p-4">
            {hourlyTrend.length > 0 ? (
              <div className="space-y-4">
                {hourlyTrend.map((hour, idx) => {
                  const maxViews = Math.max(...hourlyTrend.map(h => h.views), 1);
                  const widthPct = (hour.views / maxViews) * 100;
                  return (
                    <div key={hour.hour}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-400">{hour.hour}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-white">{hour.views} views</span>
                          <span className="text-xs text-gray-500">{hour.watch_minutes}m watched</span>
                        </div>
                      </div>
                      <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all" 
                          style={{ width: `${Math.max(widthPct, 3)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Activity className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No hourly data yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Activity Feed */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 className="font-bold flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-400" />
              Live Activity Feed
            </h3>
            <p className="text-xs text-gray-500 mt-1">Recent viewer activity</p>
          </div>
          <span className="text-xs text-gray-500">Last 15 events</span>
        </div>
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {activityFeed.length > 0 ? (
            <div className="space-y-3">
              {activityFeed.map((activity, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-gray-800/30 rounded-lg p-3 border border-gray-800/50">
                  <div className="w-10 h-10 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                    {activity.thumbnail_url ? (
                      <img src={activity.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="h-4 w-4 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{activity.video_title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {Math.round(activity.watch_duration / 60)}m watched
                      </span>
                      <span className="text-xs text-gray-600">•</span>
                      <span className="text-xs text-gray-500">
                        {activity.completion_rate}% completed
                      </span>
                      {activity.country && (
                        <>
                          <span className="text-xs text-gray-600">•</span>
                          <span className="text-xs text-gray-500">{activity.country}</span>
                        </>
                      )}
                      {activity.device_type && (
                        <>
                          <span className="text-xs text-gray-600">•</span>
                          <span className="text-xs text-gray-500">{activity.device_type}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-purple-400 font-medium">{activity.time_ago}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Activity className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Revenue & Conversion (Coming Soon) */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-400" />
            Revenue & Conversion
            <span className="ml-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">Coming Soon</span>
          </h3>
          <p className="text-xs text-gray-500 mt-1">Track revenue metrics and conversion rates</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 opacity-50">
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <DollarSign className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-500">$0.00</p>
              <p className="text-xs text-gray-600">Revenue Today</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <CreditCard className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-500">0</p>
              <p className="text-xs text-gray-600">Subscriptions</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <Target className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-500">0%</p>
              <p className="text-xs text-gray-600">Conversion Rate</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 text-center">
              <TrendingUp className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-500">$0.00</p>
              <p className="text-xs text-gray-600">Avg Revenue/User</p>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-4">
            Revenue tracking will be available once Stripe integration is enabled for subscriptions.
          </p>
        </div>
      </div>
    </div>
  );
};

// Content Performance Section - Shows top videos, category analysis, completion rates, drop-off points
const ContentPerformanceSection = ({ data }) => {
  const contentData = data?.content_performance || {};
  const topVideos = contentData.top_videos || [];
  const topByWatchTime = contentData.top_by_watch_time || [];
  const topByCompletion = contentData.top_by_completion || [];
  const categoryPerformance = contentData.category_performance || [];
  const completionBreakdown = contentData.completion_breakdown || [];
  const dropOffAnalysis = contentData.drop_off_analysis || {};
  const thumbnailCtr = contentData.thumbnail_ctr || [];
  const summary = contentData.summary || {};
  
  const CHART_COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];
  
  // Prepare pie chart data for completion breakdown
  const completionPieData = completionBreakdown.map((item, i) => ({
    name: item.range,
    value: item.count,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));
  
  // Prepare drop-off chart data
  const dropOffData = (dropOffAnalysis.breakdown || []).map((item, i) => ({
    name: item.range,
    value: item.count,
    percentage: item.percentage,
    color: i < 2 ? '#ef4444' : i < 4 ? '#f59e0b' : '#10b981' // Red for early, yellow for mid, green for late
  }));

  const formatWatchTime = (seconds) => {
    if (!seconds) return '0s';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-orange-600/30 to-amber-600/20 rounded-xl p-4 border border-orange-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="h-5 w-5 text-orange-400" />
            <span className="text-gray-300 text-xs">Total Views</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_views?.toLocaleString() || 0}</p>
          <p className="text-xs text-orange-300 mt-1">{summary.videos_analyzed || 0} videos</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Watch Time</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_watch_time_hours || 0}h</p>
          <p className="text-xs text-blue-300 mt-1">Total hours</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Completion Rate</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.overall_completion_rate || 0}%</p>
          <p className="text-xs text-green-300 mt-1">Finished video</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Avg Completion</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.avg_completion || 0}%</p>
          <p className="text-xs text-purple-300 mt-1">Average watch %</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5 text-red-400" />
            <span className="text-gray-300 text-xs">Drop-off Point</span>
          </div>
          <p className="text-2xl font-bold text-white">{dropOffAnalysis.most_common_drop_off || 'N/A'}</p>
          <p className="text-xs text-red-300 mt-1">Most common exit</p>
        </div>
        
        <div className="bg-gradient-to-br from-cyan-600/30 to-teal-600/20 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="h-5 w-5 text-cyan-400" />
            <span className="text-gray-300 text-xs">Categories</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.categories_count || 0}</p>
          <p className="text-xs text-cyan-300 mt-1">Active categories</p>
        </div>
      </div>

      {/* Top Videos Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Videos by Views - Chart + Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-400" />
              Top Videos by Views
            </h3>
            <p className="text-xs text-gray-500 mt-1">Ranked by total view count</p>
          </div>
          <div className="p-4 max-h-[400px] overflow-y-auto">
            {topVideos.length > 0 ? (
              <div className="space-y-3">
                {topVideos.slice(0, 10).map((video, idx) => (
                  <div key={video.video_id} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-3 w-3 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.avg_completion}% avg completion</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-orange-400">{video.views?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Film className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No video data available yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top Videos by Watch Time */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-400" />
              Top Videos by Watch Time
            </h3>
            <p className="text-xs text-gray-500 mt-1">Ranked by total watch duration</p>
          </div>
          <div className="p-4 max-h-[400px] overflow-y-auto">
            {topByWatchTime.length > 0 ? (
              <div className="space-y-3">
                {topByWatchTime.map((video, idx) => (
                  <div key={video.video_id} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-blue-500 text-white' :
                      idx === 1 ? 'bg-blue-400 text-white' :
                      idx === 2 ? 'bg-blue-300 text-blue-900' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-3 w-3 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">{video.views} views</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-blue-400">{formatWatchTime(video.total_watch_time)}</p>
                      <p className="text-xs text-gray-500">watched</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No watch time data yet</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Analysis & Completion Rates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Performance */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Layers className="h-5 w-5 text-purple-400" />
              Category Performance
            </h3>
            <p className="text-xs text-gray-500 mt-1">Which topics perform best</p>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {categoryPerformance.length > 0 ? (
              <div className="space-y-4">
                {categoryPerformance.map((cat, idx) => {
                  const maxViews = Math.max(...categoryPerformance.map(c => c.views));
                  const widthPct = maxViews > 0 ? (cat.views / maxViews * 100) : 0;
                  return (
                    <div key={cat.category_id}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{cat.icon || '📁'}</span>
                          <span className="text-sm text-gray-300">{cat.category_name}</span>
                          <span className="text-xs text-gray-500">({cat.video_count} videos)</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-white">{cat.views}</span>
                          <span className="text-xs text-purple-400">{cat.avg_completion}% avg</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all" 
                          style={{ width: `${Math.max(widthPct, 3)}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <Layers className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No category data</p>
              </div>
            )}
          </div>
        </div>

        {/* Completion Rate Distribution - Donut + Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <PieChart className="h-5 w-5 text-green-400" />
              Video Completion Rates
            </h3>
            <p className="text-xs text-gray-500 mt-1">Percentage who watch to completion</p>
          </div>
          <div className="p-6">
            {completionPieData.length > 0 && completionPieData.some(d => d.value > 0) ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={completionPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {completionPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {completionPieData.map((item, idx) => {
                    const total = completionPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{item.value}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No completion data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drop-off Points & Thumbnail CTR */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Drop-off Points Analysis */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              Drop-off Points
            </h3>
            <p className="text-xs text-gray-500 mt-1">Where viewers typically leave</p>
          </div>
          <div className="p-4">
            {dropOffData.length > 0 && dropOffData.some(d => d.value > 0) ? (
              <div className="space-y-4">
                {dropOffData.map((item, idx) => (
                  <div key={item.name}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-300">{item.name} of video</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white">{item.value}</span>
                        <span className="text-xs text-gray-400">({item.percentage}%)</span>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all" 
                        style={{ width: `${Math.max(item.percentage, 3)}%`, backgroundColor: item.color }}
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-300">
                    <strong>Insight:</strong> Most viewers drop off at <span className="text-white font-bold">{dropOffAnalysis.most_common_drop_off || 'N/A'}</span> of the video.
                    Consider improving content engagement during this period.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <XCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No drop-off data yet</p>
                <p className="text-xs mt-1">This tracks incomplete video views</p>
              </div>
            )}
          </div>
        </div>

        {/* Thumbnail Click-Through Rates */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-cyan-400" />
              Thumbnail Click-Through Rates
            </h3>
            <p className="text-xs text-gray-500 mt-1">Which thumbnails get clicked most</p>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {thumbnailCtr.length > 0 && thumbnailCtr.some(t => t.impressions > 0) ? (
              <div className="space-y-3">
                {thumbnailCtr.filter(t => t.impressions > 0).slice(0, 10).map((video, idx) => (
                  <div key={video.video_id} className="flex items-center gap-3 bg-gray-800/50 rounded-lg p-2">
                    <div className="w-16 h-10 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      {video.thumbnail_url ? (
                        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{video.title}</p>
                      <p className="text-xs text-gray-500">
                        {video.impressions} shown • {video.clicks} clicked
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                      video.ctr >= 10 ? 'bg-green-500/20 text-green-400' :
                      video.ctr >= 5 ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {video.ctr}% CTR
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <ImageIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No impression data yet</p>
                <p className="text-xs mt-1">CTR requires tracking video impressions</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Videos by Completion Rate */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-400" />
            Top Videos by Engagement
          </h3>
          <p className="text-xs text-gray-500 mt-1">Videos with highest completion rates</p>
        </div>
        <div className="p-4">
          {topByCompletion.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {topByCompletion.map((video, idx) => (
                <div key={video.video_id} className="bg-gray-800/50 rounded-lg p-3 flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    idx === 0 ? 'bg-green-500 text-white' :
                    idx === 1 ? 'bg-green-400 text-white' :
                    idx === 2 ? 'bg-green-300 text-green-900' :
                    'bg-gray-700 text-gray-300'
                  }`}>
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{video.title}</p>
                    <p className="text-xs text-gray-500">{video.views} views</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-green-400">{video.completion_rate}%</p>
                    <p className="text-xs text-gray-500">completion</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No completion data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// User Behavior Section - Session duration, pages per session, user journeys, retention, cohorts
const UserBehaviorSection = ({ data }) => {
  const behaviorData = data?.user_behavior || {};
  const sessionDuration = behaviorData.session_duration || {};
  const pagesPerSession = behaviorData.pages_per_session || {};
  const userJourney = behaviorData.user_journey || {};
  const retention = behaviorData.retention || {};
  const cohortAnalysis = behaviorData.cohort_analysis || [];
  const summary = behaviorData.summary || {};
  
  const CHART_COLORS = ['#ec4899', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'];
  
  // Prepare pie chart data for session duration
  const sessionDurationPieData = (sessionDuration.breakdown || []).map((item, i) => ({
    name: item.range,
    value: item.count,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));
  
  // Prepare pie chart data for pages per session
  const pagesPerSessionPieData = (pagesPerSession.breakdown || []).map((item, i) => ({
    name: item.range,
    value: item.count,
    color: CHART_COLORS[i % CHART_COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-pink-600/30 to-rose-600/20 rounded-xl p-4 border border-pink-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-pink-400" />
            <span className="text-gray-300 text-xs">Unique Users</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_unique_users?.toLocaleString() || 0}</p>
          <p className="text-xs text-pink-300 mt-1">Total visitors</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-violet-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Total Sessions</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_sessions?.toLocaleString() || 0}</p>
          <p className="text-xs text-purple-300 mt-1">Viewing sessions</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Avg Session</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.avg_session_duration_formatted || '0s'}</p>
          <p className="text-xs text-blue-300 mt-1">Time on platform</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Film className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Videos/Session</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.avg_pages_per_session || 0}</p>
          <p className="text-xs text-green-300 mt-1">Average watched</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-amber-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Repeat className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Return Rate</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.returning_user_rate || 0}%</p>
          <p className="text-xs text-yellow-300 mt-1">Came back</p>
        </div>
        
        <div className="bg-gradient-to-br from-cyan-600/30 to-teal-600/20 rounded-xl p-4 border border-cyan-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-cyan-400" />
            <span className="text-gray-300 text-xs">New This Week</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.new_users_this_week || 0}</p>
          <p className="text-xs text-cyan-300 mt-1">New visitors</p>
        </div>
      </div>

      {/* Session Duration & Pages Per Session */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Duration - Donut + Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Clock className="h-5 w-5 text-pink-400" />
              Session Duration
            </h3>
            <p className="text-xs text-gray-500 mt-1">How long users stay on your platform</p>
          </div>
          <div className="p-6">
            {sessionDurationPieData.length > 0 && sessionDurationPieData.some(d => d.value > 0) ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={sessionDurationPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {sessionDurationPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {sessionDurationPieData.map((item, idx) => {
                    const total = sessionDurationPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{item.value}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No session data available yet</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pages Per Session - Donut + Legend */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Film className="h-5 w-5 text-purple-400" />
              Videos Per Session
            </h3>
            <p className="text-xs text-gray-500 mt-1">How many videos watched per visit</p>
          </div>
          <div className="p-6">
            {pagesPerSessionPieData.length > 0 && pagesPerSessionPieData.some(d => d.value > 0) ? (
              <div className="flex items-center gap-6">
                <div style={{ width: '150px', height: '150px', flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={pagesPerSessionPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        dataKey="value"
                        stroke="none"
                      >
                        {pagesPerSessionPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {pagesPerSessionPieData.map((item, idx) => {
                    const total = pagesPerSessionPieData.reduce((a, b) => a + b.value, 0);
                    const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }} />
                          <span className="text-sm text-gray-300">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm font-bold text-white">{item.value}</span>
                          <span className="text-sm text-gray-400 w-16 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                <div className="text-center">
                  <Film className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No video watch data yet</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Journey & Entry Points */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entry Points */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-green-400" />
              Entry Points
            </h3>
            <p className="text-xs text-gray-500 mt-1">First video users watch when they arrive</p>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {(userJourney.entry_points || []).length > 0 ? (
              <div className="space-y-3">
                {(userJourney.entry_points || []).map((entry, idx) => (
                  <div key={entry.video_id} className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-green-500 text-white' :
                      idx === 1 ? 'bg-green-400 text-white' :
                      idx === 2 ? 'bg-green-300 text-green-900' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                      {entry.thumbnail_url ? (
                        <img src={entry.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="h-3 w-3 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{entry.title}</p>
                      <p className="text-xs text-gray-500">{entry.percentage}% of users start here</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-green-400">{entry.count}</p>
                      <p className="text-xs text-gray-500">users</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <ArrowUpRight className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No entry point data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* User Journey Patterns */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <History className="h-5 w-5 text-blue-400" />
              Common User Journeys
            </h3>
            <p className="text-xs text-gray-500 mt-1">Most common paths through your site</p>
          </div>
          <div className="p-4 max-h-[350px] overflow-y-auto">
            {(userJourney.top_patterns || []).length > 0 ? (
              <div className="space-y-3">
                {(userJourney.top_patterns || []).map((pattern, idx) => (
                  <div key={idx} className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">Pattern #{idx + 1}</span>
                      <span className="text-sm font-bold text-blue-400">{pattern.count} users</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {pattern.pattern.split(' → ').map((step, i, arr) => (
                        <React.Fragment key={i}>
                          <span className="px-2 py-1 bg-gray-700 rounded text-xs text-white truncate max-w-[150px]">
                            {step.slice(0, 20)}...
                          </span>
                          {i < arr.length - 1 && (
                            <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No journey patterns yet</p>
                <p className="text-xs mt-1">Needs users watching multiple videos</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Retention Curve & New vs Returning */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Retention Curve */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-yellow-400" />
              Retention Curve
            </h3>
            <p className="text-xs text-gray-500 mt-1">User return rates over time</p>
          </div>
          <div className="p-4">
            {(retention.curve || []).length > 0 ? (
              <div className="space-y-4">
                {(retention.curve || []).map((point, idx) => {
                  const color = point.retention_rate >= 50 ? '#10b981' : 
                                point.retention_rate >= 25 ? '#f59e0b' : '#ef4444';
                  return (
                    <div key={point.days}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-300">{point.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{point.retention_rate}%</span>
                          <span className="text-xs text-gray-400">({point.retained}/{point.eligible})</span>
                        </div>
                      </div>
                      <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all" 
                          style={{ width: `${Math.max(point.retention_rate, 3)}%`, backgroundColor: color }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                  <p className="text-sm text-yellow-300">
                    <strong>Insight:</strong> {retention.returning_user_rate || 0}% of users return for multiple sessions. 
                    {retention.returning_user_rate >= 30 ? ' Great retention!' : ' Consider strategies to bring users back.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <TrendingUp className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No retention data yet</p>
                <p className="text-xs mt-1">Needs users returning over time</p>
              </div>
            )}
          </div>
        </div>

        {/* New vs Returning Users */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <Users className="h-5 w-5 text-cyan-400" />
              New vs Returning Users
            </h3>
            <p className="text-xs text-gray-500 mt-1">User behavior patterns</p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-cyan-600/20 rounded-lg p-4 border border-cyan-500/30 text-center">
                <Sparkles className="h-8 w-8 text-cyan-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{retention.new_users_this_week || 0}</p>
                <p className="text-xs text-cyan-300">New This Week</p>
              </div>
              <div className="bg-yellow-600/20 rounded-lg p-4 border border-yellow-500/30 text-center">
                <Repeat className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{retention.returning_users || 0}</p>
                <p className="text-xs text-yellow-300">Returning Users</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Return Rate</span>
                <span className="text-sm font-bold text-white">{retention.returning_user_rate || 0}%</span>
              </div>
              <div className="h-4 bg-gray-800 rounded-full overflow-hidden flex">
                <div 
                  className="h-full bg-cyan-500" 
                  style={{ width: `${100 - (retention.returning_user_rate || 0)}%` }}
                  title="New Users"
                />
                <div 
                  className="h-full bg-yellow-500" 
                  style={{ width: `${retention.returning_user_rate || 0}%` }}
                  title="Returning Users"
                />
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-cyan-500" /> New Users
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded bg-yellow-500" /> Returning
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cohort Analysis */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Grid className="h-5 w-5 text-purple-400" />
            Cohort Analysis
          </h3>
          <p className="text-xs text-gray-500 mt-1">User behavior grouped by signup week (retention %)</p>
        </div>
        <div className="p-4 overflow-x-auto">
          {cohortAnalysis.length > 0 ? (
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-3 text-gray-400 text-sm">Cohort</th>
                  <th className="text-center p-3 text-gray-400 text-sm">Users</th>
                  <th className="text-center p-3 text-gray-400 text-sm">Week 1</th>
                  <th className="text-center p-3 text-gray-400 text-sm">Week 2</th>
                  <th className="text-center p-3 text-gray-400 text-sm">Week 3</th>
                  <th className="text-center p-3 text-gray-400 text-sm">Week 4</th>
                </tr>
              </thead>
              <tbody>
                {cohortAnalysis.map((cohort, idx) => (
                  <tr key={cohort.cohort} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-3 text-sm text-gray-300">{cohort.cohort}</td>
                    <td className="p-3 text-center text-sm font-medium text-white">{cohort.users}</td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 rounded text-xs font-bold bg-purple-500 text-white">
                        100%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        cohort.week2 >= 50 ? 'bg-green-500 text-white' :
                        cohort.week2 >= 25 ? 'bg-yellow-500 text-black' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {cohort.week2}%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        cohort.week3 >= 50 ? 'bg-green-500 text-white' :
                        cohort.week3 >= 25 ? 'bg-yellow-500 text-black' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {cohort.week3}%
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        cohort.week4 >= 50 ? 'bg-green-500 text-white' :
                        cohort.week4 >= 25 ? 'bg-yellow-500 text-black' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {cohort.week4}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <Grid className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No cohort data yet</p>
              <p className="text-xs mt-1">Requires users over multiple weeks</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Analytics Detail Modal
const VideoAnalyticsModal = ({ video, details, loading, onClose, formatDuration }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden">
              {video.thumbnail_url ? (
                <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="h-4 w-4 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <h2 className="font-bold">{video.title}</h2>
              <p className="text-xs text-gray-500">Video Analytics</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : details ? (
          <div className="p-6 space-y-6">
            {/* Data Note (if tracking differs from total) */}
            {details.data_note && (
              <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-300">
                ℹ️ {details.data_note}
              </div>
            )}
            
            {/* Summary Stats - Row 1 */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{details.total_views?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Views</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{details.unique_viewers}</p>
                <p className="text-xs text-gray-500">Unique Viewers</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">{details.views_last_7_days || 0}</p>
                <p className="text-xs text-gray-500">Views (7 days)</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{details.views_last_30_days || 0}</p>
                <p className="text-xs text-gray-500">Views (30 days)</p>
              </div>
            </div>

            {/* Summary Stats - Row 2 */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{formatDuration(details.avg_watch_time_seconds)}</p>
                <p className="text-xs text-gray-500">Avg Watch Time</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{formatDuration(details.total_watch_time_seconds || 0)}</p>
                <p className="text-xs text-gray-500">Total Watch Time</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className={`text-2xl font-bold ${
                  details.avg_completion_rate >= 75 ? 'text-green-400' :
                  details.avg_completion_rate >= 50 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {details.avg_completion_rate}%
                </p>
                <p className="text-xs text-gray-500">Completion Rate</p>
              </div>
            </div>

            {/* Daily Views Chart */}
            {details.daily_views && details.daily_views.length > 0 && (
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                  Daily Views (Last 30 Days)
                </h3>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-end gap-1 h-24">
                    {details.daily_views.map((day, idx) => {
                      const maxViews = Math.max(...details.daily_views.map(d => d.views), 1);
                      const height = (day.views / maxViews) * 100;
                      const isToday = idx === details.daily_views.length - 1;
                      return (
                        <div 
                          key={day.date} 
                          className="flex-1 flex flex-col items-center group relative"
                          title={`${day.date}: ${day.views} views`}
                        >
                          <div 
                            className={`w-full rounded-t transition-all ${
                              isToday ? 'bg-green-500' : day.views > 0 ? 'bg-purple-500' : 'bg-gray-700'
                            }`}
                            style={{ height: `${height}%`, minHeight: day.views > 0 ? '4px' : '2px' }}
                          />
                          {/* Tooltip on hover */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                            {day.date.slice(5)}: {day.views}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>{details.daily_views[0]?.date.slice(5)}</span>
                    <span>{details.daily_views[Math.floor(details.daily_views.length / 2)]?.date.slice(5)}</span>
                    <span>{details.daily_views[details.daily_views.length - 1]?.date.slice(5)} (Today)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Hour Distribution */}
            <div>
              <h3 className="font-medium mb-3">Viewing by Hour</h3>
              <div className="flex items-end gap-1 h-20 bg-gray-800 rounded-lg p-3">
                {details.hour_distribution?.map((count, hour) => {
                  const maxCount = Math.max(...details.hour_distribution);
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div 
                      key={hour} 
                      className="flex-1 bg-purple-500 rounded-t"
                      style={{ height: `${height}%`, minHeight: count > 0 ? '4px' : '0' }}
                      title={`${hour}:00 - ${count} views`}
                    />
                  );
                })}
              </div>
            </div>

            {/* Traffic & Device */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-400" />
                  Traffic Sources
                </h3>
                <div className="space-y-2 bg-gray-800 rounded-lg p-4">
                  {details.traffic_sources?.slice(0, 5).map((source, idx) => {
                    const maxViews = Math.max(...details.traffic_sources.map(s => s.views), 1);
                    const percent = Math.round((source.views / details.total_views) * 100);
                    return (
                      <div key={source.source} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-4">{idx + 1}</span>
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300 truncate">{source.source}</span>
                            <span className="text-gray-400">{percent}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded"
                              style={{ width: `${(source.views / maxViews) * 100}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 w-8 text-right">{source.views}</span>
                      </div>
                    );
                  })}
                  {details.traffic_sources?.length === 0 && (
                    <p className="text-gray-500 text-sm">No data</p>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-orange-400" />
                  Devices
                </h3>
                <div className="space-y-2 bg-gray-800 rounded-lg p-4">
                  {details.device_breakdown?.map((device) => {
                    const total = details.device_breakdown.reduce((sum, d) => sum + d.views, 0);
                    const percent = total > 0 ? Math.round((device.views / total) * 100) : 0;
                    const Icon = device.device === 'mobile' ? Smartphone : 
                                device.device === 'tablet' ? Tablet : Monitor;
                    return (
                      <div key={device.device} className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-gray-400" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="capitalize">{device.device}</span>
                            <span className="text-gray-400">{percent}%</span>
                          </div>
                          <div className="h-1.5 bg-gray-700 rounded overflow-hidden">
                            <div 
                              className="h-full bg-orange-500 rounded"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 w-8 text-right">{device.views}</span>
                      </div>
                    );
                  })}
                  {details.device_breakdown?.length === 0 && (
                    <p className="text-gray-500 text-sm">No data</p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Views */}
            <div>
              <h3 className="font-medium mb-3">Recent Views</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {details.recent_views?.map((view, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-gray-800 rounded-lg p-3">
                    <div>
                      <p>{view.customer?.email || 'Anonymous'}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(view.watched_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={view.completion_rate >= 75 ? 'text-green-400' : 'text-gray-400'}>
                        {view.completion_rate}% completed
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDuration(view.watch_duration)} • {view.device || 'Unknown'}
                      </p>
                    </div>
                  </div>
                ))}
                {details.recent_views?.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No recent views</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">No data available</div>
        )}
      </div>
    </div>
  );
};

// Membership Tab
const MembershipTab = ({ settings, onRefresh }) => {
  const [form, setForm] = useState({
    basic_price: 0,
    basic_description: settings?.basic?.description || '',
    basic_is_free: settings?.basic?.is_free || false,
    premium_price: settings?.premium?.price || 2.99,
    premium_description: settings?.premium?.description || '',
    premium_is_free: settings?.premium?.is_free || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/api/vod-admin/membership/settings`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Settings saved!');
      onRefresh();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-bold mb-6">Membership Pricing</h2>
      
      <div className="space-y-8">
        {/* Premium Plan */}
        <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-6 border border-purple-500/30">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2">
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">
                <Crown className="h-3 w-3 mr-1" /> Premium
              </Badge>
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className={`text-sm ${form.premium_is_free ? 'text-green-400' : 'text-gray-400'}`}>
                {form.premium_is_free ? 'FREE' : 'Paid'}
              </span>
              <button
                type="button"
                onClick={() => setForm({ ...form, premium_is_free: !form.premium_is_free })}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  form.premium_is_free ? 'bg-green-500' : 'bg-gray-700'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                    form.premium_is_free ? 'translate-x-6' : ''
                  }`}
                />
              </button>
            </label>
          </div>
          
          {form.premium_is_free ? (
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <Check className="h-5 w-5" />
                Premium membership is FREE for all users
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Price (£/month)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.premium_price}
                  onChange={(e) => setForm({ ...form, premium_price: parseFloat(e.target.value) || 0 })}
                  className="bg-gray-800 border-gray-700"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Description</label>
                <Input
                  value={form.premium_description}
                  onChange={(e) => setForm({ ...form, premium_description: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  placeholder="Access to ALL content including premium exclusives"
                />
              </div>
            </div>
          )}
        </div>

        <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-500">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

// Subscribers Tab
const SubscribersTab = ({ subscribers, creatorSubscribers = [], onRefresh }) => {
  const [viewMode, setViewMode] = useState('all'); // 'all', 'premium', 'creator'

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-xl p-5 border border-purple-500/30">
          <p className="text-sm text-gray-400 mb-1">Premium Members</p>
          <p className="text-2xl font-bold">{subscribers.length}</p>
          <p className="text-xs text-gray-500 mt-1">Paid memberships</p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-xl p-5 border border-blue-500/30">
          <p className="text-sm text-gray-400 mb-1">Creator Followers</p>
          <p className="text-2xl font-bold">{creatorSubscribers.length}</p>
          <p className="text-xs text-gray-500 mt-1">Channel subscriptions</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-xl p-5 border border-green-500/30">
          <p className="text-sm text-gray-400 mb-1">Total Engaged</p>
          <p className="text-2xl font-bold">{subscribers.length + creatorSubscribers.length}</p>
          <p className="text-xs text-gray-500 mt-1">All subscriber types</p>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={viewMode === 'all' ? 'default' : 'outline'}
          onClick={() => setViewMode('all')}
          className={viewMode === 'all' ? 'bg-purple-600' : 'border-gray-600'}
        >
          All ({subscribers.length + creatorSubscribers.length})
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'premium' ? 'default' : 'outline'}
          onClick={() => setViewMode('premium')}
          className={viewMode === 'premium' ? 'bg-purple-600' : 'border-gray-600'}
        >
          <Crown className="h-4 w-4 mr-1" />
          Premium ({subscribers.length})
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'creator' ? 'default' : 'outline'}
          onClick={() => setViewMode('creator')}
          className={viewMode === 'creator' ? 'bg-purple-600' : 'border-gray-600'}
        >
          <Users className="h-4 w-4 mr-1" />
          Creator Followers ({creatorSubscribers.length})
        </Button>
      </div>

      {/* Premium Members Table */}
      {(viewMode === 'all' || viewMode === 'premium') && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-purple-900/20 to-pink-900/20">
            <h3 className="font-semibold flex items-center gap-2">
              <Crown className="h-4 w-4 text-purple-400" />
              Premium Members
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Customer</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Tier</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Expires</th>
                </tr>
              </thead>
              <tbody>
                {subscribers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-500">
                      No premium members yet
                    </td>
                  </tr>
                ) : (
                  subscribers.map((sub, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4">
                        <div>
                          <div className="font-medium">{sub.customer?.full_name || 'Unknown'}</div>
                          <div className="text-sm text-gray-500">{sub.customer?.email}</div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={sub.tier === 'premium' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-700'}>
                          {sub.tier === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                          {sub.tier}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge className={sub.status === 'active' ? 'bg-green-600' : 'bg-gray-600'}>
                          {sub.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-gray-400">
                        {formatDate(sub.expires_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Creator Followers Table */}
      {(viewMode === 'all' || viewMode === 'creator') && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-blue-900/20 to-cyan-900/20">
            <h3 className="font-semibold flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              Creator Channel Followers
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left p-4 text-gray-400 font-medium">Email</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Creator</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Notifications</th>
                  <th className="text-left p-4 text-gray-400 font-medium">Subscribed</th>
                </tr>
              </thead>
              <tbody>
                {creatorSubscribers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-gray-500">
                      No creator followers yet
                    </td>
                  </tr>
                ) : (
                  creatorSubscribers.map((sub, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-gray-800/50">
                      <td className="p-4">
                        <div className="font-medium">{sub.email}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {sub.creator?.profile_image_url ? (
                            <img 
                              src={sub.creator.profile_image_url} 
                              alt="" 
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center text-xs font-bold text-blue-400">
                              {sub.creator?.name?.charAt(0) || '?'}
                            </div>
                          )}
                          <span>{sub.creator?.name || 'Unknown Creator'}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          {sub.notify_new_videos && (
                            <Badge className="bg-blue-600/20 text-blue-400 text-xs">Videos</Badge>
                          )}
                          {sub.notify_premieres && (
                            <Badge className="bg-purple-600/20 text-purple-400 text-xs">Premieres</Badge>
                          )}
                          {sub.notify_posts && (
                            <Badge className="bg-pink-600/20 text-pink-400 text-xs">Posts</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-gray-400">
                        {formatDate(sub.subscribed_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// All Users Tab - Shared with LTV Live
const UsersTab = ({ users, loading, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-gray-400">Loading users...</span>
      </div>
    );
  }

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-5 border border-blue-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-400" />
            </div>
            <span className="text-gray-400 text-sm">Total Users</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">{users.length}</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-xl p-5 border border-green-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Verified</span>
          </div>
          <p className="text-3xl font-bold text-green-400">
            {users.filter(u => u.email_verified).length}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-xl p-5 border border-purple-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Admins</span>
          </div>
          <p className="text-3xl font-bold text-purple-400">
            {users.filter(u => u.is_admin).length}
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 rounded-xl p-5 border border-orange-500/30">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-orange-400" />
            </div>
            <span className="text-gray-400 text-sm">This Month</span>
          </div>
          <p className="text-3xl font-bold text-orange-400">
            {users.filter(u => {
              if (!u.created_at) return false;
              const created = new Date(u.created_at);
              const now = new Date();
              return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
      </div>

      {/* Search & Refresh */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-900 border-gray-700"
          />
        </div>
        <Button onClick={onRefresh} variant="outline" className="border-gray-700">
          Refresh
        </Button>
      </div>

      {/* Users Table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-800/30">
                <th className="text-left p-4 text-gray-400 font-medium text-sm">User</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Username</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Role</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Joined</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Last Login</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <UserCircle className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                    <p className="text-gray-500">No users found</p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, idx) => (
                  <tr key={user.id || idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {user.profile_picture ? (
                          <img 
                            src={user.profile_picture} 
                            alt="" 
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center font-bold">
                            {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-white">{user.full_name || 'No Name'}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-300">@{user.username || '-'}</p>
                    </td>
                    <td className="p-4">
                      {user.email_verified ? (
                        <Badge className="bg-green-600/20 text-green-400">
                          <CheckCircle className="h-3 w-3 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-600/20 text-gray-400">
                          <XCircle className="h-3 w-3 mr-1" /> Unverified
                        </Badge>
                      )}
                    </td>
                    <td className="p-4">
                      {user.is_admin ? (
                        <Badge className="bg-purple-600/20 text-purple-400">
                          <Shield className="h-3 w-3 mr-1" /> Admin
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-600/20 text-gray-400">User</Badge>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-400">
                        {user.created_at 
                          ? new Date(user.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          : '-'
                        }
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-400">
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'Never'
                        }
                      </p>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t border-gray-800 text-sm text-gray-500">
          Showing {filteredUsers.length} of {users.length} users • Shared with LTV Live
        </div>
      </div>
    </div>
  );
};

// Purchases Tab - VOD Financial Dashboard
const PurchasesTab = ({ purchasesData, stripeBalance, loading, processingRefund, onRefund, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="purchases-loading">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-gray-400">Loading purchases...</span>
      </div>
    );
  }

  const { financial_summary, purchases } = purchasesData || { financial_summary: {}, purchases: [] };

  return (
    <div className="space-y-6" data-testid="purchases-tab-content">
      {/* Financial Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="financial-summary-cards">
        <div className="bg-gradient-to-br from-green-600/20 to-emerald-800/20 rounded-xl p-5 border border-green-500/30" data-testid="gross-revenue-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-green-400" />
            </div>
            <span className="text-gray-400 text-sm">Gross Revenue</span>
          </div>
          <p className="text-3xl font-bold text-green-400" data-testid="gross-revenue-value">£{financial_summary.total_gross_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-gray-500 mt-1" data-testid="total-purchases-count">{financial_summary.total_purchases || 0} total purchases</p>
        </div>

        <div className="bg-gradient-to-br from-red-600/20 to-red-800/20 rounded-xl p-5 border border-red-500/30" data-testid="stripe-fees-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-red-400" />
            </div>
            <span className="text-gray-400 text-sm">Stripe Fees</span>
          </div>
          <p className="text-3xl font-bold text-red-400" data-testid="stripe-fees-value">£{financial_summary.total_stripe_fees?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-gray-500 mt-1">Processing fees</p>
        </div>

        <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-5 border border-blue-500/30" data-testid="net-revenue-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-blue-400" />
            </div>
            <span className="text-gray-400 text-sm">Net Revenue</span>
          </div>
          <p className="text-3xl font-bold text-blue-400" data-testid="net-revenue-value">£{financial_summary.total_net_revenue?.toFixed(2) || '0.00'}</p>
          <p className="text-xs text-gray-500 mt-1">After fees</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-xl p-5 border border-purple-500/30" data-testid="breakdown-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Crown className="h-5 w-5 text-purple-400" />
            </div>
            <span className="text-gray-400 text-sm">Breakdown</span>
          </div>
          <div className="flex gap-4">
            <div>
              <p className="text-xl font-bold text-purple-400" data-testid="total-memberships-count">{financial_summary.total_memberships || 0}</p>
              <p className="text-xs text-gray-500">Memberships</p>
            </div>
            <div>
              <p className="text-xl font-bold text-pink-400" data-testid="total-video-purchases-count">{financial_summary.total_video_purchases || 0}</p>
              <p className="text-xs text-gray-500">Videos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe Balance */}
      {stripeBalance && !stripeBalance.error && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-purple-400" />
            VOD Stripe Account Balance
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500">Available</p>
              <p className="text-2xl font-bold text-green-400">
                £{stripeBalance.available?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-400">
                £{stripeBalance.pending?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Purchases Table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden" data-testid="purchases-table-container">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2">
            <Receipt className="h-5 w-5 text-purple-400" />
            All VOD Purchases
          </h3>
          <Button size="sm" variant="outline" onClick={onRefresh} className="border-gray-700" data-testid="refresh-purchases-btn">
            Refresh
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="purchases-table">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-800/30">
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Customer</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Type</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Item</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Amount</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Fee</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Net</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Date</th>
                <th className="text-left p-4 text-gray-400 font-medium text-sm">Transaction ID</th>
                <th className="text-center p-4 text-gray-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-12 text-center" data-testid="purchases-empty-state">
                    <DollarSign className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                    <p className="text-gray-500">No purchases yet</p>
                    <p className="text-gray-600 text-sm mt-1">Revenue from memberships and video purchases will appear here</p>
                  </td>
                </tr>
              ) : (
                purchases.map((purchase, idx) => (
                  <tr key={purchase.id || idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-white">{purchase.customer_name}</p>
                        <p className="text-xs text-gray-500">{purchase.customer_email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={purchase.type === 'membership' 
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500' 
                        : 'bg-blue-600'
                      }>
                        {purchase.type === 'membership' && <Crown className="h-3 w-3 mr-1" />}
                        {purchase.type === 'membership' ? 'Membership' : 'Video'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <p className="text-gray-300 text-sm">
                        {purchase.type === 'membership' 
                          ? purchase.tier_name 
                          : purchase.video_title
                        }
                      </p>
                      {purchase.type === 'membership' && purchase.membership_end && (
                        <p className="text-xs text-gray-500">
                          Expires: {new Date(purchase.membership_end).toLocaleDateString()}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-green-400">
                        £{purchase.amount_paid?.toFixed(2) || '0.00'}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className={`text-sm ${purchase.stripe_data?.fee_estimated ? 'text-gray-500 italic' : 'text-red-400'}`}>
                        £{purchase.stripe_data?.fee?.toFixed(2) || '0.00'}
                        {purchase.stripe_data?.fee_estimated && '*'}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-blue-400">
                        £{purchase.stripe_data?.net?.toFixed(2) || '0.00'}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-400">
                        {purchase.purchased_at 
                          ? new Date(purchase.purchased_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })
                          : '-'
                        }
                      </p>
                      <p className="text-xs text-gray-600">
                        {purchase.purchased_at 
                          ? new Date(purchase.purchased_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : ''
                        }
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-xs text-gray-500 font-mono">{purchase.transaction_id}</p>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {purchase.stripe_data?.receipt_url && (
                          <a
                            href={purchase.stripe_data.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white p-1"
                            title="View Receipt"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        {!purchase.refunded && purchase.stripe_payment_intent && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRefund(purchase.id, purchase.type)}
                            disabled={processingRefund === purchase.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs"
                          >
                            {processingRefund === purchase.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              'Refund'
                            )}
                          </Button>
                        )}
                        {purchase.refunded && (
                          <Badge className="bg-red-600/20 text-red-400 text-xs">Refunded</Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {purchases.length > 0 && (
          <div className="p-4 border-t border-gray-800 text-xs text-gray-500">
            * Estimated fees (Stripe API not connected or test key in use)
          </div>
        )}
      </div>
    </div>
  );
};

// Video Modal with File Uploads
const VideoModal = ({ video, categories, series, creators = [], onClose, onSave }) => {
  const [form, setForm] = useState({
    title: video?.title || '',
    description: video?.description || '',
    thumbnail_url: video?.thumbnail_url || '',
    mobile_thumbnail_url: video?.mobile_thumbnail_url || '',
    video_url: video?.video_url || '',
    trailer_url: video?.trailer_url || '',
    duration: video?.duration || 0,
    category_id: video?.category_id || '',
    tier: video?.tier || 'basic',
    price: video?.price || 4.99,
    sale_price: video?.sale_price || '',
    is_published: video?.is_published ?? true,
    featured: video?.featured || false,
    free_preview: video?.free_preview || false,
    // Visibility status
    visibility_status: video?.visibility_status || 'public',
    premiere_date: video?.premiere_date ? video.premiere_date.slice(0, 16) : '', // Format for datetime-local input
    // Series/Episode fields
    series_id: video?.series_id || '',
    season_number: video?.season_number || '',
    episode_number: video?.episode_number || '',
    // Creator/Channel assignment
    creator_id: video?.creator_id || '',
    is_premium_exclusive: video?.is_premium_exclusive || false,
    // R2 4K fields
    video_source: video?.video_source || 'stream', // 'stream' or 'r2'
    r2_url_4k: video?.r2_url_4k || '',
    r2_url_1080p: video?.r2_url_1080p || '',
    has_4k: video?.has_4k || false,
    // SEO fields
    seo_title: video?.seo_title || '',
    seo_description: video?.seo_description || '',
    seo_keywords: video?.seo_keywords || '',
    focus_keyword: video?.focus_keyword || '',
    // Social media sharing image (1200x630 for best display)
    social_image_url: video?.social_image_url || '',
  });
  const [saving, setSaving] = useState(false);
  
  // Upload states
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingMobileThumbnail, setUploadingMobileThumbnail] = useState(false);
  const [uploadingSocialImage, setUploadingSocialImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingTrailer, setUploadingTrailer] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  
  // R2 4K Upload states
  const [uploading4K, setUploading4K] = useState(false);
  const [uploading1080p, setUploading1080p] = useState(false);
  const [progress4K, setProgress4K] = useState(0);
  const [progress1080p, setProgress1080p] = useState(0);

  const getAuthToken = () => {
    // Try admin token first, then customer token
    return localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
  };

  // Upload thumbnail to R2
  const handleThumbnailUpload = async (e, isMobile = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const setUploading = isMobile ? setUploadingMobileThumbnail : setUploadingThumbnail;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'thumbnails');
      formData.append('reference_name', isMobile ? `${form.title || 'untitled'}-mobile` : (form.title || 'untitled'));
      
      const res = await axios.post(`${API}/api/r2/upload-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        if (isMobile) {
          setForm(prev => ({ ...prev, mobile_thumbnail_url: res.data.url }));
        } else {
          setForm(prev => ({ ...prev, thumbnail_url: res.data.url }));
        }
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      alert('Failed to upload thumbnail: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Upload video to Cloudflare Stream
  const handleVideoUpload = async (e, isTrailer = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const setUploading = isTrailer ? setUploadingTrailer : setUploadingVideo;
    const setProgress = isTrailer ? setTrailerUploadProgress : setVideoUploadProgress;
    
    setUploading(true);
    setProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', isTrailer ? `${form.title || 'untitled'} - Trailer` : form.title || 'untitled');
      
      const token = getAuthToken();
      const res = await axios.post(`${API}/api/cloudflare/stream/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percent);
        }
      });
      
      if (res.data.success) {
        if (isTrailer) {
          setForm(prev => ({ ...prev, trailer_url: res.data.video_id }));
        } else {
          setForm(prev => ({ 
            ...prev, 
            video_url: res.data.video_id,
            duration: res.data.duration || prev.duration 
          }));
        }
      }
    } catch (err) {
      console.error('Video upload error:', err);
      alert('Failed to upload video: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Upload video to R2 for 4K support (chunked upload for large files)
  const handleR2Upload = async (e, quality) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const setUploading = quality === '4k' ? setUploading4K : setUploading1080p;
    const setProgress = quality === '4k' ? setProgress4K : setProgress1080p;
    
    setUploading(true);
    setProgress(0);
    
    let upload_id = null;
    
    try {
      // Use smaller chunks for better reliability (5MB instead of 10MB)
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      
      console.log(`Starting ${quality} upload: ${file.name}, ${file.size} bytes, ${totalChunks} chunks`);
      
      // Step 1: Initialize multipart upload
      const initFormData = new FormData();
      initFormData.append('filename', file.name);
      initFormData.append('content_type', file.type || 'video/mp4');
      initFormData.append('quality', quality);
      initFormData.append('video_name', form.title || '');
      
      const initRes = await axios.post(`${API}/api/r2/init-upload`, initFormData, {
        timeout: 30000 // 30 second timeout for init
      });
      upload_id = initRes.data.upload_id;
      const key = initRes.data.key;
      
      console.log(`Upload initialized: ${upload_id}`);
      
      // Step 2: Upload chunks with retry logic
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append('upload_id', upload_id);
        chunkFormData.append('part_number', (i + 1).toString());
        chunkFormData.append('chunk', chunk);
        
        // Retry logic for each chunk
        let retries = 3;
        let success = false;
        
        while (retries > 0 && !success) {
          try {
            await axios.post(`${API}/api/r2/upload-chunk`, chunkFormData, {
              timeout: 120000, // 2 minute timeout per chunk
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            success = true;
            console.log(`Chunk ${i + 1}/${totalChunks} uploaded`);
          } catch (chunkErr) {
            retries--;
            console.warn(`Chunk ${i + 1} failed, ${retries} retries left:`, chunkErr.message);
            if (retries === 0) throw chunkErr;
            // Wait before retry
            await new Promise(r => setTimeout(r, 2000));
          }
        }
        
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setProgress(progress);
      }
      
      // Step 3: Complete upload
      console.log('Completing upload...');
      const completeFormData = new FormData();
      completeFormData.append('upload_id', upload_id);
      completeFormData.append('quality', quality);
      
      const completeRes = await axios.post(`${API}/api/r2/complete-upload`, completeFormData, {
        timeout: 60000 // 1 minute timeout for completion
      });
      
      if (completeRes.data.success) {
        console.log(`Upload complete: ${completeRes.data.url}`);
        if (quality === '4k') {
          setForm(prev => ({ 
            ...prev, 
            r2_url_4k: completeRes.data.url,
            has_4k: true,
            video_source: 'r2'
          }));
        } else {
          setForm(prev => ({ 
            ...prev, 
            r2_url_1080p: completeRes.data.url,
            video_source: 'r2'
          }));
        }
      }
    } catch (err) {
      console.error('R2 upload error:', err);
      alert('Failed to upload video: ' + (err.response?.data?.detail || err.message));
      
      // Try to abort the upload if it was started
      if (upload_id) {
        try {
          const abortFormData = new FormData();
          abortFormData.append('upload_id', upload_id);
          await axios.post(`${API}/api/r2/abort-upload`, abortFormData);
          console.log('Upload aborted');
        } catch (abortErr) {
          console.error('Abort error:', abortErr);
        }
      }
    } finally {
      setUploading(false);
      // Don't reset progress immediately so user can see 100%
      setTimeout(() => setProgress(0), 2000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Validate premiere date if premiere is selected
      if (form.visibility_status === 'premiere' && !form.premiere_date) {
        alert('Please set a premiere date');
        setSaving(false);
        return;
      }
      
      // Prepare data - convert empty values appropriately
      const submitData = {
        ...form,
        sale_price: form.sale_price === '' || form.sale_price === null ? null : parseFloat(form.sale_price),
        series_id: form.series_id || null,
        season_number: form.season_number === '' ? null : parseInt(form.season_number) || null,
        episode_number: form.episode_number === '' ? null : parseInt(form.episode_number) || null,
        // Creator assignment
        creator_id: form.creator_id || null,
        is_premium_exclusive: form.creator_id ? form.is_premium_exclusive : false,
        // Convert datetime-local to ISO string
        premiere_date: form.premiere_date ? new Date(form.premiere_date).toISOString() : null,
        // Keep is_published in sync with visibility_status for backward compatibility
        is_published: form.visibility_status !== 'private',
      };
      
      if (video) {
        await axios.put(`${API}/api/vod-admin/videos/${video.id}`, submitData, { headers });
      } else {
        await axios.post(`${API}/api/vod-admin/videos`, submitData, { headers });
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save video: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold">{video ? 'Edit Video' : 'Add Video'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <Film className="h-5 w-5" /> Basic Information
            </h3>
            
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Title *</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                placeholder="Enter video title"
                className="bg-gray-800 border-gray-700"
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-2 block">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Enter video description..."
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Category *</label>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  required
                  className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Tier</label>
                <select
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="basic">Standard (Free with account)</option>
                  <option value="premium">Premium (Subscription required)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Thumbnail Uploads */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <Upload className="h-5 w-5" /> Thumbnails
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Desktop Thumbnail */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Desktop Thumbnail (16:9)</label>
                <div className="space-y-2">
                  {form.thumbnail_url && (
                    <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-800">
                      <img src={form.thumbnail_url} alt="Thumbnail" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, thumbnail_url: '' })}
                        className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingThumbnail ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-purple-500'}`}>
                    {uploadingThumbnail ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-5 w-5" /> {form.thumbnail_url ? 'Change' : 'Upload'} Thumbnail</>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(e, false)}
                      className="hidden"
                      disabled={uploadingThumbnail}
                    />
                  </label>
                </div>
              </div>

              {/* Mobile Thumbnail */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Mobile Thumbnail (9:16) - Optional</label>
                <div className="space-y-2">
                  {form.mobile_thumbnail_url && (
                    <div className="relative w-24 aspect-[9/16] rounded-lg overflow-hidden bg-gray-800 mx-auto">
                      <img src={form.mobile_thumbnail_url} alt="Mobile Thumbnail" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, mobile_thumbnail_url: '' })}
                        className="absolute top-1 right-1 p-0.5 bg-red-600 rounded-full hover:bg-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingMobileThumbnail ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-purple-500'}`}>
                    {uploadingMobileThumbnail ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-5 w-5" /> {form.mobile_thumbnail_url ? 'Change' : 'Upload'} Mobile</>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleThumbnailUpload(e, true)}
                      className="hidden"
                      disabled={uploadingMobileThumbnail}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Video Upload */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <Video className="h-5 w-5" /> Main Video
            </h3>
            
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-400">
                <strong>4K Premium Quality:</strong> Upload both 4K and 1080p versions. Viewers can select quality manually, and the player will auto-switch to 1080p on slower connections.
              </p>
            </div>
            
            {/* Quality Version Tabs */}
            <div className="flex gap-2 p-1 bg-gray-800 rounded-lg">
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, video_source: 'r2', activeVideoTab: '4k' }))}
                className={`flex-1 px-4 py-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  (form.activeVideoTab || '4k') === '4k' 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <span className="text-xs font-bold px-1.5 py-0.5 bg-black/20 rounded">4K</span>
                4K Version
                {form.r2_url_4k && <Check className="h-4 w-4 ml-1" />}
              </button>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, video_source: 'r2', activeVideoTab: '1080p' }))}
                className={`flex-1 px-4 py-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  form.activeVideoTab === '1080p' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <span className="text-xs font-bold px-1.5 py-0.5 bg-black/20 rounded">1080p</span>
                1080p Version
                {form.r2_url_1080p && <Check className="h-4 w-4 ml-1" />}
              </button>
            </div>
            
            {/* 4K Version Upload Tab */}
            {(form.activeVideoTab || '4k') === '4k' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-400">
                  Upload your 4K version (3840×2160) - Primary quality for viewers with fast connections
                </p>
                {form.r2_url_4k ? (
                  <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Play className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-400">4K Video Ready</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{form.r2_url_4k}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, r2_url_4k: '', has_4k: false })}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className={`flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploading4K ? 'border-amber-500 bg-amber-500/10' : 'border-amber-500/50 hover:border-amber-500 hover:bg-amber-500/5'}`}>
                      {uploading4K ? (
                        <>
                          <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
                          <p className="font-medium text-lg">Uploading 4K Video...</p>
                          <div className="w-full max-w-md bg-gray-800 rounded-full h-3 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-300"
                              style={{ width: `${progress4K}%` }}
                            />
                          </div>
                          <p className="text-sm text-gray-400">{progress4K}%</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-amber-500" />
                          <p className="font-medium text-lg text-amber-400">Upload 4K Version</p>
                          <p className="text-sm text-gray-500">Export from Compressor at 3840×2160</p>
                          <p className="text-xs text-gray-600">MP4, MOV, WebM supported</p>
                        </>
                      )}
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm"
                        onChange={(e) => handleR2Upload(e, '4k')}
                        className="hidden"
                        disabled={uploading4K}
                      />
                    </label>
                    
                    {/* Paste URL option */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-700" /></div>
                      <div className="relative flex justify-center text-xs uppercase"><span className="bg-gray-900 px-2 text-gray-500">Or paste R2 / direct URL</span></div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://pub-xxx.r2.dev/video-4k.mp4"
                        className="bg-gray-800 border-gray-700 text-sm flex-1"
                        data-testid="r2-url-4k-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const url = e.target.value.trim();
                            if (url) {
                              setForm({ ...form, r2_url_4k: url, has_4k: true });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 shrink-0"
                        data-testid="r2-url-4k-save"
                        onClick={(e) => {
                          const input = e.target.closest('.flex').querySelector('input');
                          const url = input?.value?.trim();
                          if (url) {
                            setForm({ ...form, r2_url_4k: url, has_4k: true });
                            input.value = '';
                          }
                        }}
                      >
                        Save URL
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* 1080p Version Upload Tab */}
            {form.activeVideoTab === '1080p' && (
              <div className="space-y-2">
                <p className="text-sm text-gray-400">
                  Upload your 1080p version (1920×1080) - Fallback for viewers with slower connections
                </p>
                {form.r2_url_1080p ? (
                  <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Play className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-400">1080p Video Ready</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">{form.r2_url_1080p}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm({ ...form, r2_url_1080p: '' })}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className={`flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploading1080p ? 'border-blue-500 bg-blue-500/10' : 'border-blue-500/50 hover:border-blue-500 hover:bg-blue-500/5'}`}>
                      {uploading1080p ? (
                        <>
                          <Loader2 className="h-10 w-10 animate-spin text-blue-400" />
                          <p className="font-medium text-lg">Uploading 1080p Video...</p>
                          <div className="w-full max-w-md bg-gray-800 rounded-full h-3 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
                              style={{ width: `${progress1080p}%` }}
                            />
                          </div>
                          <p className="text-sm text-gray-400">{progress1080p}%</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-blue-500" />
                          <p className="font-medium text-lg text-blue-400">Upload 1080p Version</p>
                          <p className="text-sm text-gray-500">Export from Compressor at 1920×1080</p>
                          <p className="text-xs text-gray-600">MP4, MOV, WebM supported</p>
                        </>
                      )}
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm"
                        onChange={(e) => handleR2Upload(e, '1080p')}
                        className="hidden"
                        disabled={uploading1080p}
                      />
                    </label>
                    
                    {/* Paste URL option */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-700" /></div>
                      <div className="relative flex justify-center text-xs uppercase"><span className="bg-gray-900 px-2 text-gray-500">Or paste R2 / direct URL</span></div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://pub-xxx.r2.dev/video-1080p.mp4"
                        className="bg-gray-800 border-gray-700 text-sm flex-1"
                        data-testid="r2-url-1080p-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const url = e.target.value.trim();
                            if (url) {
                              setForm({ ...form, r2_url_1080p: url });
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20 shrink-0"
                        data-testid="r2-url-1080p-save"
                        onClick={(e) => {
                          const input = e.target.closest('.flex').querySelector('input');
                          const url = input?.value?.trim();
                          if (url) {
                            setForm({ ...form, r2_url_1080p: url });
                            input.value = '';
                          }
                        }}
                      >
                        Save URL
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Upload Status Summary */}
            <div className="flex gap-4 p-3 bg-gray-900 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded">4K</span>
                {form.r2_url_4k ? (
                  <span className="text-green-400 text-sm flex items-center gap-1"><Check className="h-4 w-4" /> Uploaded</span>
                ) : (
                  <span className="text-gray-500 text-sm">Not uploaded</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded">1080p</span>
                {form.r2_url_1080p ? (
                  <span className="text-green-400 text-sm flex items-center gap-1"><Check className="h-4 w-4" /> Uploaded</span>
                ) : (
                  <span className="text-gray-500 text-sm">Not uploaded</span>
                )}
              </div>
            </div>
            
            {/* Duration input - common for both sources */}
            <div className="mt-4">
              <label className="text-sm text-gray-400 mb-2 block">Duration</label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    value={Math.floor(form.duration / 60)}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value) || 0;
                      const seconds = form.duration % 60;
                      setForm({ ...form, duration: (minutes * 60) + seconds });
                    }}
                    placeholder="0"
                    className="bg-gray-800 border-gray-700 text-sm w-20 text-center"
                  />
                  <span className="text-gray-400 text-sm">min</span>
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={form.duration % 60}
                    onChange={(e) => {
                      const minutes = Math.floor(form.duration / 60);
                      const seconds = Math.min(59, parseInt(e.target.value) || 0);
                      setForm({ ...form, duration: (minutes * 60) + seconds });
                    }}
                    placeholder="0"
                    className="bg-gray-800 border-gray-700 text-sm w-20 text-center"
                  />
                  <span className="text-gray-400 text-sm">sec</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Total: {Math.floor(form.duration / 60)}m {form.duration % 60}s ({form.duration} seconds)
              </p>
            </div>
          </div>

          {/* Trailer Upload */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <Film className="h-5 w-5" /> Trailer (Optional)
            </h3>
            
            <div className="space-y-2">
              {form.trailer_url ? (
                <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Film className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-400">Trailer Ready</p>
                      <p className="text-xs text-gray-500">ID: {form.trailer_url}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, trailer_url: '' })}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingTrailer ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-blue-500'}`}>
                  {uploadingTrailer ? (
                    <>
                      <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
                      <p className="font-medium">Uploading Trailer...</p>
                      <div className="w-full max-w-xs bg-gray-800 rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${trailerUploadProgress}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-400">{trailerUploadProgress}%</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-500" />
                      <p className="font-medium">Upload Trailer</p>
                      <p className="text-sm text-gray-500">Short preview video</p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm"
                    onChange={(e) => handleVideoUpload(e, true)}
                    className="hidden"
                    disabled={uploadingTrailer}
                  />
                </label>
              )}
              
              <Input
                value={form.trailer_url}
                onChange={(e) => setForm({ ...form, trailer_url: e.target.value })}
                placeholder="Or enter Cloudflare Stream ID manually"
                className="bg-gray-800 border-gray-700 text-sm"
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Pricing
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Original Price (£) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                  required
                  className="bg-gray-800 border-gray-700"
                />
                <p className={`text-xs mt-1 ${form.price > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                  {form.price > 0 ? `Paid video — customers must purchase for £${form.price.toFixed(2)}` : 'Free video — anyone can watch'}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Sale Price (£) - Optional</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.sale_price}
                  onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                  placeholder="Leave empty if not on sale"
                  className="bg-gray-800 border-gray-700"
                />
                {form.sale_price && parseFloat(form.sale_price) < form.price && (
                  <p className="text-xs text-green-400 mt-1">
                    {Math.round((1 - parseFloat(form.sale_price) / form.price) * 100)}% off!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Series / Episode Linking */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <Tv className="h-5 w-5" /> Series & Episode (Optional)
            </h3>
            <p className="text-sm text-gray-500">
              Link this video to a series as a specific episode
            </p>
            
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Series</label>
                <select
                  value={form.series_id}
                  onChange={(e) => setForm({ ...form, series_id: e.target.value })}
                  className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">No Series (Standalone)</option>
                  {series.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>
              
              {form.series_id && (
                <>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Season Number</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.season_number}
                      onChange={(e) => setForm({ ...form, season_number: e.target.value })}
                      placeholder="1"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Episode Number</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.episode_number}
                      onChange={(e) => setForm({ ...form, episode_number: e.target.value })}
                      placeholder="1"
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </>
              )}
            </div>
            
            {form.series_id && (
              <p className="text-xs text-blue-400">
                This video will appear as Season {form.season_number || 1}, Episode {form.episode_number || 1} in the series page
              </p>
            )}
          </div>

          {/* Creator/Channel Assignment */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
              <Star className="h-5 w-5" /> Creator Channel (Optional)
            </h3>
            <p className="text-sm text-gray-500">
              Assign this video to a creator's channel. Subscribers will be notified of new uploads.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Creator Channel</label>
                <select
                  value={form.creator_id}
                  onChange={(e) => setForm({ ...form, creator_id: e.target.value })}
                  className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">No Creator (Standalone)</option>
                  {creators.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              {form.creator_id && (
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_premium_exclusive}
                      onChange={(e) => setForm({ ...form, is_premium_exclusive: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-300">Premium Exclusive</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-2">(Only for premium subscribers of this creator)</p>
                </div>
              )}
            </div>
            
            {form.creator_id && (
              <p className="text-xs text-cyan-400">
                This video will appear on the creator's channel at /c/{creators.find(c => c.id === form.creator_id)?.slug || 'channel'}
              </p>
            )}
          </div>

          {/* SEO Section */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h3 className="text-lg font-semibold text-green-400 flex items-center gap-2">
              <Search className="h-5 w-5" /> SEO (Search Engine Optimization)
            </h3>
            <p className="text-xs text-gray-400">
              Optimize your video for Google Video search to increase discoverability
            </p>
            
            {/* Focus Keyword */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Focus Keyword *</label>
              <Input
                value={form.focus_keyword}
                onChange={(e) => setForm({ ...form, focus_keyword: e.target.value })}
                placeholder="e.g., behind the scenes concert, live music documentary"
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">
                The main keyword/phrase you want this video to rank for in Google
              </p>
            </div>

            {/* SEO Title */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                SEO Title 
                <span className={`ml-2 text-xs ${(form.seo_title || form.title).length > 60 ? 'text-red-400' : 'text-green-400'}`}>
                  ({(form.seo_title || form.title).length}/60 characters)
                </span>
              </label>
              <Input
                value={form.seo_title}
                onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                placeholder={form.title || "Leave empty to use video title"}
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">
                Appears in Google search results. Include your focus keyword near the beginning.
              </p>
            </div>

            {/* SEO Description */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                Meta Description
                <span className={`ml-2 text-xs ${form.seo_description.length > 160 ? 'text-red-400' : form.seo_description.length > 120 ? 'text-yellow-400' : 'text-green-400'}`}>
                  ({form.seo_description.length}/160 characters)
                </span>
              </label>
              <textarea
                value={form.seo_description}
                onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                rows={3}
                placeholder="Write a compelling description that includes your focus keyword. This appears in Google search results."
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
              />
            </div>

            {/* Keywords/Tags */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Keywords / Tags</label>
              <Input
                value={form.seo_keywords}
                onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
                placeholder="concert, live music, documentary, behind the scenes, LTV"
                className="bg-gray-800 border-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">
                Comma-separated keywords related to your video content
              </p>
            </div>

            {/* Social Media Sharing Image */}
            <div className="pt-4 border-t border-gray-700">
              <label className="text-sm text-gray-400 mb-2 block flex items-center gap-2">
                <Share2 className="h-4 w-4" /> Social Media Image (1200x630)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                This image appears when sharing on WhatsApp, Instagram, Snapchat, TikTok, Facebook, Twitter. Recommended size: 1200x630 pixels for best display on all platforms.
              </p>
              
              {form.social_image_url ? (
                <div className="relative">
                  <img 
                    src={form.social_image_url} 
                    alt="Social preview" 
                    className="w-full max-w-md h-auto rounded-lg border border-gray-700"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setForm({ ...form, social_image_url: '' })}
                    className="absolute top-2 right-2"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <div className="mt-2 p-2 bg-gray-800 rounded text-xs">
                    <p className="text-green-400 flex items-center gap-1">
                      <Check className="h-3 w-3" /> Social image set - links will show rich preview
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      setUploadingSocialImage(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('category', 'social');
                        formData.append('reference_name', form.title || '');
                        
                        const res = await axios.post(`${API}/api/r2/upload-image`, formData, {
                          headers: { 
                            Authorization: `Bearer ${getAuthToken()}`,
                            'Content-Type': 'multipart/form-data'
                          }
                        });
                        
                        setForm({ ...form, social_image_url: res.data.url });
                      } catch (err) {
                        console.error('Social image upload error:', err);
                        alert('Failed to upload social image');
                      } finally {
                        setUploadingSocialImage(false);
                      }
                    }}
                    className="hidden"
                    id="social-image-upload"
                  />
                  <label 
                    htmlFor="social-image-upload"
                    className="flex items-center gap-2 px-4 py-3 bg-gray-800 border border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-purple-500 transition-colors"
                  >
                    {uploadingSocialImage ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin text-purple-400" />
                        <span className="text-sm text-gray-400">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-purple-400" />
                        <span className="text-sm text-gray-400">Upload Social Image (1200x630 recommended)</span>
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Without this, the thumbnail will be used for social sharing (may be cropped on some platforms)
                  </p>
                </div>
              )}
            </div>

            {/* SEO Score Preview */}
            {(form.focus_keyword || form.seo_title || form.seo_description) && (
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Google Preview & SEO Score
                </h4>
                
                {/* Google Preview */}
                <div className="bg-white rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-700 mb-1 truncate">
                    ltvproduction.com › vod › video
                  </p>
                  <h5 className="text-xl text-blue-800 hover:underline cursor-pointer truncate">
                    {form.seo_title || form.title || 'Your Video Title'}
                  </h5>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {form.seo_description || form.description || 'Your video description will appear here...'}
                  </p>
                </div>

                {/* SEO Score Checklist */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 mb-2">SEO Checklist:</p>
                  
                  {/* Focus keyword in title */}
                  <div className="flex items-center gap-2 text-sm">
                    {form.focus_keyword && (form.seo_title || form.title).toLowerCase().includes(form.focus_keyword.toLowerCase()) ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span className={form.focus_keyword && (form.seo_title || form.title).toLowerCase().includes(form.focus_keyword.toLowerCase()) ? 'text-green-400' : 'text-red-400'}>
                      Focus keyword in title
                    </span>
                  </div>

                  {/* Focus keyword in description */}
                  <div className="flex items-center gap-2 text-sm">
                    {form.focus_keyword && form.seo_description.toLowerCase().includes(form.focus_keyword.toLowerCase()) ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span className={form.focus_keyword && form.seo_description.toLowerCase().includes(form.focus_keyword.toLowerCase()) ? 'text-green-400' : 'text-red-400'}>
                      Focus keyword in description
                    </span>
                  </div>

                  {/* Title length */}
                  <div className="flex items-center gap-2 text-sm">
                    {(form.seo_title || form.title).length >= 30 && (form.seo_title || form.title).length <= 60 ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-400" />
                    )}
                    <span className={(form.seo_title || form.title).length >= 30 && (form.seo_title || form.title).length <= 60 ? 'text-green-400' : 'text-yellow-400'}>
                      Title length (30-60 chars)
                    </span>
                  </div>

                  {/* Description length */}
                  <div className="flex items-center gap-2 text-sm">
                    {form.seo_description.length >= 120 && form.seo_description.length <= 160 ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-400" />
                    )}
                    <span className={form.seo_description.length >= 120 && form.seo_description.length <= 160 ? 'text-green-400' : 'text-yellow-400'}>
                      Description length (120-160 chars)
                    </span>
                  </div>

                  {/* Has thumbnail */}
                  <div className="flex items-center gap-2 text-sm">
                    {form.thumbnail_url ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-400" />
                    )}
                    <span className={form.thumbnail_url ? 'text-green-400' : 'text-red-400'}>
                      Has thumbnail image
                    </span>
                  </div>

                  {/* Has duration */}
                  <div className="flex items-center gap-2 text-sm">
                    {form.duration > 0 ? (
                      <CheckCircle className="h-4 w-4 text-green-400" />
                    ) : (
                      <XCircle className="h-4 w-4 text-yellow-400" />
                    )}
                    <span className={form.duration > 0 ? 'text-green-400' : 'text-yellow-400'}>
                      Has video duration
                    </span>
                  </div>
                </div>

                {/* Overall Score */}
                {(() => {
                  let score = 0;
                  if (form.focus_keyword && (form.seo_title || form.title).toLowerCase().includes(form.focus_keyword.toLowerCase())) score++;
                  if (form.focus_keyword && form.seo_description.toLowerCase().includes(form.focus_keyword.toLowerCase())) score++;
                  if ((form.seo_title || form.title).length >= 30 && (form.seo_title || form.title).length <= 60) score++;
                  if (form.seo_description.length >= 120 && form.seo_description.length <= 160) score++;
                  if (form.thumbnail_url) score++;
                  if (form.duration > 0) score++;
                  
                  const percentage = Math.round((score / 6) * 100);
                  const color = percentage >= 80 ? 'text-green-400' : percentage >= 50 ? 'text-yellow-400' : 'text-red-400';
                  const bgColor = percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500';
                  
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">SEO Score</span>
                        <span className={`text-lg font-bold ${color}`}>{percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className={`${bgColor} h-2 rounded-full transition-all`} 
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {percentage >= 80 ? 'Great! Your video is well optimized for search.' : 
                         percentage >= 50 ? 'Good start, but there is room for improvement.' : 
                         'Add more SEO details to improve discoverability.'}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Visibility & Publishing Options */}
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <h4 className="text-sm font-medium text-gray-300">Visibility Status</h4>
            
            {/* Visibility Status Radio Group */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'public', label: 'Public', icon: '🌍', desc: 'Visible to everyone' },
                { value: 'unlisted', label: 'Unlisted', icon: '🔗', desc: 'Only via direct link' },
                { value: 'private', label: 'Private', icon: '🔒', desc: 'Hidden from all' },
                { value: 'premiere', label: 'Premiere', icon: '🎬', desc: 'Scheduled release' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    form.visibility_status === option.value
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="visibility_status"
                    value={option.value}
                    checked={form.visibility_status === option.value}
                    onChange={(e) => setForm({ ...form, visibility_status: e.target.value })}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <span className="text-xl">{option.icon}</span>
                    <p className="font-medium mt-1">{option.label}</p>
                    <p className="text-xs text-gray-500">{option.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            
            {/* Premiere Date Picker - Only shown when Premiere is selected */}
            {form.visibility_status === 'premiere' && (
              <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                <label className="block text-sm font-medium text-purple-300 mb-2">
                  Premiere Date & Time
                </label>
                <Input
                  type="datetime-local"
                  value={form.premiere_date}
                  onChange={(e) => setForm({ ...form, premiere_date: e.target.value })}
                  className="bg-[#1a1a1a] border-gray-700"
                  min={new Date().toISOString().slice(0, 16)}
                  required={form.visibility_status === 'premiere'}
                />
                <p className="text-xs text-gray-400 mt-2">
                  The video will be hidden until this date. Users can set reminders to be notified when it goes live.
                  A countdown timer will be shown on the video page.
                </p>
              </div>
            )}
            
            {/* Video Link - Show for existing videos (unlisted, public, premiere) */}
            {video && form.slug && form.visibility_status !== 'private' && (
              <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <label className="block text-sm font-medium text-blue-300 mb-2 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" /> Video Link {form.visibility_status === 'unlisted' && <Badge className="bg-yellow-600 text-xs">Unlisted</Badge>}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={form.visibility_status === 'unlisted' 
                      ? `${window.location.origin}/vod/watch/${form.slug}?autoplay=true`
                      : `${window.location.origin}/vod/watch/${form.slug}`}
                    readOnly
                    className="bg-[#1a1a1a] border-gray-700 text-gray-300"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const url = form.visibility_status === 'unlisted'
                        ? `${window.location.origin}/vod/watch/${form.slug}?autoplay=true`
                        : `${window.location.origin}/vod/watch/${form.slug}`;
                      navigator.clipboard.writeText(url);
                      alert('Link copied to clipboard!');
                    }}
                    className="bg-blue-600 hover:bg-blue-500 px-4"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {form.visibility_status === 'unlisted' 
                    ? '🔗 Unlisted video - Only accessible via this link. Video will auto-play when opened.'
                    : 'Direct link to this video.'}
                </p>
              </div>
            )}

            {/* Other Publishing Options */}
            <div className="flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-5 h-5 rounded accent-purple-500"
                />
                <span>Featured</span>
              </label>
            </div>
            
            {/* Free Preview Option */}
            <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.free_preview}
                  onChange={(e) => setForm({ ...form, free_preview: e.target.checked })}
                  className="w-5 h-5 rounded accent-green-500 mt-0.5"
                />
                <div>
                  <span className="font-medium text-green-400">Free Preview (No Registration Required)</span>
                  <p className="text-xs text-gray-400 mt-1">
                    Enable this to let visitors watch this video without creating an account. 
                    Perfect for Episode 1 to hook viewers before they sign up.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-gray-700">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving || uploadingVideo || uploadingTrailer || uploadingThumbnail || uploadingMobileThumbnail || uploading4K || uploading1080p || (!form.video_url && !form.r2_url_4k)} 
              className="bg-purple-600 hover:bg-purple-500 flex-1"
            >
              {saving ? 'Saving...' : (video ? 'Update Video' : 'Create Video')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Category Modal
const CategoryModal = ({ category, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: category?.name || '',
    description: category?.description || '',
    icon: category?.icon || '📁',
    sort_order: category?.sort_order || 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (category) {
        await axios.put(`${API}/api/vod-admin/categories/${category.id}`, form, { headers });
      } else {
        await axios.post(`${API}/api/vod-admin/categories`, form, { headers });
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const iconOptions = ['🎙️', '📺', '🎵', '🎬', '🎧', '📁', '🎤', '🎪', '🎭', '🎯'];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold">{category ? 'Edit Category' : 'Add Category'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Name *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Description</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Icon</label>
            <div className="flex flex-wrap gap-2">
              {iconOptions.map(icon => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setForm({ ...form, icon })}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-colors ${
                    form.icon === icon 
                      ? 'bg-purple-600 ring-2 ring-purple-400' 
                      : 'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Sort Order</label>
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-gray-700">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-500 flex-1">
              {saving ? 'Saving...' : (category ? 'Update Category' : 'Create Category')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Series Modal
const SeriesModal = ({ series, categories, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: series?.title || '',
    description: series?.description || '',
    thumbnail_url: series?.thumbnail_url || '',
    trailer_url: series?.trailer_url || '',
    category_id: series?.category_id || '',
    tier: series?.tier || 'basic',
    is_published: series?.is_published ?? true,
    featured: series?.featured || false,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadingTrailer, setUploadingTrailer] = useState(false);
  const [trailerProgress, setTrailerProgress] = useState(0);

  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingThumbnail(true);
    
    try {
      // Create a clean folder name using the series title
      const seriesName = form.title?.trim() || 'untitled-series';
      const cleanFolder = `vod/series/${seriesName.replace(/[^a-zA-Z0-9-_]/g, '-')}`;
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', cleanFolder);
      
      const token = getAuthToken();
      const res = await axios.post(`${API}/api/cloudflare/images/upload-file`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        setForm(prev => ({ ...prev, thumbnail_url: res.data.url }));
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      alert('Failed to upload thumbnail: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleTrailerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a video file (MP4, MOV, AVI, WebM, or MKV)');
      return;
    }
    
    // Check file size (max 500MB)
    if (file.size > 500 * 1024 * 1024) {
      alert('File too large. Maximum size is 500MB');
      return;
    }
    
    setUploadingTrailer(true);
    setTrailerProgress(0);
    
    try {
      const token = getAuthToken();
      
      // Create a clean name using the series title
      const seriesName = form.title?.trim() || 'untitled-series';
      const cleanName = `VOD-Series/${seriesName.replace(/[^a-zA-Z0-9-_]/g, '-')}/trailer`;
      
      // Step 1: Get direct upload URL from Cloudflare Stream
      setTrailerProgress(5);
      const uploadRes = await axios.post(`${API}/api/cloudflare/stream/upload-url-json`, {
        name: cleanName,
        maxDurationSeconds: 1800 // 30 minutes max for trailers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!uploadRes.data.success) {
        throw new Error(uploadRes.data.detail || 'Failed to get upload URL');
      }
      
      const { uploadURL, uid } = uploadRes.data;
      
      // Step 2: Upload video directly to Cloudflare using the direct upload URL
      setTrailerProgress(10);
      
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(uploadURL, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 85) + 10;
          setTrailerProgress(progress);
        }
      });
      
      setTrailerProgress(100);
      setForm(prev => ({ ...prev, trailer_url: uid }));
      
      // Show success message briefly
      setTimeout(() => setTrailerProgress(0), 1500);
      
    } catch (err) {
      console.error('Trailer upload error:', err);
      alert('Failed to upload trailer: ' + (err.response?.data?.detail || err.message));
      setTrailerProgress(0);
    } finally {
      setUploadingTrailer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      if (series) {
        await axios.put(`${API}/api/vod-admin/series/${series.id}`, form, { headers });
      } else {
        await axios.post(`${API}/api/vod-admin/series`, form, { headers });
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save series: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold">{series ? 'Edit Series' : 'Create Series'}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Thumbnail */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Series Thumbnail</label>
            <div className="space-y-2">
              {form.thumbnail_url && (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-800">
                  <img src={form.thumbnail_url} alt="Thumbnail" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, thumbnail_url: '' })}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingThumbnail ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-purple-500'}`}>
                {uploadingThumbnail ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-5 w-5" /> {form.thumbnail_url ? 'Change' : 'Upload'} Thumbnail</>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                  disabled={uploadingThumbnail}
                />
              </label>
            </div>
          </div>

          {/* Trailer Upload */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Series Trailer (Optional)</label>
            <div className="space-y-2">
              {form.trailer_url && !uploadingTrailer && (
                <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Film className="h-5 w-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-green-400">Trailer Uploaded</p>
                        <p className="text-xs text-gray-500">ID: {form.trailer_url}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, trailer_url: '' })}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <a 
                    href={`https://iframe.videodelivery.net/${form.trailer_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:underline mt-2 block"
                  >
                    Preview in new tab
                  </a>
                </div>
              )}
              
              {uploadingTrailer && (
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                    <span className="text-sm">Uploading trailer... {trailerProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                      style={{ width: `${trailerProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {!uploadingTrailer && (
                <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors border-gray-700 hover:border-purple-500`}>
                  <Video className="h-5 w-5" />
                  <span>{form.trailer_url ? 'Change' : 'Upload'} Trailer Video</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleTrailerUpload}
                    className="hidden"
                    disabled={uploadingTrailer}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Title *</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              placeholder="Enter series title"
              className="bg-gray-800 border-gray-700"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Describe the series..."
              className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
            />
          </div>

          {/* Category and Tier */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Category *</label>
              <select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                required
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
              >
                <option value="basic">Standard (Free with account)</option>
                <option value="premium">Premium (Subscription required)</option>
              </select>
            </div>
          </div>

          {/* Publishing Options */}
          <div className="flex gap-6 pt-4 border-t border-gray-800">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                className="w-5 h-5 rounded accent-purple-500"
              />
              <span>Published</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="w-5 h-5 rounded accent-purple-500"
              />
              <span>Featured</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="border-gray-700">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving || uploadingThumbnail || uploadingTrailer} 
              className="bg-purple-600 hover:bg-purple-500 flex-1"
            >
              {saving ? 'Saving...' : (series ? 'Update Series' : 'Create Series')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Popup Settings Tab Component
const PopupSettingsTab = ({ authToken }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    trigger_type: 'percentage',
    trigger_value: 50,
    title: 'Enjoying the content?',
    message: 'Create a FREE account to stay updated with:',
    benefits: ['New episode releases', 'Exclusive series & documentaries', 'Live concerts & shows', 'Save your watch progress'],
    cta_button_text: 'Create Free Account',
    dismiss_button_text: 'Maybe later'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBenefit, setNewBenefit] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [authToken]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/vod-admin/popup/settings`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.data) {
        setSettings(res.data);
      }
    } catch (err) {
      console.error('Error fetching popup settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/vod-admin/popup/settings`, settings, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      alert('Popup settings saved successfully!');
    } catch (err) {
      console.error('Error saving popup settings:', err);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setSettings(prev => ({
        ...prev,
        benefits: [...(prev.benefits || []), newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setSettings(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Bell className="h-5 w-5 text-pink-500" />
            Signup Popup Settings
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Control when and what the signup popup shows to non-logged in users
          </p>
        </div>
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-purple-600 hover:bg-purple-500"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">Enable Signup Popup</h3>
              <p className="text-gray-400 text-sm">Show the signup prompt to non-logged in viewers</p>
            </div>
            <button
              onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`w-14 h-8 rounded-full transition-colors relative ${settings.enabled ? 'bg-purple-600' : 'bg-gray-700'}`}
            >
              <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${settings.enabled ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Trigger Settings */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6 space-y-5">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Clock className="h-4 w-4 text-purple-400" />
            Popup Trigger
          </h3>
          
          {/* Beginning of Video Toggle */}
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-white">Beginning of Video</h4>
                <p className="text-sm text-gray-400 mt-1">Show popup immediately when user presses play</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ 
                  ...prev, 
                  trigger_type: prev.trigger_type === 'on_play' ? 'percentage' : 'on_play' 
                }))}
                className={`w-14 h-8 rounded-full transition-colors relative ${settings.trigger_type === 'on_play' ? 'bg-purple-600' : 'bg-gray-700'}`}
              >
                <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-transform ${settings.trigger_type === 'on_play' ? 'right-1' : 'left-1'}`} />
              </button>
            </div>
          </div>
          
          {/* Alternative Trigger Options (shown when "on_play" is OFF) */}
          {settings.trigger_type !== 'on_play' && (
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Trigger Type</label>
                <select
                  value={settings.trigger_type}
                  onChange={(e) => setSettings(prev => ({ ...prev, trigger_type: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="percentage">Percentage of Video Watched</option>
                  <option value="seconds">After X Seconds of Watching</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {settings.trigger_type === 'percentage' ? 'Trigger at (%)' : 'Trigger after (seconds)'}
                </label>
                <Input
                  type="number"
                  value={settings.trigger_value}
                  onChange={(e) => setSettings(prev => ({ ...prev, trigger_value: parseFloat(e.target.value) || 0 }))}
                  min={0}
                  max={settings.trigger_type === 'percentage' ? 100 : 3600}
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>
          )}
          
          {/* Status Message */}
          <div className={`text-sm rounded-lg px-4 py-3 flex items-center gap-2 ${
            settings.trigger_type === 'on_play' 
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' 
              : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
          }`}>
            <span className="text-lg">
              {settings.trigger_type === 'on_play' ? '▶️' : '⏱️'}
            </span>
            <span>
              {settings.trigger_type === 'on_play' 
                ? 'Popup will appear immediately when the user presses play'
                : settings.trigger_type === 'percentage' 
                  ? `Popup will appear when viewer reaches ${settings.trigger_value}% of the video`
                  : `Popup will appear after viewer watches ${settings.trigger_value} seconds`
              }
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Content Settings */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Settings className="h-4 w-4 text-purple-400" />
            Popup Content
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
            <Input
              value={settings.title}
              onChange={(e) => setSettings(prev => ({ ...prev, title: e.target.value }))}
              className="bg-gray-800 border-gray-600 text-white placeholder-gray-500"
              placeholder="e.g., Enjoying the content?"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
            <Input
              value={settings.message}
              onChange={(e) => setSettings(prev => ({ ...prev, message: e.target.value }))}
              className="bg-gray-800 border-gray-600 text-white placeholder-gray-500"
              placeholder="e.g., Create a FREE account to stay updated with:"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Benefits List</label>
            <div className="space-y-2 mb-3">
              {(settings.benefits || []).map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5">
                  <Sparkles className="h-4 w-4 text-purple-400 flex-shrink-0" />
                  <span className="flex-1 text-sm text-white">{benefit}</span>
                  <button 
                    onClick={() => removeBenefit(index)}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                className="bg-gray-800 border-gray-600 text-white placeholder-gray-500 flex-1"
                placeholder="Add a new benefit..."
                onKeyPress={(e) => e.key === 'Enter' && addBenefit()}
              />
              <Button onClick={addBenefit} variant="outline" className="border-gray-600 text-white hover:bg-gray-700">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Button Text Settings */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold text-white">Button Text</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Main CTA Button</label>
              <Input
                value={settings.cta_button_text}
                onChange={(e) => setSettings(prev => ({ ...prev, cta_button_text: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white placeholder-gray-500"
                placeholder="e.g., Create Free Account"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Dismiss Button</label>
              <Input
                value={settings.dismiss_button_text}
                onChange={(e) => setSettings(prev => ({ ...prev, dismiss_button_text: e.target.value }))}
                className="bg-gray-800 border-gray-600 text-white placeholder-gray-500"
                placeholder="e.g., Maybe later"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6">
          <h3 className="font-semibold text-white mb-4">Preview</h3>
          <div className="bg-gradient-to-br from-gray-800 to-black rounded-2xl p-6 border border-purple-500/30 max-w-md mx-auto">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Bell className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="text-center mb-4">
              <h4 className="text-lg font-bold text-white mb-2">{settings.title}</h4>
              <p className="text-gray-400 text-sm">{settings.message}</p>
            </div>
            <div className="space-y-2 mb-6">
              {(settings.benefits || []).slice(0, 4).map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Sparkles className="h-2.5 w-2.5 text-purple-400" />
                  </div>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <div className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-center text-white font-semibold text-sm">
                {settings.cta_button_text}
              </div>
              <div className="w-full py-1.5 text-center text-gray-400 text-xs">
                {settings.dismiss_button_text}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Creators/Channels Tab Component
const CreatorsTab = ({ creators, loading, onAdd, onEdit, onRefresh, authToken }) => {
  const navigate = useNavigate();
  
  const handleDelete = async (creatorId) => {
    if (!window.confirm('Are you sure you want to deactivate this creator channel?')) return;
    
    try {
      await axios.delete(`${API}/api/creators/profiles/${creatorId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      onRefresh();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Failed to deactivate creator');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Creator Channels</h2>
          <p className="text-gray-400 text-sm mt-1">
            Manage creator/show profiles. Users can subscribe to creators and receive notifications for new content.
          </p>
        </div>
        <Button onClick={onAdd} className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500">
          <Plus className="h-4 w-4 mr-2" /> Add Creator
        </Button>
      </div>

      {creators.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800">
          <Star className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500 mb-4">No creator channels yet</p>
          <p className="text-gray-600 text-sm mb-6">Create a channel for your shows or creators to enable subscriptions and notifications</p>
          <Button onClick={onAdd} className="bg-gradient-to-r from-blue-600 to-cyan-600">
            <Plus className="h-4 w-4 mr-2" /> Create Your First Channel
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {creators.map((creator) => (
            <div key={creator.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden group">
              {/* Banner */}
              <div className="h-24 bg-gradient-to-br from-blue-900/50 to-cyan-900/30 relative overflow-hidden">
                {creator.banner_image_url && (
                  <img 
                    src={creator.banner_image_url} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
                {/* Status badge */}
                <div className="absolute top-2 right-2">
                  {creator.is_active ? (
                    <Badge className="bg-green-600">Active</Badge>
                  ) : (
                    <Badge className="bg-gray-600">Inactive</Badge>
                  )}
                </div>
              </div>

              {/* Profile */}
              <div className="p-4 -mt-10 relative">
                <div className="flex items-end gap-3 mb-3">
                  <div className="w-16 h-16 rounded-full border-4 border-gray-900 overflow-hidden bg-gray-800 flex-shrink-0">
                    {creator.profile_image_url ? (
                      <img src={creator.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl font-bold text-blue-400">
                        {creator.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  {creator.is_verified && (
                    <BadgeCheck className="w-5 h-5 text-purple-400 -ml-6 mb-1" />
                  )}
                </div>

                <h3 className="font-bold text-lg text-white line-clamp-1">{creator.name}</h3>
                <p className="text-sm text-gray-500 mb-2">/c/{creator.slug}</p>
                
                <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                  {creator.description || 'No description'}
                </p>

                {/* Stats */}
                <div className="flex gap-4 text-sm text-gray-400 mb-4">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {creator.subscriber_count || 0} subs
                  </span>
                  <span className="flex items-center gap-1">
                    <Video className="w-4 h-4" />
                    {creator.video_count || 0} videos
                  </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/c/${creator.slug}`)}
                    className="flex-1 border-gray-700"
                  >
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(creator)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(creator.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Reels Tab Component
const ReelsTab = ({ creators, authToken }) => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReel, setEditingReel] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    fetchReels();
    fetchVideos();
  }, []);

  const fetchReels = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/reels?published_only=false&limit=100`);
      setReels(response.data.reels || []);
    } catch (err) {
      console.error('Error fetching reels:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/api/vod/videos?limit=100`);
      setVideos(response.data.videos || []);
    } catch (err) {
      console.error('Error fetching videos:', err);
    }
  };

  const handleDelete = async (reelId) => {
    if (!window.confirm('Are you sure you want to delete this reel?')) return;
    
    try {
      await axios.delete(`${API}/api/reels/${reelId}`);
      fetchReels();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Failed to delete reel');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Video Reels</h2>
          <p className="text-gray-400 text-sm mt-1">
            Short-form vertical videos (max 3 minutes). Link reels to full videos to drive engagement.
          </p>
        </div>
        <Button 
          onClick={() => { setEditingReel(null); setShowModal(true); }} 
          className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Reel
        </Button>
      </div>

      {reels.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800">
          <Film className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500 mb-4">No reels yet</p>
          <p className="text-gray-600 text-sm mb-6">Create short vertical videos to engage viewers on the homepage</p>
          <Button 
            onClick={() => { setEditingReel(null); setShowModal(true); }}
            className="bg-gradient-to-r from-pink-600 to-purple-600"
          >
            <Plus className="h-4 w-4 mr-2" /> Create Your First Reel
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {reels.map((reel) => (
            <div key={reel.id} className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden group">
              {/* Thumbnail */}
              <div className="relative aspect-[9/16] bg-gray-800">
                {reel.thumbnail_url ? (
                  <img 
                    src={reel.thumbnail_url} 
                    alt={reel.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-900/50 to-purple-900/50">
                    <Film className="h-8 w-8 text-gray-600" />
                  </div>
                )}
                
                {/* Duration badge */}
                {reel.duration > 0 && (
                  <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                    {formatDuration(reel.duration)}
                  </span>
                )}
                
                {/* Status badge */}
                <div className="absolute top-2 left-2">
                  {reel.is_published ? (
                    <Badge className="bg-green-600 text-xs">Published</Badge>
                  ) : (
                    <Badge className="bg-yellow-600 text-xs">Draft</Badge>
                  )}
                </div>
                
                {/* Action buttons overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => { setEditingReel(reel); setShowModal(true); }}
                    className="bg-blue-600 hover:bg-blue-500"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDelete(reel.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-3">
                <h3 className="font-medium text-white text-sm line-clamp-2 mb-1">{reel.title}</h3>
                {reel.creator_name && (
                  <p className="text-gray-400 text-xs truncate">{reel.creator_name}</p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3" /> {reel.view_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {reel.like_count || 0}
                  </span>
                </div>
                {reel.linked_video_title && (
                  <p className="text-purple-400 text-xs mt-2 truncate flex items-center gap-1">
                    <LinkIcon className="h-3 w-3" /> {reel.linked_video_title}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reel Modal */}
      {showModal && (
        <ReelModal 
          reel={editingReel}
          creators={creators}
          videos={videos}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchReels(); }}
        />
      )}
    </div>
  );
};

// Reel Modal Component
const ReelModal = ({ reel, creators, videos, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: reel?.title || '',
    description: reel?.description || '',
    video_url: reel?.video_url || '',
    thumbnail_url: reel?.thumbnail_url || '',
    creator_id: reel?.creator_id || '',
    linked_video_id: reel?.linked_video_id || '',
    duration: reel?.duration || 0,
    is_published: reel?.is_published ?? true,
    stream_video_id: reel?.stream_video_id || '' // Cloudflare Stream video ID
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [processingVideo, setProcessingVideo] = useState(false);

  // Filter videos by selected creator
  const creatorVideos = formData.creator_id 
    ? videos.filter(v => v.creator_id === formData.creator_id)
    : videos;

  // Poll for video processing status
  const pollVideoStatus = async (videoId) => {
    setProcessingVideo(true);
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/cloudflare/stream/video/${videoId}`);
        const data = response.data;
        
        if (data.ready_to_stream) {
          // Video is ready! Generate thumbnail at 1 second mark
          const thumbnailResponse = await axios.post(`${API}/api/cloudflare/stream/video/${videoId}/thumbnail`, null, {
            params: { time_seconds: 1.0 }
          });
          const autoThumbnail = thumbnailResponse.data.thumbnail_url;
          
          // Update form with HLS URL and auto-generated thumbnail
          setFormData(prev => ({
            ...prev,
            video_url: data.playback.hls,
            thumbnail_url: autoThumbnail || data.thumbnail, // Use auto-generated thumbnail
            duration: Math.floor(data.duration || 0),
            stream_video_id: videoId
          }));
          setProcessingVideo(false);
          return true;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          setProcessingVideo(false);
          alert('Video processing is taking longer than expected. Please check back later.');
        }
      } catch (err) {
        console.error('Error checking video status:', err);
        setProcessingVideo(false);
      }
    };
    
    checkStatus();
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 2GB for reels)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > maxSize) {
      alert('Video file is too large. Maximum size is 2GB.');
      return;
    }

    // Get video duration before upload
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = async () => {
      const duration = Math.floor(video.duration);
      if (duration > 180) {
        if (!window.confirm('Video is longer than 3 minutes. Reels work best under 3 minutes. Continue anyway?')) {
          return;
        }
      }
      
      setUploading(true);
      setUploadProgress(0);
      
      try {
        // Upload video through our backend server (bypasses CORS)
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('name', formData.title || file.name);
        if (formData.creator_id) {
          uploadFormData.append('creator_id', formData.creator_id);
        }
        uploadFormData.append('max_duration_seconds', Math.max(duration + 60, 300).toString());
        
        const response = await axios.post(`${API}/api/cloudflare/stream/upload-video`, uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
          timeout: 600000 // 10 minute timeout for large files
        });
        
        if (response.data.success) {
          const { video_id, playback_hls, thumbnail } = response.data;
          
          setUploadProgress(100);
          setFormData(prev => ({
            ...prev,
            video_url: playback_hls,
            thumbnail_url: thumbnail,
            duration,
            stream_video_id: video_id
          }));
          
          // Poll for video processing completion
          pollVideoStatus(video_id);
        } else {
          throw new Error(response.data.detail || 'Upload failed');
        }
        
      } catch (err) {
        console.error('Upload error:', err);
        alert(err.response?.data?.detail || err.message || 'Failed to upload video');
      } finally {
        setUploading(false);
      }
    };
    
    video.onerror = () => {
      alert('Could not read video file. Please try a different file.');
    };
    
    video.src = URL.createObjectURL(file);
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingThumb(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('folder', 'reel-thumbnails');
      uploadFormData.append('reference_name', formData.title || '');

      const response = await axios.post(`${API}/api/r2/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, thumbnail_url: response.data.url }));
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload thumbnail');
    } finally {
      setUploadingThumb(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.video_url || !formData.creator_id) {
      alert('Please fill in all required fields: Title, Video, and Creator');
      return;
    }

    setSaving(true);
    try {
      if (reel) {
        await axios.put(`${API}/api/reels/${reel.id}`, formData);
      } else {
        await axios.post(`${API}/api/reels`, formData);
      }
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save reel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">
            {reel ? 'Edit Reel' : 'Add New Reel'}
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Reel title"
              className="bg-gray-800 border-gray-700"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Short description..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
              rows={3}
            />
          </div>

          {/* Creator */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Creator *</label>
            <select
              value={formData.creator_id}
              onChange={(e) => setFormData(prev => ({ ...prev, creator_id: e.target.value, linked_video_id: '' }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="">Select a creator</option>
              {creators.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.display_name}</option>
              ))}
            </select>
          </div>

          {/* Linked Video */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Link to Full Video (Optional)
              <span className="text-gray-500 font-normal ml-2">- Viewers can click to watch the full video</span>
            </label>
            <select
              value={formData.linked_video_id}
              onChange={(e) => setFormData(prev => ({ ...prev, linked_video_id: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="">No linked video</option>
              {creatorVideos.map(v => (
                <option key={v.id} value={v.id}>{v.title}</option>
              ))}
            </select>
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Video * <span className="text-gray-500 font-normal">(Vertical 9:16, up to 2GB)</span>
              <span className="ml-2 text-xs text-purple-400">Powered by Cloudflare Stream</span>
            </label>
            {formData.video_url ? (
              <div className="relative aspect-[9/16] max-w-[200px] bg-gray-800 rounded-lg overflow-hidden">
                <video 
                  src={formData.video_url} 
                  className="w-full h-full object-cover"
                  controls
                />
                <button 
                  onClick={() => setFormData(prev => ({ ...prev, video_url: '', stream_video_id: '' }))}
                  className="absolute top-2 right-2 p-1 bg-red-600 rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : processingVideo ? (
              <div className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-purple-600 rounded-lg bg-purple-900/20">
                <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-2" />
                <span className="text-purple-400 text-sm font-medium">Processing video...</span>
                <span className="text-gray-500 text-xs mt-1">Optimizing for mobile streaming</span>
              </div>
            ) : uploading ? (
              <div className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-pink-600 rounded-lg bg-pink-900/20">
                <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden mb-3">
                  <div 
                    className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="text-pink-400 text-sm font-medium">Uploading... {uploadProgress}%</span>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800/50 hover:border-pink-600 transition">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                <Upload className="h-8 w-8 text-gray-500 mb-2" />
                <span className="text-gray-400 text-sm">Click to upload video</span>
                <span className="text-gray-600 text-xs mt-1">Adaptive streaming for mobile</span>
              </label>
            )}
            {formData.duration > 0 && (
              <p className="text-sm text-gray-400 mt-2">
                Duration: {Math.floor(formData.duration / 60)}:{(formData.duration % 60).toString().padStart(2, '0')}
                {formData.duration > 180 && (
                  <span className="text-yellow-500 ml-2">(exceeds 3 min limit)</span>
                )}
              </p>
            )}
          </div>

          {/* Thumbnail Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Thumbnail <span className="text-gray-500 font-normal">(9:16 aspect ratio recommended)</span>
            </label>
            {formData.thumbnail_url ? (
              <div className="relative aspect-[9/16] max-w-[120px] bg-gray-800 rounded-lg overflow-hidden">
                <img 
                  src={formData.thumbnail_url} 
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                />
                <button 
                  onClick={() => setFormData(prev => ({ ...prev, thumbnail_url: '' }))}
                  className="absolute top-2 right-2 p-1 bg-red-600 rounded-full"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-32 h-56 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800/50 transition">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                />
                {uploadingThumb ? (
                  <Loader2 className="h-6 w-6 animate-spin text-pink-500" />
                ) : (
                  <>
                    <ImageIcon className="h-6 w-6 text-gray-500 mb-2" />
                    <span className="text-gray-400 text-xs">Add thumbnail</span>
                  </>
                )}
              </label>
            )}
          </div>

          {/* Published Status */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_published"
              checked={formData.is_published}
              onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-700 bg-gray-800"
            />
            <label htmlFor="is_published" className="text-gray-300">Publish reel (visible on homepage)</label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-to-r from-pink-600 to-purple-600"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {reel ? 'Update Reel' : 'Create Reel'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Homepage Layout Tab Component
const HomepageLayoutTab = ({ authToken }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/homepage/layout`);
      setSections(res.data.sections || []);
    } catch (err) {
      console.error('Error fetching homepage layout:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (iconName) => {
    const icons = {
      'Image': <Image className="h-4 w-4" />,
      'Grid': <Grid className="h-4 w-4" />,
      'Users': <Users className="h-4 w-4" />,
      'TrendingUp': <TrendingUp className="h-4 w-4" />,
      'Sparkles': <Sparkles className="h-4 w-4" />,
      'History': <History className="h-4 w-4" />,
      'Star': <Star className="h-4 w-4" />,
    };
    return icons[iconName] || <Layers className="h-4 w-4" />;
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    // Swap order values
    const tempOrder = newSections[index].order;
    newSections[index].order = newSections[targetIndex].order;
    newSections[targetIndex].order = tempOrder;
    
    // Swap positions in array
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    setSections(newSections);
  };

  const toggleVisibility = (index) => {
    const newSections = [...sections];
    newSections[index].is_visible = !newSections[index].is_visible;
    setSections(newSections);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/homepage/layout`, {
        sections: sections.map((s, i) => ({
          id: s.id,
          order: i,
          is_visible: s.is_visible
        }))
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      alert('Homepage layout saved successfully!');
      fetchSections();
    } catch (err) {
      console.error('Error saving layout:', err);
      alert('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const resetLayout = async () => {
    if (!window.confirm('Are you sure you want to reset to default layout?')) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/homepage/layout/reset`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      fetchSections();
    } catch (err) {
      console.error('Error resetting layout:', err);
      alert('Failed to reset layout');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Homepage Layout</h2>
          <p className="text-gray-400 text-sm mt-1">
            Manage the order and visibility of sections on the VOD homepage. Drag sections up or down to reorder.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={resetLayout}
            disabled={saving}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Reset to Default
          </Button>
          <Button
            onClick={saveLayout}
            disabled={saving}
            className="bg-cyan-600 hover:bg-cyan-500"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Layout
          </Button>
        </div>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 bg-gray-800/50 border-b border-gray-700">
          <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-400">
            <div className="col-span-1">Order</div>
            <div className="col-span-5">Section</div>
            <div className="col-span-3">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
        </div>
        
        <div className="divide-y divide-gray-800">
          {sections.map((section, index) => (
            <div 
              key={section.id} 
              className={`p-4 ${!section.is_visible ? 'opacity-50' : ''}`}
            >
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-1 text-gray-500 font-mono text-sm">
                  #{index + 1}
                </div>
                <div className="col-span-5 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${section.is_visible ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-700 text-gray-500'} flex items-center justify-center`}>
                    {getSectionIcon(section.icon)}
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{section.display_name}</h4>
                    <p className="text-xs text-gray-500">{section.id}</p>
                  </div>
                </div>
                <div className="col-span-3">
                  <button
                    onClick={() => toggleVisibility(index)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                      section.is_visible 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-gray-700 text-gray-400'
                    }`}
                  >
                    {section.is_visible ? (
                      <>
                        <Eye className="h-3.5 w-3.5" />
                        Visible
                      </>
                    ) : (
                      <>
                        <EyeOff className="h-3.5 w-3.5" />
                        Hidden
                      </>
                    )}
                  </button>
                </div>
                <div className="col-span-3 flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSection(index, 'up')}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-white disabled:opacity-30"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveSection(index, 'down')}
                    disabled={index === sections.length - 1}
                    className="text-gray-400 hover:text-white disabled:opacity-30"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Note */}
      <div className="mt-6 p-4 bg-cyan-900/20 rounded-xl border border-cyan-500/30">
        <p className="text-sm text-cyan-300">
          <strong>Note:</strong> Changes will be reflected on the VOD homepage immediately after saving. 
          Sections marked as "Hidden" will not appear to users. "Continue Watching" and "Recommended For You" 
          sections only appear for logged-in users.
        </p>
      </div>
    </div>
  );
};

// Creator Modal Component
const CreatorModal = ({ creator, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: creator?.name || '',
    slug: creator?.slug || '',
    description: creator?.description || '',
    profile_image_url: creator?.profile_image_url || '',
    banner_image_url: creator?.banner_image_url || '',
    social_media_image_url: creator?.social_media_image_url || '',
    featured_video_id: creator?.featured_video_id || '',
    is_active: creator?.is_active ?? true,
    is_verified: creator?.is_verified || false,
    social_links: creator?.social_links || [],
    stripe_account: creator?.stripe_account || '',
    linked_event_ids: creator?.linked_event_ids || [],
    platform_fee_percentage: creator?.platform_fee_percentage || 0
  });
  const [saving, setSaving] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingSocialImage, setUploadingSocialImage] = useState(false);
  const [slugError, setSlugError] = useState('');
  const [creatorVideos, setCreatorVideos] = useState([]);
  const [stripeAccounts, setStripeAccounts] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);

  // Fetch videos assigned to this creator
  useEffect(() => {
    const fetchCreatorVideos = async () => {
      if (!creator?.id) return;
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
        const res = await axios.get(`${API}/api/creators/profiles/slug/${creator.slug}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCreatorVideos(res.data.videos || []);
      } catch (err) {
        console.error('Error fetching creator videos:', err);
      }
    };
    fetchCreatorVideos();
  }, [creator]);

  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
  };

  // Fetch stripe accounts and live events for linking
  useEffect(() => {
    const fetchStripeAccounts = async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API}/api/livestream/admin/stripe-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStripeAccounts(res.data?.accounts || res.data || []);
      } catch (err) {
        console.error('Error fetching stripe accounts:', err);
      }
    };
    const fetchLiveEvents = async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API}/api/livestream/admin/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLiveEvents(res.data?.events || res.data || []);
      } catch (err) {
        console.error('Error fetching live events:', err);
      }
    };
    fetchStripeAccounts();
    fetchLiveEvents();
  }, []);

  // Generate slug from name
  const generateSlug = async (name) => {
    if (!name) return;
    try {
      const response = await axios.get(`${API}/api/creators/generate-slug?name=${encodeURIComponent(name)}`);
      setForm(prev => ({ ...prev, slug: response.data.slug }));
      setSlugError('');
    } catch (err) {
      console.error('Error generating slug:', err);
    }
  };

  // Upload image
  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const setUploading = type === 'profile' ? setUploadingProfile : 
                         type === 'banner' ? setUploadingBanner : 
                         setUploadingSocialImage;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Map type to R2 category folder
      const categoryMap = { profile: 'creators', banner: 'creators', social: 'social' };
      const category = categoryMap[type] || 'general';
      const referenceName = form.name || form.slug || 'new-creator';
      
      formData.append('category', category);
      formData.append('reference_name', `${referenceName}-${type}`);
      
      const res = await axios.post(`${API}/api/r2/upload-image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        if (type === 'profile') {
          setForm(prev => ({ ...prev, profile_image_url: res.data.url }));
        } else if (type === 'banner') {
          setForm(prev => ({ ...prev, banner_image_url: res.data.url }));
        } else {
          setForm(prev => ({ ...prev, social_media_image_url: res.data.url }));
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  // Add social link
  const addSocialLink = () => {
    setForm(prev => ({
      ...prev,
      social_links: [...prev.social_links, { platform: 'instagram', url: '', username: '' }]
    }));
  };

  // Remove social link
  const removeSocialLink = (index) => {
    setForm(prev => ({
      ...prev,
      social_links: prev.social_links.filter((_, i) => i !== index)
    }));
  };

  // Update social link
  const updateSocialLink = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      social_links: prev.social_links.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.slug) {
      alert('Name and URL slug are required');
      return;
    }
    
    setSaving(true);
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      if (creator?.id) {
        // Update existing
        await axios.put(`${API}/api/creators/profiles/${creator.id}`, form, { headers });
      } else {
        // Create new
        await axios.post(`${API}/api/creators/profiles`, form, { headers });
      }
      
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      alert(err.response?.data?.detail || 'Failed to save creator');
    } finally {
      setSaving(false);
    }
  };

  const socialPlatforms = ['instagram', 'tiktok', 'snapchat', 'youtube', 'twitter', 'facebook', 'website'];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between z-10">
            <h2 className="text-xl font-bold">{creator ? 'Edit Creator' : 'Add Creator Channel'}</h2>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {/* Banner Upload */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Banner Image (1500x500 recommended)</label>
              <div className="relative h-32 rounded-xl overflow-hidden bg-gradient-to-br from-blue-900/50 to-cyan-900/30">
                {form.banner_image_url && (
                  <img src={form.banner_image_url} alt="" className="w-full h-full object-cover" />
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                  {uploadingBanner ? (
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  ) : (
                    <div className="text-center">
                      <Upload className="h-8 w-8 mx-auto mb-2 text-white" />
                      <span className="text-sm text-white">Upload Banner</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'banner')}
                    className="hidden"
                    disabled={uploadingBanner}
                  />
                </label>
                {form.banner_image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, banner_image_url: '' })}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Profile Image */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Profile Image</label>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 rounded-full overflow-hidden bg-gray-800">
                  {form.profile_image_url ? (
                    <img src={form.profile_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-blue-400">
                      {form.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <label className={`flex items-center gap-2 px-4 py-2 border border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800 transition ${uploadingProfile ? 'opacity-50' : ''}`}>
                  {uploadingProfile ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4" /> Upload Photo</>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'profile')}
                    className="hidden"
                    disabled={uploadingProfile}
                  />
                </label>
                {form.profile_image_url && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setForm({ ...form, profile_image_url: '' })}
                    className="border-red-500/50 text-red-400"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>

            {/* Social Media Share Image */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Social Media Share Image (1200x630 recommended)</label>
              <p className="text-xs text-gray-500 mb-3">This image appears when the creator channel is shared on WhatsApp, Facebook, Instagram, etc.</p>
              <div className="relative aspect-[1200/630] max-w-md rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/50 to-pink-900/30 border border-gray-700">
                {form.social_media_image_url && (
                  <img src={form.social_media_image_url} alt="" className="w-full h-full object-cover" />
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                  {uploadingSocialImage ? (
                    <Loader2 className="h-8 w-8 animate-spin text-white" />
                  ) : (
                    <div className="text-center">
                      <Share2 className="h-8 w-8 mx-auto mb-2 text-white" />
                      <span className="text-sm text-white">Upload Share Image</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'social')}
                    className="hidden"
                    disabled={uploadingSocialImage}
                  />
                </label>
                {form.social_media_image_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, social_media_image_url: '' })}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Channel Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onBlur={() => !creator && generateSlug(form.name)}
                placeholder="e.g., The Late Show, Music Vibes"
                className="bg-gray-800 border-gray-700"
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">URL Slug *</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">/c/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="channel-name"
                  className="bg-gray-800 border-gray-700 flex-1"
                  required
                />
              </div>
              {slugError && <p className="text-red-400 text-xs mt-1">{slugError}</p>}
              <p className="text-gray-500 text-xs mt-1">This will be the channel's URL: /c/{form.slug || 'channel-name'}</p>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Tell viewers about this channel..."
                rows={3}
                className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white resize-none"
              />
            </div>

            {/* Social Links */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-400">Social Links</label>
                <Button type="button" variant="outline" size="sm" onClick={addSocialLink} className="border-gray-700">
                  <Plus className="h-4 w-4 mr-1" /> Add Link
                </Button>
              </div>
              <div className="space-y-3">
                {form.social_links.map((link, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <select
                      value={link.platform}
                      onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                      className="p-2 rounded-lg bg-gray-800 border border-gray-700 text-white w-32"
                    >
                      {socialPlatforms.map(p => (
                        <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                      ))}
                    </select>
                    <Input
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      placeholder="https://..."
                      className="bg-gray-800 border-gray-700 flex-1"
                    />
                    <Input
                      value={link.username || ''}
                      onChange={(e) => updateSocialLink(index, 'username', e.target.value)}
                      placeholder="@username"
                      className="bg-gray-800 border-gray-700 w-28"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSocialLink(index)}
                      className="text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Featured Video Selection */}
            {creator && creatorVideos.length > 0 && (
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Featured/Promoted Video</label>
                <p className="text-xs text-gray-500 mb-3">This video will be displayed prominently at the top of the creator's channel page.</p>
                <select
                  value={form.featured_video_id || ''}
                  onChange={(e) => setForm({ ...form, featured_video_id: e.target.value || null })}
                  className="w-full p-3 rounded-lg bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">No featured video</option>
                  {creatorVideos.map(video => (
                    <option key={video.id} value={video.id}>
                      {video.title} {video.duration ? `(${video.duration})` : ''}
                    </option>
                  ))}
                </select>
                {form.featured_video_id && (
                  <div className="mt-3 p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <p className="text-xs text-gray-400 mb-2">Preview:</p>
                    {(() => {
                      const selectedVideo = creatorVideos.find(v => v.id === form.featured_video_id);
                      return selectedVideo ? (
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-14 rounded bg-gray-700 overflow-hidden flex-shrink-0">
                            {selectedVideo.thumbnail_url ? (
                              <img src={selectedVideo.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Play className="h-5 w-5 text-gray-500" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{selectedVideo.title}</p>
                            <p className="text-xs text-gray-500">{selectedVideo.duration || 'No duration'}</p>
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            )}

            {creator && creatorVideos.length === 0 && (
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400">
                  <Star className="h-4 w-4 inline mr-2 text-yellow-400" />
                  No videos assigned to this creator yet. Assign videos to enable Featured Video selection.
                </p>
              </div>
            )}

            {/* Payments & Events */}
            <div className="border-t border-gray-700 pt-5">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-green-400" /> Payments & Events
              </h3>

              {/* Stripe Account */}
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-1.5 block">Stripe Account</label>
                <select
                  value={form.stripe_account}
                  onChange={(e) => setForm({ ...form, stripe_account: e.target.value })}
                  className="w-full p-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  data-testid="creator-stripe-account"
                >
                  <option value="">No Stripe account (use default)</option>
                  {stripeAccounts.map((acct) => (
                    <option key={acct.account_name || acct.id} value={acct.account_name}>
                      {acct.account_name} {acct.stripe_publishable_key ? '(Connected)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Paid video purchases will be charged through this Stripe account.
                  {stripeAccounts.length === 0 && (
                    <span className="text-amber-400"> No accounts found — create one in Stripe Settings.</span>
                  )}
                </p>
              </div>

              {/* Platform Fee */}
              <div className="mb-4">
                <label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5" /> Platform Fee (%)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="50"
                    step="0.5"
                    value={form.platform_fee_percentage}
                    onChange={(e) => setForm({ ...form, platform_fee_percentage: parseFloat(e.target.value) })}
                    className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-500"
                    data-testid="creator-platform-fee"
                  />
                  <div className="w-20 text-center">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={form.platform_fee_percentage}
                      onChange={(e) => setForm({ ...form, platform_fee_percentage: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                      className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm text-center"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Platform keeps {form.platform_fee_percentage}% of each video sale. Creator receives {(100 - form.platform_fee_percentage).toFixed(1)}%.
                </p>
              </div>

              {/* Linked Live Events */}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Linked Live Events
                </label>
                {form.linked_event_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.linked_event_ids.map((eventId) => {
                      const event = liveEvents.find(e => e.id === eventId);
                      return (
                        <span key={eventId} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          {event?.title || eventId}
                          <button
                            type="button"
                            onClick={() => setForm(prev => ({
                              ...prev,
                              linked_event_ids: prev.linked_event_ids.filter(id => id !== eventId)
                            }))}
                            className="hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <select
                  value=""
                  onChange={(e) => {
                    const eventId = e.target.value;
                    if (eventId && !form.linked_event_ids.includes(eventId)) {
                      setForm(prev => ({
                        ...prev,
                        linked_event_ids: [...prev.linked_event_ids, eventId]
                      }));
                    }
                  }}
                  className="w-full p-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  data-testid="creator-linked-events"
                >
                  <option value="">Select an event to link...</option>
                  {liveEvents
                    .filter(e => !form.linked_event_ids.includes(e.id))
                    .map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title} {event.date ? `(${new Date(event.date).toLocaleDateString()})` : ''}
                      </option>
                    ))
                  }
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Link past, current, or upcoming events to this creator channel.
                  {form.linked_event_ids.length > 0 && form.stripe_account && (
                    <span className="text-green-400"> Payments will use the linked Stripe account.</span>
                  )}
                </p>
              </div>
            </div>

            {/* Settings */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-300">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_verified}
                  onChange={(e) => setForm({ ...form, is_verified: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-300">Verified Badge</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-900 p-6 border-t border-gray-800 flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-gray-700">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Creator'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Ad Reels Tab Component
const AdReelsTab = ({ authToken }) => {
  const [adReels, setAdReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adSettings, setAdSettings] = useState({
    ad_frequency: 5,
    skip_for_premium: true,
    default_skip_seconds: 5,
    is_ads_enabled: true
  });
  const [analytics, setAnalytics] = useState(null);
  
  useEffect(() => {
    fetchAdReels();
    fetchAdSettings();
    fetchAnalytics();
  }, []);
  
  const fetchAdReels = async () => {
    try {
      const response = await axios.get(`${API}/api/ad-reels`);
      setAdReels(response.data || []);
    } catch (err) {
      console.error('Error fetching ad reels:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAdSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/ad-reels/settings/config`);
      setAdSettings(response.data);
    } catch (err) {
      console.error('Error fetching ad settings:', err);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/api/ad-reels/analytics/overview`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };
  
  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Delete this ad reel?')) return;
    try {
      await axios.delete(`${API}/api/ad-reels/${adId}`);
      fetchAdReels();
    } catch (err) {
      console.error('Error deleting ad:', err);
    }
  };
  
  const handleToggleAd = async (ad) => {
    try {
      await axios.put(`${API}/api/ad-reels/${ad.id}`, { is_enabled: !ad.is_enabled });
      fetchAdReels();
    } catch (err) {
      console.error('Error toggling ad:', err);
    }
  };
  
  const handleUpdateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/api/ad-reels/settings/config`, newSettings);
      setAdSettings(prev => ({ ...prev, ...newSettings }));
    } catch (err) {
      console.error('Error updating settings:', err);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'paused': return 'bg-yellow-500';
      case 'scheduled': return 'bg-blue-500';
      case 'expired': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-yellow-500" /> Ad Reels
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Manage promotional reels that appear in the feed
          </p>
        </div>
        <Button 
          onClick={() => { setEditingAd(null); setShowModal(true); }}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400"
        >
          <Plus className="h-4 w-4 mr-2" /> New Ad Reel
        </Button>
      </div>
      
      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{analytics.total_views.toLocaleString()}</div>
              <div className="text-gray-400 text-sm">Total Views</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-400">{analytics.average_completion_rate}%</div>
              <div className="text-gray-400 text-sm">Completion Rate</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-400">{analytics.total_clicks.toLocaleString()}</div>
              <div className="text-gray-400 text-sm">CTA Clicks</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-400">{analytics.active_ads}</div>
              <div className="text-gray-400 text-sm">Active Ads</div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Ad Settings */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5" /> Ad Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Ad Frequency</label>
              <select 
                value={adSettings.ad_frequency}
                onChange={(e) => handleUpdateSettings({ ad_frequency: parseInt(e.target.value) })}
                className="w-full bg-gray-700 border-gray-600 rounded px-3 py-2 text-white"
              >
                <option value={3}>Every 3 reels</option>
                <option value={5}>Every 5 reels</option>
                <option value={7}>Every 7 reels</option>
                <option value={10}>Every 10 reels</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Skip After (seconds)</label>
              <select 
                value={adSettings.default_skip_seconds}
                onChange={(e) => handleUpdateSettings({ default_skip_seconds: parseInt(e.target.value) })}
                className="w-full bg-gray-700 border-gray-600 rounded px-3 py-2 text-white"
              >
                <option value={0}>No skip</option>
                <option value={3}>3 seconds</option>
                <option value={5}>5 seconds</option>
                <option value={10}>10 seconds</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                checked={adSettings.skip_for_premium}
                onChange={(e) => handleUpdateSettings({ skip_for_premium: e.target.checked })}
                className="rounded"
              />
              <label className="text-sm text-gray-400">Skip ads for premium users</label>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                checked={adSettings.is_ads_enabled}
                onChange={(e) => handleUpdateSettings({ is_ads_enabled: e.target.checked })}
                className="rounded"
              />
              <label className="text-sm text-gray-400">Enable ads globally</label>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Ad Reels List */}
      {adReels.length === 0 ? (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-8 text-center">
            <Megaphone className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">No ad reels yet</p>
            <Button 
              onClick={() => { setEditingAd(null); setShowModal(true); }}
              className="bg-yellow-500 hover:bg-yellow-400"
            >
              Create First Ad
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {adReels.map((ad) => (
            <Card key={ad.id} className="bg-gray-800/50 border-gray-700 overflow-hidden">
              <div className="relative aspect-[9/16] bg-gray-900">
                {ad.thumbnail_url ? (
                  <img src={ad.thumbnail_url} alt={ad.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="h-16 w-16 text-gray-600" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-2">
                  <span className={`${getStatusColor(ad.status)} text-white text-xs px-2 py-1 rounded-full`}>
                    {ad.status}
                  </span>
                </div>
                <div className="absolute bottom-2 left-2">
                  <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded-full font-medium">
                    AD
                  </span>
                </div>
              </div>
              <CardContent className="p-3">
                <h4 className="font-semibold text-white truncate">{ad.title}</h4>
                <p className="text-gray-400 text-sm truncate">{ad.advertiser_name}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span><Eye className="h-3 w-3 inline mr-1" />{ad.view_count || 0}</span>
                  <span><Check className="h-3 w-3 inline mr-1" />{ad.completion_count || 0}</span>
                  {ad.cta_text && <span><ExternalLink className="h-3 w-3 inline mr-1" />{ad.click_count || 0}</span>}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleToggleAd(ad)}
                    className={`flex-1 ${ad.is_enabled ? 'border-green-500 text-green-500' : 'border-gray-500 text-gray-500'}`}
                  >
                    {ad.is_enabled ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                    {ad.is_enabled ? 'Pause' : 'Enable'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => { setEditingAd(ad); setShowModal(true); }}
                    className="border-gray-600"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => handleDeleteAd(ad.id)}
                    className="border-red-500 text-red-500 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Ad Reel Modal */}
      {showModal && (
        <AdReelModal 
          ad={editingAd}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchAdReels(); fetchAnalytics(); }}
        />
      )}
    </div>
  );
};

// Ad Reel Modal Component
const AdReelModal = ({ ad, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: ad?.title || '',
    description: ad?.description || '',
    advertiser_name: ad?.advertiser_name || '',
    advertiser_logo_url: ad?.advertiser_logo_url || '',
    video_url: ad?.video_url || '',
    thumbnail_url: ad?.thumbnail_url || '',
    cta_text: ad?.cta_text || '',
    cta_url: ad?.cta_url || '',
    skip_after_seconds: ad?.skip_after_seconds || 5,
    start_date: ad?.start_date ? new Date(ad.start_date).toISOString().slice(0, 16) : '',
    end_date: ad?.end_date ? new Date(ad.end_date).toISOString().slice(0, 16) : '',
    is_enabled: ad?.is_enabled ?? true
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate video
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }
    
    // Max 500MB for ad reels
    if (file.size > 500 * 1024 * 1024) {
      alert('Ad video must be under 500MB');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Use the Cloudflare upload proxy
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/api/reels/upload-proxy`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      if (response.data.playback_url) {
        setFormData(prev => ({
          ...prev,
          video_url: response.data.playback_url,
          thumbnail_url: response.data.thumbnail_url || prev.thumbnail_url,
          stream_video_id: response.data.stream_video_id
        }));
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.advertiser_name || !formData.video_url) {
      alert('Please fill in required fields: Title, Advertiser Name, and Video');
      return;
    }
    
    setSaving(true);
    
    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      if (ad) {
        await axios.put(`${API}/api/ad-reels/${ad.id}`, submitData);
      } else {
        await axios.post(`${API}/api/ad-reels`, submitData);
      }
      
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-yellow-500" />
            {ad ? 'Edit Ad Reel' : 'New Ad Reel'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Video Upload */}
          <div>
            <label className="text-sm text-gray-400 block mb-2">Video *</label>
            {formData.video_url ? (
              <div className="relative aspect-[9/16] max-h-64 bg-gray-800 rounded-lg overflow-hidden">
                <video src={formData.video_url} className="w-full h-full object-contain" controls />
                <button 
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, video_url: '', thumbnail_url: '' }))}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center">
                {uploading ? (
                  <div>
                    <Loader2 className="h-8 w-8 animate-spin text-yellow-500 mx-auto mb-2" />
                    <p className="text-gray-400">Uploading... {uploadProgress}%</p>
                    <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 mb-2">Upload 9:16 vertical video</p>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      id="ad-video-upload"
                    />
                    <label 
                      htmlFor="ad-video-upload"
                      className="inline-block bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded-lg cursor-pointer font-medium"
                    >
                      Select Video
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Title *</label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="bg-gray-800 border-gray-700"
                placeholder="Ad title"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Advertiser Name *</label>
              <Input 
                value={formData.advertiser_name}
                onChange={(e) => setFormData(prev => ({ ...prev, advertiser_name: e.target.value }))}
                className="bg-gray-800 border-gray-700"
                placeholder="Brand/Company name"
              />
            </div>
          </div>
          
          <div>
            <label className="text-sm text-gray-400 block mb-1">Description</label>
            <textarea 
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
              rows={2}
              placeholder="Brief description"
            />
          </div>
          
          <div>
            <label className="text-sm text-gray-400 block mb-1">Advertiser Logo URL</label>
            <Input 
              value={formData.advertiser_logo_url}
              onChange={(e) => setFormData(prev => ({ ...prev, advertiser_logo_url: e.target.value }))}
              className="bg-gray-800 border-gray-700"
              placeholder="https://..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">CTA Button Text</label>
              <Input 
                value={formData.cta_text}
                onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                className="bg-gray-800 border-gray-700"
                placeholder="e.g., Learn More, Shop Now"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">CTA URL</label>
              <Input 
                value={formData.cta_url}
                onChange={(e) => setFormData(prev => ({ ...prev, cta_url: e.target.value }))}
                className="bg-gray-800 border-gray-700"
                placeholder="https://..."
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Skip After (seconds)</label>
              <select 
                value={formData.skip_after_seconds}
                onChange={(e) => setFormData(prev => ({ ...prev, skip_after_seconds: parseInt(e.target.value) }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white"
              >
                <option value={0}>No skip</option>
                <option value={3}>3 seconds</option>
                <option value={5}>5 seconds</option>
                <option value={10}>10 seconds</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Start Date</label>
              <Input 
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">End Date</label>
              <Input 
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                className="bg-gray-800 border-gray-700"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <input 
              type="checkbox"
              checked={formData.is_enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
              className="rounded"
            />
            <label className="text-gray-400">Enable this ad immediately</label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-gray-700">
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={saving || uploading}
              className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400"
            >
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : (ad ? 'Update Ad' : 'Create Ad')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========================= CREATOR ACCOUNTS TAB =========================
const CreatorAccountsTab = ({ creators, authToken }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [reels, setReels] = useState([]);
  
  // Form state
  const [email, setEmail] = useState('');
  const [selectedCreatorId, setSelectedCreatorId] = useState('');
  const [showRevenue, setShowRevenue] = useState(false);
  const [showAdRevenue, setShowAdRevenue] = useState(false);
  const [selectedReelIds, setSelectedReelIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchAccounts();
    fetchReels();
  }, [authToken]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/api/creator-portal/admin/accounts`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAccounts(res.data);
    } catch (err) {
      console.error('Error fetching creator accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReels = async () => {
    try {
      const res = await axios.get(`${API}/api/reels`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setReels(res.data.reels || []);
    } catch (err) {
      console.error('Error fetching reels:', err);
    }
  };

  const handleCreate = async () => {
    if (!email || !selectedCreatorId) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/creator-portal/admin/accounts`, {
        email,
        creator_profile_id: selectedCreatorId,
        show_revenue: showRevenue,
        show_ad_revenue: showAdRevenue,
        assigned_reel_ids: selectedReelIds
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      setGeneratedPassword(res.data.generated_password);
      fetchAccounts();
      // Don't close modal - show the password first
    } catch (err) {
      console.error('Create error:', err);
      alert(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingAccount) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/creator-portal/admin/accounts/${editingAccount.id}`, {
        show_revenue: showRevenue,
        show_ad_revenue: showAdRevenue,
        assigned_reel_ids: selectedReelIds,
        is_active: editingAccount.is_active
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      fetchAccounts();
      setShowEditModal(false);
      setEditingAccount(null);
    } catch (err) {
      console.error('Update error:', err);
      alert(err.response?.data?.detail || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (accountId) => {
    if (!window.confirm('Are you sure you want to reset this creator\'s password?')) return;
    
    try {
      const res = await axios.post(`${API}/api/creator-portal/admin/accounts/${accountId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      alert(`New password: ${res.data.new_password}\n\nShare this with the creator.`);
    } catch (err) {
      console.error('Reset password error:', err);
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDelete = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this creator account?')) return;
    
    try {
      await axios.delete(`${API}/api/creator-portal/admin/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      fetchAccounts();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.detail || 'Failed to delete account');
    }
  };

  const toggleReelSelection = (reelId) => {
    setSelectedReelIds(prev => 
      prev.includes(reelId) 
        ? prev.filter(id => id !== reelId)
        : [...prev, reelId]
    );
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const resetForm = () => {
    setEmail('');
    setSelectedCreatorId('');
    setShowRevenue(false);
    setShowAdRevenue(false);
    setSelectedReelIds([]);
    setGeneratedPassword('');
    setCopySuccess(false);
  };

  const openEditModal = (account) => {
    setEditingAccount(account);
    setShowRevenue(account.show_revenue || false);
    setShowAdRevenue(account.show_ad_revenue || false);
    setSelectedReelIds(account.assigned_reel_ids || []);
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Creator Portal Accounts</h2>
          <p className="text-gray-400 text-sm mt-1">
            Manage login accounts for creators to view their analytics (read-only access).
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500"
        >
          <Plus className="h-4 w-4 mr-2" /> Create Account
        </Button>
      </div>

      {accounts.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-xl border border-gray-800">
          <UserCog className="h-16 w-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-500 mb-4">No creator accounts yet</p>
          <p className="text-gray-600 text-sm mb-6">Create accounts for creators to view their channel analytics</p>
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-gradient-to-r from-teal-600 to-cyan-600"
          >
            <Plus className="h-4 w-4 mr-2" /> Create First Account
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => (
            <div key={account.id} className="bg-gray-900 rounded-xl border border-gray-800 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center">
                    <UserCog className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{account.creator_name}</h3>
                      {account.is_active ? (
                        <Badge className="bg-green-600 text-xs">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-600 text-xs">Inactive</Badge>
                      )}
                      {account.must_change_password && (
                        <Badge className="bg-yellow-600 text-xs">Password Change Required</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{account.email}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {account.show_revenue && (
                        <span className="flex items-center gap-1 text-green-400">
                          <DollarSign className="h-3 w-3" /> Revenue Visible
                        </span>
                      )}
                      {account.show_ad_revenue && (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <DollarSign className="h-3 w-3" /> Ad Revenue
                        </span>
                      )}
                      {account.assigned_reel_ids?.length > 0 && (
                        <span className="flex items-center gap-1 text-pink-400">
                          <Film className="h-3 w-3" /> {account.assigned_reel_ids.length} Reels
                        </span>
                      )}
                      {account.last_login && (
                        <span>Last login: {new Date(account.last_login).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(account)}
                    className="border-gray-700"
                  >
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetPassword(account.id)}
                    className="border-gray-700 text-yellow-400 hover:text-yellow-300"
                  >
                    <Key className="h-4 w-4 mr-1" /> Reset Password
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(account.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-gray-900 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Create Creator Account</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {generatedPassword ? (
              /* Password Generated - Show Copy Section */
              <div className="space-y-4">
                <div className="p-4 bg-green-900/30 border border-green-500/50 rounded-lg">
                  <p className="text-green-400 font-medium mb-2 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    Account Created Successfully!
                  </p>
                  <p className="text-gray-300 text-sm">Share these credentials with the creator:</p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-400">Email</label>
                    <p className="text-white font-mono bg-gray-800 p-2 rounded">{email}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400">Temporary Password</label>
                    <div className="flex gap-2">
                      <p className="flex-1 text-white font-mono bg-gray-800 p-2 rounded">{generatedPassword}</p>
                      <Button
                        onClick={copyPassword}
                        className={copySuccess ? 'bg-green-600' : 'bg-teal-600'}
                      >
                        {copySuccess ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-400">Login URL</label>
                    <p className="text-white font-mono bg-gray-800 p-2 rounded">/creator/login</p>
                  </div>
                </div>
                
                <div className="p-3 bg-yellow-900/30 border border-yellow-500/50 rounded-lg text-sm text-yellow-300">
                  <p>⚠️ The creator will be asked to change their password on first login.</p>
                </div>
                
                <Button
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600"
                >
                  Done
                </Button>
              </div>
            ) : (
              /* Create Form */
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Email Address *</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="creator@email.com"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Assign to Creator Profile *</label>
                  <select
                    value={selectedCreatorId}
                    onChange={(e) => setSelectedCreatorId(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-white"
                  >
                    <option value="">Select a creator...</option>
                    {creators.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Show Revenue Data</p>
                    <p className="text-sm text-gray-400">Allow creator to see purchase earnings</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowRevenue(!showRevenue)}
                    className={`w-12 h-6 rounded-full transition-colors ${showRevenue ? 'bg-green-600' : 'bg-gray-600'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showRevenue ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Show Ad Revenue</p>
                    <p className="text-sm text-gray-400">Allow creator to see ad revenue analytics</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAdRevenue(!showAdRevenue)}
                    className={`w-12 h-6 rounded-full transition-colors ${showAdRevenue ? 'bg-emerald-600' : 'bg-gray-600'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showAdRevenue ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Assign Reels (Optional)</label>
                  <p className="text-xs text-gray-500 mb-2">Select which reels the creator can see analytics for</p>
                  <div className="max-h-40 overflow-y-auto space-y-2 bg-gray-800 p-3 rounded-lg">
                    {reels.length === 0 ? (
                      <p className="text-gray-500 text-sm">No reels available</p>
                    ) : (
                      reels.map(reel => (
                        <label key={reel.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedReelIds.includes(reel.id)}
                            onChange={() => toggleReelSelection(reel.id)}
                            className="rounded border-gray-600"
                          />
                          <span className="text-sm text-white">{reel.title}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="flex-1 border-gray-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600"
                  >
                    {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create Account'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && editingAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-gray-900 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Edit Creator Account</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Creator</label>
                <p className="text-white font-medium">{editingAccount.creator_name}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Email</label>
                <p className="text-white">{editingAccount.email}</p>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-white font-medium">Account Active</p>
                  <p className="text-sm text-gray-400">Enable/disable account access</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingAccount({...editingAccount, is_active: !editingAccount.is_active})}
                  className={`w-12 h-6 rounded-full transition-colors ${editingAccount.is_active ? 'bg-green-600' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${editingAccount.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-white font-medium">Show Revenue Data</p>
                  <p className="text-sm text-gray-400">Allow creator to see purchase earnings</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRevenue(!showRevenue)}
                  className={`w-12 h-6 rounded-full transition-colors ${showRevenue ? 'bg-green-600' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showRevenue ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div>
                  <p className="text-white font-medium">Show Ad Revenue</p>
                  <p className="text-sm text-gray-400">Allow creator to see ad revenue analytics</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAdRevenue(!showAdRevenue)}
                  className={`w-12 h-6 rounded-full transition-colors ${showAdRevenue ? 'bg-emerald-600' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${showAdRevenue ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Assigned Reels</label>
                <div className="max-h-40 overflow-y-auto space-y-2 bg-gray-800 p-3 rounded-lg">
                  {reels.length === 0 ? (
                    <p className="text-gray-500 text-sm">No reels available</p>
                  ) : (
                    reels.map(reel => (
                      <label key={reel.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedReelIds.includes(reel.id)}
                          onChange={() => toggleReelSelection(reel.id)}
                          className="rounded border-gray-600"
                        />
                        <span className="text-sm text-white">{reel.title}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 border-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-cyan-600"
                >
                  {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================= SUBSCRIPTIONS TAB =========================
const SubscriptionsTab = ({ authToken }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedSub, setSelectedSub] = useState(null);
  const [stripeDetails, setStripeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, [authToken, filterStatus]);

  const fetchSubscriptions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/api/vod/payment/admin/subscriptions?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSubscriptions(res.data.subscriptions || []);
      setStats(res.data.stats || {});
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeDetails = async (subscriptionId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/api/vod/payment/admin/subscriptions/${subscriptionId}/stripe-details`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setStripeDetails(res.data);
    } catch (err) {
      console.error('Error fetching stripe details:', err);
      setStripeDetails({ error: 'Failed to load Stripe details' });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await axios.post(`${API}/api/vod/payment/admin/subscriptions/${subscriptionId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      fetchSubscriptions();
      setSelectedSub(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate MRR and other metrics
  const monthlyRevenue = (stats.active || 0) * 2.99;
  const churnRate = stats.total > 0 ? ((stats.cancelled || 0) / stats.total * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Crown className="h-5 w-5 text-white" />
            </div>
            Premium Subscriptions
          </h2>
          <p className="text-gray-400 text-sm mt-2">Manage all £2.99/month premium subscribers</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 text-white text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <Button onClick={fetchSubscriptions} variant="outline" className="border-gray-700 hover:border-green-500">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards - Traffic Source Style */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-green-600/30 to-emerald-600/20 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="h-5 w-5 text-green-400" />
            <span className="text-gray-300 text-xs">Active</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.active || 0}</p>
          <p className="text-xs text-green-300 mt-1">Currently paying</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Premium Tier</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.premium || 0}</p>
          <p className="text-xs text-purple-300 mt-1">£2.99/month</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">MRR</span>
          </div>
          <p className="text-2xl font-bold text-white">£{monthlyRevenue.toFixed(2)}</p>
          <p className="text-xs text-blue-300 mt-1">Monthly revenue</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Total</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
          <p className="text-xs text-yellow-300 mt-1">All time</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-600/30 to-rose-600/20 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5 text-red-400" />
            <span className="text-gray-300 text-xs">Cancelled</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.cancelled || 0}</p>
          <p className="text-xs text-red-300 mt-1">Churned</p>
        </div>
        
        <div className="bg-gradient-to-br from-gray-600/30 to-gray-700/20 rounded-xl p-4 border border-gray-500/30">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="h-5 w-5 text-gray-400" />
            <span className="text-gray-300 text-xs">Churn Rate</span>
          </div>
          <p className="text-2xl font-bold text-white">{churnRate}%</p>
          <p className="text-xs text-gray-400 mt-1">Cancellation rate</p>
        </div>
      </div>

      {/* Subscription Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <PieChart className="h-5 w-5 text-green-400" />
              Subscription Status
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {[
              { label: 'Active', value: stats.active || 0, color: '#10b981', icon: CheckCircle },
              { label: 'Cancelled', value: stats.cancelled || 0, color: '#ef4444', icon: XCircle },
              { label: 'Expired', value: stats.expired || 0, color: '#6b7280', icon: Clock }
            ].map((item) => {
              const total = (stats.active || 0) + (stats.cancelled || 0) + (stats.expired || 0);
              const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
              return (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <item.icon className="h-4 w-4" style={{ color: item.color }} />
                      <span className="text-sm text-gray-300">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-white">{item.value}</span>
                      <span className="text-xs text-gray-400">({pct}%)</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all" 
                      style={{ width: `${Math.max(parseFloat(pct), 2)}%`, backgroundColor: item.color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Revenue Projection */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-400" />
              Revenue Projection
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {[
              { label: 'Monthly (MRR)', value: monthlyRevenue, color: '#3b82f6' },
              { label: 'Quarterly', value: monthlyRevenue * 3, color: '#8b5cf6' },
              { label: 'Annual (ARR)', value: monthlyRevenue * 12, color: '#10b981' }
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-gray-300">{item.label}</span>
                </div>
                <span className="text-lg font-bold text-white">£{item.value.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-400" />
            All Subscribers
            <Badge className="bg-gray-700 text-gray-300 ml-2">{subscriptions.length}</Badge>
          </h3>
        </div>
        
        {subscriptions.length === 0 ? (
          <div className="text-center py-16">
            <Crown className="h-16 w-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-500 mb-2">No subscriptions found</p>
            <p className="text-gray-600 text-sm">Premium subscribers will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Customer</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Tier</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Status</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Joined</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Next Payment</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">Stripe Ref</th>
                  <th className="text-right text-xs text-gray-400 font-medium px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                          {(sub.customer_name || sub.customer_email || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium">{sub.customer_name || 'Unknown'}</p>
                          <p className="text-gray-400 text-xs">{sub.customer_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={sub.tier === 'premium' 
                        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 border-purple-500/30' 
                        : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }>
                        <Crown className="h-3 w-3 mr-1" />
                        {sub.tier}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          sub.status === 'active' ? 'bg-green-500 animate-pulse' :
                          sub.status === 'cancelled' ? 'bg-yellow-500' : 'bg-gray-500'
                        }`} />
                        <Badge className={
                          sub.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                          sub.status === 'cancelled' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                          'bg-gray-500/20 text-gray-400 border-gray-500/30'
                        }>
                          {sub.status}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-gray-500" />
                        {formatDate(sub.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {sub.next_payment_date ? (
                        <span className="text-green-400 flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(sub.next_payment_date)}
                        </span>
                      ) : sub.expires_at ? (
                        <span className="text-yellow-400">Expires: {formatDate(sub.expires_at)}</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {sub.stripe_subscription_id ? (
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-3.5 w-3.5 text-blue-400" />
                          <span className="text-xs text-gray-400 font-mono bg-gray-800 px-2 py-1 rounded">
                            {sub.stripe_subscription_id.slice(0, 12)}...
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">No Stripe ID</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setSelectedSub(sub); fetchStripeDetails(sub.id); }}
                        className="border-gray-700 hover:border-purple-500 hover:bg-purple-500/10"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Subscription Details Modal */}
      {selectedSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-900 rounded-xl border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Crown className="h-5 w-5 text-purple-400" />
                Subscription Details
              </h3>
              <button onClick={() => { setSelectedSub(null); setStripeDetails(null); }} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Customer Info */}
            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/10 rounded-xl p-4 mb-4 border border-purple-500/30">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-purple-400" />
                Customer Information
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Name:</span>
                  <span className="text-white ml-2">{selectedSub.customer_name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white ml-2">{selectedSub.customer_email || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">First Name:</span>
                  <span className="text-white ml-2">{selectedSub.first_name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Last Name:</span>
                  <span className="text-white ml-2">{selectedSub.last_name || '-'}</span>
                </div>
              </div>
            </div>

            {/* Subscription Info */}
            <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/10 rounded-xl p-4 mb-4 border border-green-500/30">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <Crown className="h-5 w-5 text-green-400" />
                Subscription Information
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Tier:</span>
                  <span className="text-purple-400 ml-2 font-medium">{selectedSub.tier}</span>
                </div>
                <div>
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-green-400 ml-2 font-medium">£{selectedSub.amount?.toFixed(2) || '2.99'}/month</span>
                </div>
                <div>
                  <span className="text-gray-400">Status:</span>
                  <span className={`ml-2 font-medium ${selectedSub.status === 'active' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {selectedSub.status}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Joined:</span>
                  <span className="text-white ml-2">{formatDate(selectedSub.created_at)}</span>
                </div>
                <div>
                  <span className="text-gray-400">Next Payment:</span>
                  <span className="text-white ml-2">{selectedSub.next_payment_date ? formatDate(selectedSub.next_payment_date) : '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Expires:</span>
                  <span className="text-white ml-2">{selectedSub.expires_at ? formatDate(selectedSub.expires_at) : '-'}</span>
                </div>
              </div>
            </div>

            {/* Stripe Details */}
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/10 rounded-xl p-4 mb-4 border border-blue-500/30">
              <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-400" />
                Stripe Reference
              </h4>
              {loadingDetails ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
                </div>
              ) : stripeDetails?.error ? (
                <p className="text-yellow-400 text-sm">{stripeDetails.error}</p>
              ) : stripeDetails ? (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-gray-400">Subscription ID:</span>
                      <p className="text-white font-mono text-xs mt-1 bg-gray-800 px-2 py-1 rounded">{stripeDetails.stripe_subscription_id}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Customer ID:</span>
                      <p className="text-white font-mono text-xs mt-1 bg-gray-800 px-2 py-1 rounded">{stripeDetails.stripe_customer_id}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-gray-400">Stripe Status:</span>
                      <p className="text-white mt-1">{stripeDetails.status}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Period End:</span>
                      <p className="text-white mt-1">{formatDate(stripeDetails.current_period_end)}</p>
                    </div>
                  </div>
                  {stripeDetails.payment_method && (
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <span className="text-gray-400">Payment Method:</span>
                      <p className="text-white mt-1 flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-blue-400" />
                        {stripeDetails.payment_method.brand} •••• {stripeDetails.payment_method.last4} 
                        (Exp: {stripeDetails.payment_method.exp_month}/{stripeDetails.payment_method.exp_year})
                      </p>
                    </div>
                  )}
                  {stripeDetails.recent_invoices?.length > 0 && (
                    <div>
                      <span className="text-gray-400">Recent Invoices:</span>
                      <div className="mt-2 space-y-1">
                        {stripeDetails.recent_invoices.map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between bg-gray-800 rounded px-3 py-2">
                            <span className="text-xs text-gray-300">{formatDate(inv.created)}</span>
                            <span className="text-xs text-green-400 font-medium">£{inv.amount.toFixed(2)}</span>
                            <Badge className={`text-xs ${inv.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{inv.status}</Badge>
                            {inv.invoice_pdf && (
                              <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" /> PDF
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm space-y-2">
                  <p className="text-gray-400">Subscription ID: <span className="text-white font-mono bg-gray-800 px-2 py-1 rounded">{selectedSub.stripe_subscription_id || 'N/A'}</span></p>
                  <p className="text-gray-400">Customer ID: <span className="text-white font-mono bg-gray-800 px-2 py-1 rounded">{selectedSub.stripe_customer_id || 'N/A'}</span></p>
                </div>
              )}
            </div>

            {/* Actions */}
            {selectedSub.status === 'active' && (
              <div className="flex justify-end">
                <Button
                  onClick={() => handleCancelSubscription(selectedSub.id)}
                  className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ========================= STRIPE SETTINGS TAB =========================
const StripeSettingsTab = ({ authToken }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Form state
  const [apiKey, setApiKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, [authToken]);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/vod/payment/admin/stripe-config`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setConfig(res.data);
    } catch (err) {
      console.error('Error fetching Stripe config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!apiKey && !publishableKey) {
      alert('Please enter at least the API key');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/vod/payment/admin/stripe-config`, {
        stripe_api_key: apiKey || undefined,
        stripe_publishable_key: publishableKey || undefined,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      alert('Stripe configuration saved successfully');
      setApiKey('');
      setPublishableKey('');
      fetchConfig();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConfig = async () => {
    if (!window.confirm('Are you sure you want to remove the Stripe configuration?')) return;
    
    try {
      await axios.delete(`${API}/api/vod/payment/admin/stripe-config`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      alert('Stripe configuration removed');
      fetchConfig();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove configuration');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/vod/payment/admin/stripe-config/test`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            Stripe Payment Settings
          </h2>
          <p className="text-gray-400 text-sm mt-2">Configure your Stripe account for premium subscriptions (£2.99/month)</p>
        </div>
        <Button onClick={fetchConfig} variant="outline" className="border-gray-700 hover:border-purple-500">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Status Overview Cards - Traffic Source Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`bg-gradient-to-br ${config?.has_config ? 'from-green-600/30 to-emerald-600/20 border-green-500/30' : 'from-red-600/30 to-rose-600/20 border-red-500/30'} rounded-xl p-4 border`}>
          <div className="flex items-center gap-2 mb-2">
            {config?.has_config ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
            <span className="text-gray-300 text-xs">Connection</span>
          </div>
          <p className="text-xl font-bold text-white">{config?.has_config ? 'Connected' : 'Not Set'}</p>
          <p className={`text-xs mt-1 ${config?.has_config ? 'text-green-300' : 'text-red-300'}`}>
            {config?.has_config ? 'Stripe is active' : 'Setup required'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-600/30 to-pink-600/20 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Key className="h-5 w-5 text-purple-400" />
            <span className="text-gray-300 text-xs">Secret Key</span>
          </div>
          <p className="text-xl font-bold text-white">{config?.stripe_api_key_masked ? 'Set' : 'Missing'}</p>
          <p className="text-xs text-purple-300 mt-1">sk_live_... or sk_test_...</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-600/30 to-cyan-600/20 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="h-5 w-5 text-blue-400" />
            <span className="text-gray-300 text-xs">Publishable Key</span>
          </div>
          <p className="text-xl font-bold text-white">{config?.stripe_publishable_key_masked ? 'Set' : 'Missing'}</p>
          <p className="text-xs text-blue-300 mt-1">pk_live_... or pk_test_...</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/20 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5 text-yellow-400" />
            <span className="text-gray-300 text-xs">Mode</span>
          </div>
          <p className="text-xl font-bold text-white">
            {config?.using_env_key ? 'ENV' : config?.has_config ? 'Manual' : 'N/A'}
          </p>
          <p className="text-xs text-yellow-300 mt-1">
            {config?.using_env_key ? 'Environment variable' : 'Database stored'}
          </p>
        </div>
      </div>

      {/* Current Configuration Panel */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-400" />
            Current Configuration
          </h3>
        </div>
        <div className="p-6">
          {config?.has_config ? (
            <div className="space-y-6">
              {/* Connection Status */}
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-900/30 to-emerald-900/20 rounded-xl border border-green-500/30">
                <div className="w-4 h-4 rounded-full bg-green-500 animate-pulse" />
                <div className="flex-1">
                  <span className="text-green-400 font-medium text-lg">Stripe Connected</span>
                  <p className="text-gray-400 text-sm">Your payment gateway is active and ready to process subscriptions</p>
                </div>
                {config.using_env_key && (
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    <Settings className="h-3 w-3 mr-1" />
                    Using ENV Variable
                  </Badge>
                )}
              </div>
              
              {/* Key Display */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-purple-600/10 to-pink-600/5 rounded-xl p-4 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Key className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-400 text-sm">Secret Key</span>
                  </div>
                  <p className="text-white font-mono bg-gray-800 px-3 py-2 rounded-lg text-sm">
                    {config.stripe_api_key_masked || 'Not set'}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-600/10 to-cyan-600/5 rounded-xl p-4 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="h-4 w-4 text-blue-400" />
                    <span className="text-gray-400 text-sm">Publishable Key</span>
                  </div>
                  <p className="text-white font-mono bg-gray-800 px-3 py-2 rounded-lg text-sm">
                    {config.stripe_publishable_key_masked || 'Not set'}
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleTestConnection} 
                  disabled={testing} 
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                  Test Connection
                </Button>
                {!config.using_env_key && (
                  <Button 
                    onClick={handleRemoveConfig} 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove Configuration
                  </Button>
                )}
                <a 
                  href="https://dashboard.stripe.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Stripe Dashboard
                </a>
              </div>
              
              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-xl ${testResult.success ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/20 border border-green-500/30' : 'bg-gradient-to-r from-red-900/30 to-rose-900/20 border border-red-500/30'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {testResult.success ? <CheckCircle className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
                    <p className={`font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {testResult.message}
                    </p>
                  </div>
                  {testResult.success && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <p className="text-gray-400 text-xs mb-1">Available Balance</p>
                        <p className="text-green-400 text-xl font-bold">£{testResult.available_balance?.toFixed(2)}</p>
                      </div>
                      <div className="bg-gray-800/50 rounded-lg p-3">
                        <p className="text-gray-400 text-xs mb-1">Pending Balance</p>
                        <p className="text-yellow-400 text-xl font-bold">£{testResult.pending_balance?.toFixed(2)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-10 w-10 text-gray-600" />
              </div>
              <p className="text-gray-400 text-lg mb-2">No Stripe configuration found</p>
              <p className="text-gray-500 text-sm mb-6">Add your Stripe API keys below to enable payments</p>
              <div className="flex justify-center gap-4">
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Setup Required
                </Badge>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Update Configuration */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Key className="h-5 w-5 text-yellow-400" />
            {config?.has_config ? 'Update' : 'Add'} Stripe Keys
          </h3>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Secret Key (sk_live_... or sk_test_...)</label>
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk_live_xxxxxxxxxxxxxxxxxxxxx"
                className="bg-gray-800 border-gray-700 font-mono pr-10 h-12"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
              <ExternalLink className="h-3 w-3" />
              Get this from Stripe Dashboard → Developers → API keys
            </p>
          </div>
          
          <div>
            <label className="text-gray-300 text-sm font-medium mb-2 block">Publishable Key (pk_live_... or pk_test_...) - Optional</label>
            <Input
              type="text"
              value={publishableKey}
              onChange={(e) => setPublishableKey(e.target.value)}
              placeholder="pk_live_xxxxxxxxxxxxxxxxxxxxx"
              className="bg-gray-800 border-gray-700 font-mono h-12"
            />
          </div>
          
          <div className="p-4 bg-gradient-to-r from-yellow-900/20 to-orange-900/10 border border-yellow-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-yellow-400 font-medium">Important Security Notice</p>
                <p className="text-gray-400 text-sm mt-1">
                  Use test keys (sk_test_...) for testing. Switch to live keys (sk_live_...) for production payments.
                  Never share your secret key publicly.
                </p>
              </div>
            </div>
          </div>
          
          <Button 
            onClick={handleSaveConfig} 
            disabled={saving || (!apiKey && !publishableKey)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 h-12 px-6"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-cyan-400" />
            How to Get Your Stripe Keys
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { step: 1, title: 'Open Stripe Dashboard', desc: 'Go to dashboard.stripe.com', icon: Globe, color: 'blue' },
              { step: 2, title: 'Navigate to API Keys', desc: 'Developers → API keys', icon: Key, color: 'purple' },
              { step: 3, title: 'Copy Secret Key', desc: 'Starts with sk_live_ or sk_test_', icon: Copy, color: 'green' },
              { step: 4, title: 'Copy Publishable Key', desc: 'Starts with pk_live_ or pk_test_', icon: Globe, color: 'yellow' },
              { step: 5, title: 'Paste in Form Above', desc: 'Enter both keys in the form', icon: Save, color: 'pink' },
              { step: 6, title: 'Test Connection', desc: 'Verify everything works', icon: Zap, color: 'cyan' }
            ].map((item) => (
              <div key={item.step} className={`bg-gradient-to-br from-${item.color}-600/10 to-${item.color}-600/5 rounded-xl p-4 border border-${item.color}-500/20`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-8 rounded-lg bg-${item.color}-500/20 flex items-center justify-center`}>
                    <span className={`text-${item.color}-400 font-bold text-sm`}>{item.step}</span>
                  </div>
                  <item.icon className={`h-4 w-4 text-${item.color}-400`} />
                </div>
                <h4 className="text-white font-medium text-sm">{item.title}</h4>
                <p className="text-gray-400 text-xs mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-800">
            <a 
              href="https://dashboard.stripe.com/apikeys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg text-white font-medium transition-all"
            >
              <ExternalLink className="h-4 w-4" />
              Open Stripe API Keys Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function VODAdminDashboardWithBoundary() {
  return (
    <ErrorBoundary>
      <VODAdminDashboard />
    </ErrorBoundary>
  );
}
